// client-card.js — Phase 3.3: V4 Client Card Redesign
// Show switcher · Status banner · Five-tab structure

import { apiFetch, formatDate, formatCurrency, runManualAction } from './utils.js';
import { state } from './state.js';

// ── Helpers ────────────────────────────────────────────────────────────────

function formatMMSS(seconds) {
  if (!seconds || isNaN(seconds)) return '';
  const s = Math.max(0, Math.round(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function parseMMSS(str) {
  if (!str || !str.trim()) return 0;
  const parts = str.trim().split(':');
  if (parts.length === 1) return parseInt(parts[0]) || 0;
  return (parseInt(parts[0]) || 0) * 60 + (parseInt(parts[1]) || 0);
}

// ── Show config constants ──────────────────────────────────────────────────

const DESIGN_STATUS_OPTIONS = [
  { value: 'not_started',    label: 'Not Started' },
  { value: 'concept',        label: 'Concept Phase' },
  { value: 'in_design',      label: 'In Design' },
  { value: 'design_review',  label: 'Design Review' },
  { value: 'design_locked',  label: 'Design Locked' },
  { value: 'ready_to_write', label: 'Ready to Write' },
  { value: 'writing',        label: 'Writing' },
  { value: 'revisions',      label: 'Revisions' },
  { value: 'complete',       label: 'Complete' },
];

const PRESET_PARTS = [
  // Sections Ryan writes music for
  { value: 'winds',          label: 'Winds',               group: 'Sections' },
  { value: 'front_ensemble', label: 'Front Ensemble',       group: 'Sections' },
  { value: 'battery',        label: 'Battery',              group: 'Sections' },
  { value: 'sound_design',   label: 'Sound Design',         group: 'Sections' },
  // Ryan's role on this show
  { value: 'role_composer',  label: 'Composer',             group: 'My Role' },
  { value: 'role_arranger',  label: 'Arranger',             group: 'My Role' },
  { value: 'role_sound_designer', label: 'Sound Designer',  group: 'My Role' },
  { value: 'role_consultant', label: 'Consultant/Clinician', group: 'My Role' },
  { value: 'role_instructor', label: 'Instructor',           group: 'My Role' },
  { value: 'role_director',   label: 'Director',             group: 'My Role' },
  { value: 'role_drill_writer', label: 'Drill Writer',       group: 'My Role' },
];

// ── Progress sync handlers (called from inline oninput attributes) ─────────

function _setProgPct(pct) {
  pct = Math.min(100, Math.max(0, Math.round(pct)));
  const range  = document.getElementById('cfg-pct-range');
  const num    = document.getElementById('cfg-pct-num');
  const unfill = document.getElementById('cfg-prog-unfill');
  if (range)  range.value  = pct;
  if (num)    num.value    = pct;
  if (unfill) unfill.style.left = pct + '%';
}

window._onProgSlider = function(val) {
  const pct = parseInt(val) || 0;
  _setProgPct(pct);
  // Update Written So Far from duration × pct
  const durSecs = parseMMSS(document.getElementById('cfg-duration')?.value || '');
  if (durSecs > 0) {
    const w = document.getElementById('cfg-written');
    if (w) w.value = formatMMSS(Math.round(pct / 100 * durSecs));
  }
  if (_currentShow) window._scheduleAutosave(_currentShow.id);
};

window._onProgNum = function(val) {
  const pct = parseInt(val) || 0;
  _setProgPct(pct);
  const durSecs = parseMMSS(document.getElementById('cfg-duration')?.value || '');
  if (durSecs > 0) {
    const w = document.getElementById('cfg-written');
    if (w) w.value = formatMMSS(Math.round(pct / 100 * durSecs));
  }
  if (_currentShow) window._scheduleAutosave(_currentShow.id);
};

window._onWrittenInput = function(val) {
  const writSecs = parseMMSS(val);
  const durSecs  = parseMMSS(document.getElementById('cfg-duration')?.value || '');
  if (durSecs > 0 && writSecs >= 0) {
    _setProgPct(Math.round(writSecs / durSecs * 100));
  }
  if (_currentShow) window._scheduleAutosave(_currentShow.id);
};

// Stored so saveShowConfig can merge with existing values from the full show object
let _currentShow = null;

// Autosave debounce
let _autosaveTimer = null;
window._scheduleAutosave = function(showId) {
  const ind = document.getElementById('cfg-save-indicator');
  if (ind) { ind.textContent = '● unsaved'; ind.className = 'cfg-save-indicator unsaved'; }
  clearTimeout(_autosaveTimer);
  _autosaveTimer = setTimeout(() => window.saveShowConfig(showId), 1400);
};

// ── Entry point ────────────────────────────────────────────────────────────

export async function renderClientCard(clientId) {
  state.currentClientId = clientId;

  const [client, shows, directorContacts] = await Promise.all([
    apiFetch(`/api/v1/clients/${clientId}`),
    apiFetch(`/api/v1/clients/${clientId}/shows`),
    apiFetch(`/api/contacts/directors/${clientId}`).catch(() => [])
  ]);

  // Default to most recent show by season_year
  const sorted = [...shows].sort((a, b) => (b.season_year || 0) - (a.season_year || 0));
  const activeShowId = state.currentShowId && shows.find(s => s.id === state.currentShowId)
    ? state.currentShowId
    : sorted[0]?.id;
  const activeShow = shows.find(s => s.id === activeShowId) || sorted[0];

  const container = document.getElementById('client-detail-content');
  if (!container) return;

  const currentYear = new Date().getFullYear();
  const isPast = activeShow && activeShow.season_year && activeShow.season_year < currentYear;

  container.innerHTML = `
    <div class="client-card-v4${isPast ? ' past-season' : ''}">
      ${renderShowSwitcher(client, sorted, activeShowId)}
      ${activeShow ? renderStatusBanner(activeShow, clientId) : ''}
      ${renderDirectorBar(client, directorContacts, activeShow)}
      ${renderTabBar(state.currentTab || 'production')}
      <div class="tab-content" id="tab-content">
        <div class="loading-state">Loading…</div>
      </div>
    </div>
  `;

  // Expose client and active show so all tab handlers have reliable context
  window._currentClient = client;
  window._currentShow   = activeShow || null;

  if (activeShow) {
    await loadTabContent(state.currentTab || 'production', activeShow, client);
  }

  wireTabClicks(activeShow, client);
}

export function initClientCard() {
  window.renderClientCard = renderClientCard;
}

// ── Show Switcher ──────────────────────────────────────────────────────────

function renderShowSwitcher(client, shows, activeShowId) {
  if (!shows || shows.length === 0) {
    return `<div class="show-switcher empty">No shows yet
      <button class="btn-add-show" onclick="addShow(${client.id})">+ Add Show</button>
    </div>`;
  }

  const tabs = shows.map(show => {
    const isActive = show.id === activeShowId;
    const titleText = show.title || show.season || `Show ${show.id}`;
    return `
      <button class="show-tab ${isActive ? 'active' : ''}"
              data-show-id="${show.id}"
              onclick="switchShow(${show.id})">
        <span class="show-tab-title">${titleText}</span>
        ${show.season_year ? `<span class="show-tab-year">${show.season_year}</span>` : ''}
        ${isActive ? `<span class="show-tab-edit-btn" onclick="event.stopPropagation(); window.editShowTitle(${show.id})" title="Edit title">✏</span>` : ''}
      </button>
    `;
  }).join('');

  return `
    <div class="show-switcher">
      <div class="show-tabs-scroll">${tabs}</div>
      <button class="btn-add-show-icon" onclick="addShow(${client.id})" title="Add show">+</button>
      <button class="btn-yeet-show" onclick="window.openYeetModal(window._currentClient, window._currentShow)" title="Delete show or client">⚠</button>
    </div>
  `;
}

// ── Add Show Modal ─────────────────────────────────────────────────────────

window.addShow = function(clientId) {
  document.getElementById('add-show-modal')?.remove();

  const currentYear = new Date().getFullYear();
  const modal = document.createElement('div');
  modal.id = 'add-show-modal';
  modal.className = 'modal-overlay';
  modal.innerHTML = `
    <div class="modal-box" style="max-width:440px;">
      <div class="modal-header">
        <span class="modal-title" style="font-family:var(--font-arcade)">New Show</span>
        <button class="modal-close" onclick="document.getElementById('add-show-modal').remove()">✕</button>
      </div>
      <div class="modal-body" style="display:grid;gap:0.65rem;">
        <div>
          <label class="cfg-label">Production Title</label>
          <input type="text" id="as-title" class="cfg-text" style="width:100%" placeholder="e.g. Into the Storm">
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:0.5rem;">
          <div>
            <label class="cfg-label">Season Year</label>
            <input type="number" id="as-year" class="cfg-num" style="width:100%" value="${currentYear}">
          </div>
          <div>
            <label class="cfg-label">Show Type</label>
            <select id="as-type" class="cfg-text" style="width:100%">
              <option value="marching_band">Marching Band</option>
              <option value="composition">Composition</option>
            </select>
          </div>
        </div>
        <div>
          <label class="cfg-label">Deliver By Date <span style="color:#5a7a9a">(optional)</span></label>
          <input type="date" id="as-due" class="cfg-text" style="width:100%">
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn-fin-action" onclick="window.saveNewShow(${clientId})">Create Show</button>
        <button class="btn-fin-secondary" onclick="document.getElementById('add-show-modal').remove()">Cancel</button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
  document.getElementById('as-title')?.focus();
};

window.saveNewShow = async function(clientId) {
  const title = document.getElementById('as-title')?.value.trim();
  if (!title) { alert('Production title is required.'); return; }

  const payload = {
    client_id:   clientId,
    title,
    season_year: parseInt(document.getElementById('as-year')?.value) || new Date().getFullYear(),
    show_type:   document.getElementById('as-type')?.value || 'marching_band',
    due_date:    document.getElementById('as-due')?.value || null,
  };

  try {
    await apiFetch('/api/v1/shows', { method: 'POST', body: JSON.stringify(payload) });
    document.getElementById('add-show-modal')?.remove();
    await renderClientCard(clientId);
  } catch (err) {
    alert('Failed to create show: ' + err.message);
  }
};

// ── Status Banner ──────────────────────────────────────────────────────────

function renderStatusBanner(show, clientId) {
  const percent = show.percent_complete || 0;
  const dueDate = show.due_date ? formatDate(show.due_date) : 'No due date';

  const surveyDone = !!show.survey_returned_at;
  const surveySent = !!show.survey_sent_at && !surveyDone;

  const nonSurveyChips = [
    { label: 'Contract', done: show.contract_signed === true || show.contract_status === 'signed' },
    { label: 'Invoice',  done: show.invoiced === true || show.invoice_status === 'paid' },
    { label: 'Drive',    done: !!show.drive_folder_id },
  ];

  const nonSurveyHtml = nonSurveyChips.map(chip => `
    <span class="status-chip ${chip.done ? 'done' : 'pending'}">
      ${chip.done ? '✓' : '○'} ${chip.label}
    </span>
  `).join('');

  // Survey chip is interactive
  const surveyLabel = surveyDone ? '✓ Survey' : (surveySent ? '↻ Survey Sent' : '○ Survey');
  const surveyChipClass = surveyDone ? 'done' : (surveySent ? 'pending survey-sent' : 'pending');
  const surveyChipHtml = `
    <span class="status-chip ${surveyChipClass} clickable" onclick="window.handleSurveyChip(${show.id}, ${clientId})" title="${surveyDone ? 'View survey results' : (surveySent ? 'Resend or view survey' : 'Send ensemble survey')}">
      ${surveyLabel}
    </span>
  `;

  return `
    <div class="status-banner">
      <div class="progress-row">
        <div class="progress-bar-track">
          <div class="progress-bar-unfill" style="left:${percent}%"></div>
        </div>
        <span class="progress-percent">${percent}%</span>
        <span class="progress-due">Due: ${dueDate}</span>
      </div>
      <div class="status-chips">${nonSurveyHtml}${surveyChipHtml}</div>
    </div>
  `;
}

// ── Director Info Bar ──────────────────────────────────────────────────────

function renderDirectorBar(client, directorContacts = [], activeShow = null) {
  const currentYear = new Date().getFullYear();

  // Determine current director: is_primary contact, or one active this year, or fall back to client record
  const current = directorContacts.find(c => c.is_primary)
    || directorContacts.find(c => !c.end_year || c.end_year >= currentYear)
    || null;

  const past = directorContacts.filter(c => c.end_year && c.end_year < currentYear)
    .sort((a, b) => (b.end_year || 0) - (a.end_year || 0));

  // Display name/email/phone: prefer current contact, fall back to client record
  // Prefer show-level data (canonical after cascade) then contact then client fallback
  const displayName  = activeShow?.director_name  || current?.name  || client.name  || '';
  const displayEmail = activeShow?.director_email || current?.email || client.email || '';
  const displayPhone = activeShow?.director_phone || current?.phone || client.phone || '';
  const yearRange    = current
    ? (current.start_year ? `${current.start_year}${current.end_year ? '–' + current.end_year : '–present'}` : '')
    : '';

  const pastHtml = past.length ? `
    <div class="director-history" id="director-history-${client.id}" style="display:none;">
      ${past.map(p => `
        <div class="director-history-row">
          <span class="dh-name">${p.name}</span>
          <span class="dh-years">${p.start_year || '?'}–${p.end_year || '?'}</span>
          ${p.email ? `<a href="mailto:${p.email}" class="dh-email">${p.email}</a>` : ''}
          <button class="dh-edit-btn" onclick="window.openDirectorEdit(${p.id}, ${client.id})">Edit</button>
        </div>
      `).join('')}
    </div>
  ` : '';

  return `
    <div class="director-bar">
      ${client.org_name ? `<div class="director-school">${client.org_name}</div>` : ''}
      <div class="director-info-row">
        <span class="director-name">${displayName}</span>
        ${yearRange ? `<span class="director-year-range">${yearRange}</span>` : ''}
        ${displayEmail ? `<a href="mailto:${displayEmail}" class="director-contact">${displayEmail}</a>` : ''}
        ${displayPhone ? `<span class="director-contact">${displayPhone}</span>` : ''}
        ${client.state ? `<span class="director-location">${client.city ? client.city + ', ' : ''}${client.state}</span>` : ''}
        ${activeShow ? `<button class="dh-edit-btn" style="margin-left:0.4rem;" onclick="window.openCurrentDirectorEdit(${activeShow.id}, ${client.id})">✎ edit</button>` : ''}
        <span class="director-manage-btn" onclick="window.openDirectorManage(${client.id})">
          ${directorContacts.length ? '⊕ history' : '⊕ add director'}
        </span>
      </div>
      <div class="director-website-row">
        ${client.band_website
          ? `<a href="${client.band_website}" target="_blank" class="director-website-link" title="${client.band_website}">🔗 ${client.band_website.replace(/^https?:\/\//, '')}</a>`
          : `<span style="font-size:0.72rem;color:#3a5a7a;">No website on file</span>`}
        <button class="btn-find-website" id="btn-find-website-${client.id}"
                onclick="window.findBandWebsite(${client.id})">
          ${client.band_website ? '↻ Update' : '⌕ Find Website'}
        </button>
        ${client.band_website ? `
          <button class="btn-find-website" onclick="window.editBandWebsite(${client.id}, '${client.band_website}')">Edit</button>
        ` : ''}
      </div>
      ${pastHtml}
      ${past.length ? `
        <button class="director-history-toggle" onclick="
          const h = document.getElementById('director-history-${client.id}');
          if (h) { h.style.display = h.style.display === 'none' ? 'block' : 'none'; this.textContent = h.style.display === 'none' ? '▸ ${past.length} past director${past.length !== 1 ? 's' : ''}' : '▾ ${past.length} past director${past.length !== 1 ? 's' : ''}'; }
        ">▸ ${past.length} past director${past.length !== 1 ? 's' : ''}</button>
      ` : ''}
    </div>
  `;
}

// ── Tab Bar ────────────────────────────────────────────────────────────────

const TABS = ['production', 'sources', 'communications', 'files-team', 'schedule', 'results', 'copyright', 'finance'];
const TAB_LABELS = {
  'production':     'Production',
  'schedule':       'Schedule',
  'results':        'Results',
  'finance':        'Finance',
  'communications': 'Communications',
  'files-team':     'Files & Team',
  'copyright':      'Copyright',
  'sources':        'Design'
};

function renderTabBar(activeTab = 'production') {
  return `
    <div class="card-tab-bar">
      ${TABS.map(tab => `
        <button class="card-tab ${tab === activeTab ? 'active' : ''}" data-tab="${tab}">
          ${TAB_LABELS[tab]}
        </button>
      `).join('')}
    </div>
  `;
}

function wireTabClicks(show, client) {
  document.querySelectorAll('.card-tab').forEach(btn => {
    btn.addEventListener('click', async () => {
      // Flush pending autosave before leaving production tab — DOM still intact here
      if (_autosaveTimer) {
        clearTimeout(_autosaveTimer);
        _autosaveTimer = null;
        await window.saveShowConfig(_currentShow?.id || show.id).catch(() => {});
      }
      document.querySelectorAll('.card-tab').forEach(t => t.classList.remove('active'));
      btn.classList.add('active');
      state.currentTab = btn.dataset.tab;
      await loadTabContent(btn.dataset.tab, show, client);
    });
  });
}

// ── Tab Content Loader ─────────────────────────────────────────────────────

async function loadTabContent(tab, show, client) {
  const content = document.getElementById('tab-content');
  if (!content) return;
  content.innerHTML = '<div class="loading-state">Loading…</div>';

  try {
    switch (tab) {
      case 'production': {
        // Re-fetch latest show data so we render what's actually in the DB
        const freshShows = await apiFetch(`/api/v1/clients/${client.id}/shows`).catch(() => null);
        const freshShow = freshShows ? (freshShows.find(s => s.id === show.id) || show) : show;
        content.innerHTML = await renderProductionTab(freshShow, client.id);
        break;
      }
      case 'finance':
        content.innerHTML = await renderFinanceTab(show, client.id);
        break;
      case 'communications':
        content.innerHTML = await renderCommunicationsTab(client.id, show.id);
        break;
      case 'files-team':
        content.innerHTML = await renderFilesTeamTab(show, client.id);
        break;
      case 'copyright':
        content.innerHTML = await renderCopyrightTab(show.id, client.id);
        break;
      case 'sources':
        content.innerHTML = await renderSourcesTab(client.id, show.id);
        wireSourcesTab(client.id);
        break;
      case 'schedule':
        content.innerHTML = await renderScheduleTab(show, client);
        wireScheduleTab(client.id);
        break;
      case 'results':
        content.innerHTML = await renderResultsTab(show, client);
        wireResultsTab(client.id);
        break;
    }
  } catch (err) {
    console.error(`[client-card] Tab "${tab}" load error:`, err);
    content.innerHTML = `<div class="empty-state" style="color:var(--accent-pink)">Error loading ${tab}: ${err.message}</div>`;
  }
}

// ── Production Tab ─────────────────────────────────────────────────────────

async function renderProductionTab(show, clientId) {
  _currentShow = show; // save so saveShowConfig can merge all fields
  window._currentShow = show; // expose to inline HTML handlers (oninput/onchange in template literals)

  const [milestones, onboarding, autoLog] = await Promise.all([
    apiFetch(`/api/v1/shows/${show.id}/milestones`).catch(() => []),
    apiFetch(`/api/onboarding/client/${clientId}`).catch(() => []),
    apiFetch(`/api/automations/log?client_id=${clientId}`).catch(() => [])
  ]);

  const activeOnboarding = onboarding.find(o => !o.all_complete);
  const completedOnboarding = onboarding.find(o => o.all_complete);

  return `
    <div class="tab-panel active" id="tab-production">
      ${renderShowConfig(show)}
      ${activeOnboarding || completedOnboarding
        ? renderOnboardingTracker(activeOnboarding || completedOnboarding)
        : renderStartOnboardingButton(clientId, show.id)}
      ${renderMilestoneTimeline(milestones, show)}
      ${renderAutomationLog(autoLog, show.id)}
      <div class="section-label" style="margin-top:1rem;">Relationship</div>
      <div style="display:flex;gap:0.5rem;align-items:center;flex-wrap:wrap;padding:0.4rem 0;">
        <button class="btn-secondary btn-sm"
          onclick="window.runManualAction('run_csm_brief',{client_id:${clientId}},null,'Run CSM brief')">
          Run CSM Brief
        </button>
        <a href="/api/csm-advisor/brief/${clientId}" target="_blank" class="btn-secondary btn-sm"
          style="text-decoration:none;">View Last Brief ↗</a>
        <span class="auto-inline-hint" id="ma-status-csm-${clientId}"></span>
      </div>
    </div>
  `;
}

function renderStartOnboardingButton(clientId, showId) {
  return `
    <div class="onboarding-start-prompt">
      <div class="prompt-text">No active onboarding for this show.</div>
      <button class="btn-start-onboarding" onclick="window.launchOnboardingWizard(${clientId}, ${showId})">
        Start Onboarding →
      </button>
    </div>
  `;
}

function renderOnboardingTracker(session) {
  const steps = [
    { num: 1, label: 'Client Record',           field: 'step1_complete' },
    { num: 2, label: 'Contract Sent',            field: 'step2_complete' },
    { num: 3, label: 'Signed · Survey · Drive',  field: 'step3_complete' },
    { num: 4, label: 'Survey Return · Portal',   field: 'step4_complete' },
  ];

  const stepsHtml = steps.map(step => {
    const done   = session[step.field];
    const active = !done && session.current_step === step.num;
    return `
      <div class="onboarding-step ${done ? 'done' : active ? 'active' : 'upcoming'}">
        <span class="step-num">${done ? '✓' : step.num}</span>
        <span class="step-label">${step.label}</span>
      </div>
    `;
  }).join('');

  const allDone = session.all_complete;
  return `
    <div class="onboarding-tracker ${allDone ? 'complete' : ''}">
      <div class="tracker-header">
        <span class="tracker-title">Onboarding</span>
        ${allDone
          ? '<span class="tracker-complete-badge">Complete</span>'
          : `<span class="tracker-step-badge">Step ${session.current_step} of 4</span>`}
      </div>
      <div class="onboarding-steps">${stepsHtml}</div>
    </div>
  `;
}

function renderMilestoneTimeline(milestones, show) {
  if (!milestones || milestones.length === 0) {
    if (!show) return '';
    const hasDeliverDate = !!show.deliver_by_date;
    return `
      <div class="milestone-timeline">
        <div class="section-label">Milestones</div>
        <div style="padding:0.75rem 0;color:var(--text-muted);font-size:0.85rem;">
          ${hasDeliverDate
            ? `No milestones yet. <button class="btn-ghost btn-xs" onclick="window.generateMilestones(${show.id})">Generate from dates →</button>`
            : `Set a <strong>Deliver By</strong> date in Show Settings, then generate milestones.`
          }
        </div>
      </div>
    `;
  }

  return `
    <div class="milestone-timeline">
      <div class="section-label" style="display:flex;align-items:center;justify-content:space-between;">
        Milestones
        <span style="font-size:0.65rem;color:#3a5a7a;font-weight:400;text-transform:none;letter-spacing:0;">underway → complete</span>
      </div>
      ${milestones.map(m => {
        // Support new status field; fall back to legacy completed boolean
        const st = m.status || (m.completed ? 'complete' : 'not_started');
        const milId = m.id;
        const name  = m.milestone_name || m.name || '';

        // Box 1 (underway): filled yellow when underway or complete
        const box1Icon  = (st === 'underway' || st === 'complete') ? '●' : '○';
        // Box 2 (complete): green checkmark when complete
        const box2Icon  = st === 'complete' ? '✓' : '○';

        return `
          <div class="ms-row ms-${st}">
            <button class="ms-box ms-box1" title="Underway"
              onclick="window.setMilestoneStatus(${milId}, '${st}', 'box1')">
              <span class="ms-box1-icon">${box1Icon}</span>
            </button>
            <button class="ms-box ms-box2" title="Complete"
              onclick="window.setMilestoneStatus(${milId}, '${st}', 'box2')">
              <span class="ms-box2-icon">${box2Icon}</span>
            </button>
            <span class="ms-name">${name}</span>
            ${m.due_date ? `<span class="ms-due">${formatDate(m.due_date)}</span>` : ''}
          </div>
        `;
      }).join('')}
    </div>
  `;
}

function renderShowConfig(show) {
  const pct        = show.percent_complete || 0;
  const durStr     = formatMMSS(show.duration_seconds);
  const writStr    = formatMMSS(show.written_seconds);
  const feeDisplay = show.fee_cents ? (show.fee_cents / 100).toFixed(2) : '';
  const deliverVal = show.deliver_by_date ? show.deliver_by_date.slice(0, 10) : '';
  const readyVal   = show.ready_to_write_date ? show.ready_to_write_date.slice(0, 10) : '';
  const designVal  = show.design_status || 'not_started';

  const designOptHtml = DESIGN_STATUS_OPTIONS.map(o =>
    `<option value="${o.value}" ${designVal === o.value ? 'selected' : ''}>${o.label}</option>`
  ).join('');

  // Parse ensemble_parts safely
  let activeParts = [];
  try {
    activeParts = Array.isArray(show.ensemble_parts) ? show.ensemble_parts
      : (typeof show.ensemble_parts === 'string' ? JSON.parse(show.ensemble_parts || '[]') : []);
  } catch (_) {}

  // Group parts by group label
  const groups = {};
  PRESET_PARTS.forEach(p => {
    if (!groups[p.group]) groups[p.group] = [];
    groups[p.group].push(p);
  });

  const partsHtml = Object.entries(groups).map(([grp, pts]) => `
    <div class="cfg-parts-group">
      <div class="cfg-parts-group-label">${grp}</div>
      <div class="cfg-parts-chips">
        ${pts.map(p => `
          <label class="cfg-part-chip ${activeParts.includes(p.value) ? 'selected' : ''}">
            <input type="checkbox" class="cfg-part-cb" value="${p.value}"
                   ${activeParts.includes(p.value) ? 'checked' : ''}
                   onchange="this.closest('.cfg-part-chip').classList.toggle('selected', this.checked)">
            ${p.label}
          </label>
        `).join('')}
      </div>
    </div>
  `).join('');

  // Design status color
  const designColors = {
    not_started: '#5a7a9a', concept: '#5a7a9a',
    in_design: '#ffb400', design_review: '#ffb400', design_locked: '#ff9900',
    ready_to_write: '#00b4d8', writing: '#00c864',
    revisions: '#ff6b35', complete: '#00c864',
  };
  const dColor = designColors[designVal] || '#5a7a9a';

  return `
    <div class="show-config-block">
      <div class="show-config-header">
        <span class="section-label" style="margin:0">Show Settings</span>
        <span class="design-status-pill" style="background:${dColor}20;color:${dColor};border-color:${dColor}40">
          ${DESIGN_STATUS_OPTIONS.find(o => o.value === designVal)?.label || 'Not Started'}
        </span>
      </div>

      <div class="show-config-grid">

        <!-- Progress (spans full width) -->
        <div class="cfg-field cfg-field-wide">
          <label class="cfg-label">Writing Progress</label>
          <div class="cfg-prog-wrap">
            <div class="cfg-prog-track" id="cfg-prog-track">
              <div class="cfg-prog-unfill" id="cfg-prog-unfill" style="left:${pct}%"></div>
              <input type="range" id="cfg-pct-range" class="cfg-prog-input"
                     min="0" max="100" value="${pct}"
                     oninput="window._onProgSlider(this.value)">
            </div>
            <input type="number" id="cfg-pct-num" class="cfg-num"
                   min="0" max="100" value="${pct}"
                   oninput="window._onProgNum(this.value)">
            <span class="cfg-unit">%</span>
          </div>
        </div>

        <!-- Duration + Written side by side -->
        <div class="cfg-field">
          <label class="cfg-label">Total Duration</label>
          <input type="text" id="cfg-duration" class="cfg-text"
                 value="${durStr}" placeholder="7:30"
                 oninput="if(window._currentShow) window._scheduleAutosave(window._currentShow.id)"
                 onchange="if(window._currentShow) window._scheduleAutosave(window._currentShow.id)">
          <span class="cfg-hint">MM:SS</span>
        </div>

        <div class="cfg-field">
          <label class="cfg-label">Written So Far</label>
          <input type="text" id="cfg-written" class="cfg-text"
                 value="${writStr}" placeholder="0:00"
                 oninput="window._onWrittenInput(this.value)"
                 onchange="window._onWrittenInput(this.value)">
          <span class="cfg-hint">MM:SS — updates %</span>
        </div>

        <!-- Design Status -->
        <div class="cfg-field">
          <label class="cfg-label">Design Status</label>
          <select id="cfg-design" class="cfg-select"
                  onchange="if(window._currentShow) window._scheduleAutosave(window._currentShow.id)">
            ${designOptHtml}
          </select>
        </div>

        <!-- Fee -->
        <div class="cfg-field">
          <label class="cfg-label">Fee ($)</label>
          <input type="number" id="cfg-fee" class="cfg-text"
                 value="${feeDisplay}" placeholder="3500.00" step="0.01"
                 oninput="if(window._currentShow) window._scheduleAutosave(window._currentShow.id)">
        </div>

        <!-- Deliver By -->
        <div class="cfg-field">
          <label class="cfg-label">Deliver By</label>
          <input type="date" id="cfg-deliver" class="cfg-text" value="${deliverVal}"
                 onchange="if(window._currentShow) window._scheduleAutosave(window._currentShow.id)">
        </div>

        <!-- Ready to Write -->
        <div class="cfg-field">
          <label class="cfg-label">Ready to Write</label>
          <input type="date" id="cfg-ready" class="cfg-text" value="${readyVal}"
                 onchange="if(window._currentShow) window._scheduleAutosave(window._currentShow.id)">
        </div>

      </div>

      <!-- Show Parts -->
      <div class="cfg-parts-section">
        <label class="cfg-label">Show Parts / Scope</label>
        ${partsHtml.replace(/onchange="this\.closest\('\.cfg-part-chip'\)\.classList\.toggle\('selected', this\.checked\)"/g,
          `onchange="this.closest('.cfg-part-chip').classList.toggle('selected',this.checked); if(window._currentShow) window._scheduleAutosave(window._currentShow.id)"`)}
      </div>

      <div class="cfg-save-row">
        <span id="cfg-save-indicator" class="cfg-save-indicator"></span>
      </div>
    </div>
  `;
}

// ── Automation Log ─────────────────────────────────────────────────────────

const AUTO_EVENT_META = {
  contract_signed:           { label: 'Contract Signed',      color: 'green' },
  contract_signed_thankyou:  { label: 'Thank-you Sent',        color: 'green' },
  invoice_paid:              { label: 'Invoice Paid',          color: 'green' },
  invoice_paid_thankyou:     { label: 'Thank-you Sent',        color: 'green' },
  invoice_sent:              { label: 'Invoice Sent',          color: 'cyan'  },
  contract_unsigned_7d:      { label: 'Contract Reminder',     color: 'amber' },
  contract_unsigned_30d:     { label: 'Contract Escalation',   color: 'amber' },
  invoice_unpaid_14d:        { label: 'Invoice Reminder',      color: 'amber' },
  invoice_unpaid_30d:        { label: 'Invoice Escalation',    color: 'amber' },
  onboarding_started:        { label: 'Onboarding Started',    color: 'cyan'  },
  portal_provisioned:        { label: 'Portal Provisioned',    color: 'cyan'  },
};

function renderAutomationLog(log, showId) {
  if (!log || log.length === 0) return '';

  // Filter to this show's events or events with no show_id, take last 8
  const relevant = log
    .filter(e => !e.show_id || e.show_id === showId)
    .slice(0, 8);

  if (relevant.length === 0) return '';

  const rows = relevant.map(e => {
    const meta   = AUTO_EVENT_META[e.trigger_event] || { label: e.trigger_event, color: 'dim' };
    const failed = e.status === 'failed';
    const color  = failed ? 'red' : meta.color;
    const detail = e.result_detail ? e.result_detail.slice(0, 80) + (e.result_detail.length > 80 ? '…' : '') : '';
    const when   = e.executed_at ? formatDate(e.executed_at) : '';
    return `
      <div class="auto-log-row">
        <span class="auto-log-dot auto-dot-${color}"></span>
        <span class="auto-log-label">${meta.label}</span>
        ${detail ? `<span class="auto-log-detail">${detail}</span>` : ''}
        <span class="auto-log-date">${when}</span>
      </div>
    `;
  }).join('');

  return `
    <div class="auto-log-section">
      <div class="section-label">Automation Activity</div>
      ${rows}
    </div>
  `;
}

// ── Finance Tab ────────────────────────────────────────────────────────────

async function renderFinanceTab(show, clientId) {
  const contractDone = show.contract_signed === true || show.contract_status === 'signed';
  const invoiceDone  = show.invoiced === true || show.invoice_status === 'paid';
  const contractSent = show.contract_sent === true || show.contract_status === 'sent' || contractDone;

  const [quotes, invoiceResp, autoLog] = await Promise.all([
    apiFetch(`/api/v1/quotes?show_id=${show.id}`).catch(() => []),
    apiFetch(`/api/v1/invoices?show_id=${show.id}`).catch(() => ({ invoices: [] })),
    apiFetch(`/api/automations/log?client_id=${clientId}`).catch(() => []),
  ]);
  const invoices = Array.isArray(invoiceResp) ? invoiceResp : (invoiceResp?.invoices || []);

  // Pull last-fired events for inline hints
  const contractEvents = ['contract_signed','contract_unsigned_7d','contract_unsigned_30d','contract_signed_thankyou'];
  const invoiceEvents  = ['invoice_sent','invoice_paid','invoice_unpaid_14d','invoice_unpaid_30d','invoice_paid_thankyou'];
  const lastContractAuto = autoLog.find(e => contractEvents.includes(e.trigger_event));
  const lastInvoiceAuto  = autoLog.find(e => invoiceEvents.includes(e.trigger_event));

  // Fee display (cents → dollars or fallback to fee_cents column)
  const feeCents = show.fee_cents || (show.total_fee ? Math.round(show.total_fee * 100) : null);
  const feeDisplay = feeCents ? formatCurrency(feeCents / 100) : '—';

  // Quotes section
  const quotesHtml = quotes.length === 0
    ? '<div class="fin-empty">No quotes yet</div>'
    : quotes.map(q => `
        <div class="fin-quote-row">
          <span class="fin-quote-num">${q.quote_number}</span>
          <span class="fin-quote-amount">${q.amount_cents ? formatCurrency(q.amount_cents / 100) : '—'}</span>
          <span class="fin-badge fin-badge-${q.status || 'draft'}">${q.status || 'draft'}</span>
          ${q.issued_at ? `<span class="fin-quote-date">${formatDate(q.issued_at)}</span>` : ''}
        </div>
      `).join('');

  // Invoice log section
  const invoiceLogHtml = invoices.length === 0
    ? (show.invoice_number
        ? `<div class="fin-empty">Invoice #${show.invoice_number}${show.invoice_date ? ' · ' + formatDate(show.invoice_date) : ''}</div>`
        : '<div class="fin-empty">No invoices yet</div>')
    : invoices.map(inv => `
        <div class="fin-quote-row">
          <span class="fin-quote-num">${inv.invoice_number}</span>
          <span class="fin-quote-amount">${inv.amount_cents ? formatCurrency(inv.amount_cents / 100) : '—'}</span>
          <span class="fin-badge fin-badge-${inv.status || 'outstanding'}">${inv.status || 'outstanding'}</span>
          ${inv.created_at ? `<span class="fin-quote-date">${formatDate(inv.created_at)}</span>` : ''}
        </div>
      `).join('');

  const esc = s => String(s || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const showDirName  = show.director_name  || '';
  const showDirEmail = show.director_email || '';

  return `
    <div class="tab-panel active" id="tab-finance">

      <!-- Director — this show's season -->
      <div class="fin-block" style="background:rgba(255,255,255,.01);">
        <div class="fin-block-header">
          <span class="fin-block-title">Director</span>
          <span style="font-size:0.72rem;${show.director_locked ? 'color:#4caf50' : 'color:#ff9800'}">
            ${show.director_locked ? '🔒 Locked' : '⚠ Not locked — may revert'}
          </span>
        </div>
        <div class="fin-meta">${esc(showDirName) || '(not set)'} · ${esc(showDirEmail) || '(no email on file)'}</div>
        <div id="fin-dir-form-${show.id}" style="display:none;margin-top:8px;">
          <div style="display:flex;gap:6px;flex-wrap:wrap;align-items:flex-end;">
            <div>
              <div style="font-size:0.72rem;color:var(--text-muted);margin-bottom:2px;">Name</div>
              <input id="fin-dir-name-${show.id}" type="text" value="${esc(showDirName)}" placeholder="Director name"
                style="background:#0d1520;border:1px solid rgba(255,255,255,.15);border-radius:4px;color:var(--text);padding:5px 8px;font-size:0.82rem;width:175px;">
            </div>
            <div>
              <div style="font-size:0.72rem;color:var(--text-muted);margin-bottom:2px;">Email</div>
              <input id="fin-dir-email-${show.id}" type="email" value="${esc(showDirEmail)}" placeholder="Email"
                style="background:#0d1520;border:1px solid rgba(255,255,255,.15);border-radius:4px;color:var(--text);padding:5px 8px;font-size:0.82rem;width:195px;">
            </div>
            <button onclick="window.saveLockDirector(${show.id}, ${clientId})"
              style="background:rgba(76,175,80,.12);border:1px solid rgba(76,175,80,.3);color:#4caf50;border-radius:4px;padding:5px 10px;font-size:0.8rem;font-weight:600;cursor:pointer;">
              🔒 Lock &amp; Save
            </button>
            <button onclick="window.cancelDirEdit(${show.id})"
              style="background:none;border:1px solid rgba(255,255,255,.1);color:var(--text-muted);border-radius:4px;padding:5px 10px;font-size:0.8rem;cursor:pointer;">
              Cancel
            </button>
          </div>
        </div>
        <div class="fin-actions">
          <button class="btn-fin-secondary" onclick="window.showDirEdit(${show.id})">
            ${show.director_locked ? '✏ Update Director' : '🔒 Set &amp; Lock Director'}
          </button>
          ${show.director_locked ? `
            <button class="btn-fin-secondary" style="color:var(--text-muted);font-size:0.72rem;"
              onclick="window.unlockDirector(${show.id}, ${clientId})">Remove Lock</button>
          ` : ''}
        </div>
      </div>

      <div class="fin-section-hr"></div>

      <!-- Contract -->
      <div class="fin-block">
        <div class="fin-block-header">
          <span class="fin-block-title">Contract</span>
          <span class="fin-badge fin-badge-${contractDone ? 'signed' : contractSent ? 'sent' : 'none'}">
            ${contractDone ? '✓ Signed' : contractSent ? 'Sent' : 'Not started'}
          </span>
        </div>
        ${show.contract_sent_at   ? `<div class="fin-meta">Sent: ${formatDate(show.contract_sent_at)}</div>` : ''}
        ${show.contract_signed_at ? `<div class="fin-meta">Signed: ${formatDate(show.contract_signed_at)}</div>` : ''}
        ${lastContractAuto ? `<div class="auto-inline-hint auto-hint-${AUTO_EVENT_META[lastContractAuto.trigger_event]?.color || 'dim'}">
          ↳ ${AUTO_EVENT_META[lastContractAuto.trigger_event]?.label || lastContractAuto.trigger_event} · ${formatDate(lastContractAuto.executed_at)}
        </div>` : ''}
        <div class="fin-actions">
          ${!contractSent ? `<button class="btn-fin-action" style="background:rgba(255,45,120,0.15);border-color:rgba(255,45,120,0.4);color:#ff2d78;"
              onclick="window.openContractModal && window._currentClient ? window.openContractModal(window._currentClient, window._currentShow) : alert('Contract modal not ready — try refreshing.')">
            Send Contract
          </button>` : `<button class="btn-fin-action" style="background:rgba(0,180,216,.1);border-color:rgba(0,180,216,.3);color:var(--cyan);"
              onclick="window.openContractModal && window._currentClient ? window.openContractModal(window._currentClient, window._currentShow) : alert('Contract modal not ready — try refreshing.')">
            Resend Contract
          </button>`}
          <button class="btn-fin-action" onclick="markContractSigned(${show.id})"
                  ${contractDone ? 'disabled' : ''}>
            ${contractDone ? '✓ Signed' : 'Mark Signed'}
          </button>
          ${!contractDone ? `
            <button class="btn-fin-secondary" onclick="window.sendContractReminder(${show.id}, ${clientId})">
              Send Reminder from David
            </button>
            <button class="btn-fin-secondary" onclick="window.runManualAction('send_contract_30d_escalation',{client_id:${clientId},show_id:${show.id}},null,'Send 30-day escalation')">
              30-day Escalation
            </button>
            <span class="auto-inline-hint" id="ma-status-contract-esc-${show.id}"></span>
          ` : `
            <button class="btn-fin-secondary" onclick="window.runManualAction('send_contract_signed_thankyou',{client_id:${clientId},show_id:${show.id}},null,'Send contract thank-you')">
              Send Thank-you
            </button>
            <span class="auto-inline-hint" id="ma-status-contract-ty-${show.id}"></span>
          `}
        </div>
      </div>

      <div class="fin-section-hr"></div>

      <!-- Invoice -->
      <div class="fin-block">
        <div class="fin-block-header">
          <span class="fin-block-title">Invoice</span>
          <span class="fin-fee-amount">${feeDisplay}</span>
          <span class="fin-badge fin-badge-${invoiceDone ? 'paid' : show.invoiced ? 'outstanding' : 'none'}">
            ${invoiceDone ? '✓ Paid' : show.invoiced ? 'Outstanding' : 'Not issued'}
          </span>
        </div>
        ${show.invoice_date ? `<div class="fin-meta">Issued: ${formatDate(show.invoice_date)}</div>` : ''}
        ${show.paid_at     ? `<div class="fin-meta">Paid: ${formatDate(show.paid_at)}</div>` : ''}
        ${lastInvoiceAuto ? `<div class="auto-inline-hint auto-hint-${AUTO_EVENT_META[lastInvoiceAuto.trigger_event]?.color || 'dim'}">
          ↳ ${AUTO_EVENT_META[lastInvoiceAuto.trigger_event]?.label || lastInvoiceAuto.trigger_event} · ${formatDate(lastInvoiceAuto.executed_at)}
        </div>` : ''}
        <div class="fin-invoice-log">${invoiceLogHtml}</div>
        <div class="fin-actions">
          <button class="btn-fin-action" onclick="window.openSendInvoiceModal(${show.id}, ${clientId}, ${feeCents || 0})">
            Send Invoice via Stripe
          </button>
          <button class="btn-fin-action" onclick="markInvoicePaid(${show.id})"
                  ${invoiceDone ? 'disabled' : ''} style="background:rgba(0,200,100,0.1);border-color:rgba(0,200,100,0.3);color:#00c864">
            ${invoiceDone ? '✓ Paid' : 'Mark Paid'}
          </button>
          ${!invoiceDone ? `
            <button class="btn-fin-secondary" onclick="window.sendInvoiceReminder(${show.id}, ${clientId})">
              Send Reminder (14d) from David
            </button>
            <button class="btn-fin-secondary" onclick="window.runManualAction('send_invoice_reminder_30d',{client_id:${clientId},show_id:${show.id}},null,'Send 30-day invoice reminder')">
              30-day Reminder
            </button>
            <button class="btn-fin-secondary" onclick="window.openItemizeInvoiceModal(${show.id}, ${clientId}, ${feeCents || 0})">
              Itemize Invoice
            </button>
            <span class="auto-inline-hint" id="ma-status-inv-30d-${show.id}"></span>
          ` : `
            <button class="btn-fin-secondary" onclick="window.runManualAction('send_invoice_paid_thankyou',{client_id:${clientId},show_id:${show.id}},null,'Send invoice thank-you')">
              Send Thank-you
            </button>
            <span class="auto-inline-hint" id="ma-status-inv-ty-${show.id}"></span>
          `}
        </div>
      </div>

      <div class="fin-section-hr"></div>

      <!-- Quotes -->
      <div class="fin-block">
        <div class="fin-block-header">
          <span class="fin-block-title">Quotes</span>
          <button class="btn-fin-small" onclick="window.openNewQuoteModal(${show.id}, ${clientId})">+ New Quote</button>
        </div>
        <div class="fin-quote-list">${quotesHtml}</div>
      </div>

      <div class="fin-section-hr"></div>

      <!-- PO Intake -->
      <div class="fin-block">
        <div class="fin-block-header">
          <span class="fin-block-title">Purchase Orders</span>
          <button class="btn-fin-small" onclick="window.openPOIntakeModal(${show.id}, ${clientId})">+ Log PO</button>
        </div>
        ${show.po_number
          ? `<div class="fin-meta">PO on file: <strong>${show.po_number}</strong></div>`
          : '<div class="fin-empty">No PO on file</div>'}
      </div>

    </div>
  `;
}

// ── Communications Tab ─────────────────────────────────────────────────────

async function renderCommunicationsTab(clientId, showId) {
  const [notes, emails] = await Promise.all([
    apiFetch(`/api/v1/clients/${clientId}/notes`).catch(() => []),
    apiFetch(`/api/email-log?client_id=${clientId}`).catch(() => [])
  ]);

  const notesHtml = notes.length === 0
    ? '<div class="empty-state">No notes yet</div>'
    : notes.map(n => `
        <div class="note-item">
          <div class="note-meta">${formatDate(n.created_at)} · ${n.source || 'manual'}</div>
          <div class="note-body">${n.body || ''}</div>
        </div>
      `).join('');

  const emailHtml = emails.length === 0
    ? '<div class="empty-state">No emails logged</div>'
    : emails.map(e => `
        <div class="email-log-item ${e.direction || 'outbound'}">
          <div class="email-meta">${formatDate(e.sent_at || e.received_at)} · ${e.direction || 'out'} · ${e.from_account || ''}</div>
          <div class="email-subject">${e.subject || '(no subject)'}</div>
        </div>
      `).join('');

  return `
    <div class="tab-panel active" id="tab-communications">
      <div class="comms-grid">
        <div class="notes-column">
          <div class="column-header">
            Notes
            <button onclick="addNoteForClient(${clientId})" class="btn-add-note">+ Add</button>
          </div>
          <div class="notes-feed">${notesHtml}</div>
        </div>
        <div class="email-column">
          <div class="column-header">Email Log</div>
          <div class="email-log">${emailHtml}</div>
        </div>
      </div>
    </div>
  `;
}

// ── Files & Team Tab ───────────────────────────────────────────────────────

async function renderFilesTeamTab(show, clientId) {
  const sharesId = show.drive_shares_folder_id || '';
  const yearId   = show.drive_folder_id        || '';

  // ── Fetch all data in parallel ─────────────────────────────────────────────
  const [storedDrivePerms, collaborators, portalData, client, directorContacts] = await Promise.all([
    apiFetch(`/api/v1/drive/permissions-by-show/${show.id}`).catch(() => []),
    apiFetch(`/api/v1/collaborators/client/${clientId}`).catch(() => []),
    apiFetch(`/api/v1/portal/show/${show.id}`).catch(() => ({
      portal_token: null, portal_url: null, pins: [], messages: [],
      audio_url: null, pdf_url: null
    })),
    apiFetch(`/api/v1/clients/${clientId}`).catch(() => ({})),
    apiFetch(`/api/contacts/directors/${clientId}`).catch(() => [])
  ]);

  const pins        = portalData.pins     || [];
  const messages    = portalData.messages || [];
  const audioUrl    = portalData.audio_url || show.portal_audio_url || '';
  const pdfUrl      = portalData.pdf_url   || show.portal_pdf_url   || '';
  const portalToken = portalData.portal_token;
  const portalUrl   = portalData.portal_url;

  // Lookup maps
  const ROLE_LABEL = { reader: 'Viewer', commenter: 'Commenter', writer: 'Editor', fileOrganizer: 'Organizer' };
  const ROLE_COLOR = { reader: '#5a7a9a', commenter: '#f0a500', writer: '#2ecc71', fileOrganizer: '#00b4d8' };
  const driveByEmail = {};
  for (const p of storedDrivePerms) {
    if (p.email) driveByEmail[p.email.toLowerCase()] = p;
  }
  const pinByEmail = {};
  const pinByName  = {};
  for (const p of pins) {
    if (p.email) pinByEmail[p.email.toLowerCase()] = p;
    if (p.name) pinByName[p.name.toLowerCase().trim()] = p;
  }

  // ── BLOCK 1: Director Access ───────────────────────────────────────────────

  let portalStatusHtml;
  if (portalToken) {
    portalStatusHtml = `
      <div class="ft-status-row">
        <span class="ft-dot active"></span>
        <span style="color:#00c864;font-size:0.82rem;">Portal link active</span>
        <div class="ft-status-actions">
          <button class="btn-ft-sm" onclick="window.resendPortalInvites(${show.id})">Resend Invites</button>
          <button class="btn-ft-sm" style="color:#00b4d8;border-color:rgba(0,180,216,0.4);" onclick="window.openPortalPreview(${show.id})">Preview Portal</button>
          <button class="btn-ft-sm" style="color:#00b4d8;border-color:rgba(0,180,216,0.4);" onclick="window.sendDirectorPortalLink(${show.id},${client.id})">Send Director Link</button>
          <button class="btn-ft-sm" style="color:#00b4d8;border-color:rgba(0,180,216,0.4);" onclick="window.runManualAction('resend_portal_access_email',{client_id:${client.id},show_id:${show.id}},null,'Resend portal access email')">Resend Access Email</button>
          <button class="btn-ft-sm" style="color:#ff6b6b;border-color:rgba(255,107,107,0.3);" onclick="window.generatePortalToken(${show.id}, true)">Reset Link</button>
        </div>
      </div>
      <span class="auto-inline-hint" id="ma-status-portal-resend-${show.id}"></span>
      <div class="ft-link-row">
        <input type="text" readonly class="ft-link-input" value="${portalUrl}">
        <button class="btn-ft-sm" onclick="(()=>{navigator.clipboard.writeText('${portalUrl}');this.textContent='✓';setTimeout(()=>this.textContent='Copy',1500)}).call(this)">Copy</button>
      </div>`;
  } else {
    portalStatusHtml = `
      <div class="ft-status-row">
        <span class="ft-dot inactive"></span>
        <span style="color:#5a7a9a;font-size:0.82rem;">No portal link generated</span>
        <button class="btn-ft-sm" style="color:#00b4d8;border-color:rgba(0,180,216,0.4);" onclick="window.generatePortalToken(${show.id}, false)">Generate Link</button>
        <button class="btn-ft-sm" style="color:#00b4d8;border-color:rgba(0,180,216,0.4);" onclick="window.runManualAction('provision_portal',{client_id:${client.id},show_id:${show.id}},null,'Provision portal + send email')">Provision + Send Email</button>
      </div>
      <span class="auto-inline-hint" id="ma-status-portal-prov-${show.id}"></span>`;
  }

  const driveYearHtml  = yearId
    ? `<a href="https://drive.google.com/drive/folders/${yearId}" target="_blank" class="ft-folder-link">Open ↗</a>`
    : `<span style="color:#5a7a9a;font-size:0.78rem;">Not linked</span>`;
  const driveSharesHtml = sharesId
    ? `<a href="https://drive.google.com/drive/folders/${sharesId}" target="_blank" class="ft-folder-link">Open ↗</a>`
    : `<span style="color:#5a7a9a;font-size:0.78rem;">Not linked</span>`;

  // Use show-level director email first (new-season directors), fall back to client email
  const directorRawEmail = show.director_email || client.email || directorContacts?.[0]?.email || '';
  const directorName     = show.director_name  || client.name  || '';
  const directorEmail    = directorRawEmail.toLowerCase();
  const directorDrivePerm = directorEmail ? driveByEmail[directorEmail] : null;

  // Director's own PIN record (matched by email or name)
  const directorPin = directorEmail
    ? (pinByEmail[directorEmail] || pinByName[directorName.toLowerCase().trim()] || null)
    : pinByName[directorName.toLowerCase().trim()] || null;

  // Store director data globally so onclick handlers don't embed strings in HTML attributes
  window._directorPinCtx = window._directorPinCtx || {};
  const _origin = window.location.origin;
  window._directorPinCtx[show.id] = {
    name:        directorName || '',
    email:       directorRawEmail,
    directorUrl: portalData.director_url ? portalData.director_url.replace(/^https?:\/\/[^/]+/, _origin) : null,
    staffUrl:    portalToken ? `${_origin}/portal?staff=${portalToken}`   : null,
    studentUrl:  portalToken ? `${_origin}/portal?student=${portalToken}` : null,
  };

  const directorPinHtml = directorPin
    ? `<div style="margin-top:0.6rem;display:flex;flex-wrap:wrap;align-items:center;gap:0.6rem;">
         <span class="ft-pin-badge" style="font-size:1rem;">${directorPin.pin}</span>
         <button class="btn-ft-sm" onclick="window.resetPortalPin(${show.id},${directorPin.id})">Reset PIN</button>
         <button class="btn-ft-sm" style="color:#00b4d8;border-color:rgba(0,180,216,0.4);" onclick="window.invitePortalPin(${show.id},${directorPin.id})">Send Portal Invite</button>
         <div style="width:100%;margin-top:0.15rem;">
           ${directorPin.last_invited_at
             ? `<span style="font-size:0.75rem;color:#00b4d8;">✉ Invite sent ${formatDate(directorPin.last_invited_at)}</span>`
             : `<span style="font-size:0.75rem;color:#5a7a9a;">No invite sent yet</span>`}
           ${directorPin.last_login ? `<span style="font-size:0.75rem;color:#3a5a7a;margin-left:0.75rem;">Last login: ${formatDate(directorPin.last_login)}</span>` : ''}
         </div>
       </div>`
    : (directorRawEmail
        ? `<div style="margin-top:0.6rem;">
             <button class="btn-ft-sm" style="color:#00b4d8;border-color:rgba(0,180,216,0.4);" onclick="window.assignDirectorPin(${show.id})">Assign PIN</button>
           </div>`
        : `<div style="margin-top:0.5rem;font-size:0.75rem;color:#5a7a9a;">No email on client record — add one to assign portal access.</div>`);

  const directorBlock = `
    <div class="ft-block">
      <div class="ft-block-header">Director Access</div>
      <div class="ft-section-label">Portal</div>
      ${portalStatusHtml}
      ${directorPinHtml}
      <div class="ft-section-label" style="margin-top:0.85rem;">Google Drive</div>
      <div class="ft-drive-rows">
        <div class="ft-drive-row">
          <span class="ft-drive-badge year">Year</span>
          ${driveYearHtml}
          ${!yearId ? `<button class="btn-ft-sm" style="color:#00b4d8;border-color:rgba(0,180,216,0.4);"
              onclick="window.driveProvisionFolder(${show.id})">Generate ↗</button>` : ''}
          <button class="btn-ft-sm" onclick="window.openDriveFolderModal(${show.id}, 'year')">Link ✎</button>
        </div>
        <div class="ft-drive-row">
          <span class="ft-drive-badge shares">Shares</span>
          ${driveSharesHtml}
          <button class="btn-ft-sm" onclick="window.openDriveFolderModal(${show.id}, 'shares')">Link ✎</button>
          ${sharesId ? `<button class="btn-ft-sm" style="color:#00b4d8;border-color:rgba(0,180,216,0.4);" onclick="window.driveInviteAll(${show.id})">Send Invites</button>` : ''}
        </div>
      </div>
    </div>`;

  // ── BLOCK 2: Team & Access (unified table) ─────────────────────────────────

  window._collabPinCtx = window._collabPinCtx || {};
  const teamRows = collaborators.map(c => {
    const emailKey  = (c.email || '').toLowerCase();
    const nameKey   = (c.name  || '').toLowerCase().trim();
    const drivePerm = emailKey ? driveByEmail[emailKey] : null;
    const pin       = (emailKey && pinByEmail[emailKey]) || pinByName[nameKey] || null;
    window._collabPinCtx[c.id] = { name: c.name, email: c.email };

    const driveCell = drivePerm
      ? `<select class="ft-role-select" style="color:${ROLE_COLOR[drivePerm.role]||'#5a7a9a'}"
           onchange="window.driveUpdatePermission(${show.id},'${drivePerm.permission_id}',this.value,this)">
           <option value="reader"    ${drivePerm.role==='reader'    ?'selected':''}>Viewer</option>
           <option value="commenter" ${drivePerm.role==='commenter' ?'selected':''}>Commenter</option>
           <option value="writer"    ${drivePerm.role==='writer'    ?'selected':''}>Editor</option>
         </select>`
      : (c.email
          ? `<span class="ft-cell-muted">Not Shared</span>`
          : `<span class="ft-cell-muted">No email</span>`);

    const lastLogin    = pin?.last_login      ? `<div style="font-size:0.65rem;color:#3a5a7a;margin-top:0.1rem;">Last login: ${formatDate(pin.last_login)}</div>` : '';
    const invitedBadge = pin?.last_invited_at ? `<div style="font-size:0.65rem;color:#00b4d8;margin-top:0.1rem;">✉ Invite sent ${formatDate(pin.last_invited_at)}</div>` : '';
    const pinCell = pin
      ? `<span class="ft-pin-badge">${pin.pin}</span>
         <button class="btn-ft-icon" title="Reset PIN" onclick="window.resetPortalPin(${show.id},${pin.id})">↺</button>
         ${(pin.email || c.email) ? `<button class="btn-ft-icon" title="Send invite" onclick="window.invitePortalPin(${show.id},${pin.id})">✉</button>` : ''}
         ${invitedBadge}${lastLogin}`
      : (c.email
          ? `<button class="btn-ft-sm" onclick="window.grantPortalAccess(${show.id},${c.id})">Grant PIN</button>`
          : `<span class="ft-cell-muted">no email</span>`);

    return `<tr class="ft-team-row">
      <td>${c.name || '—'}</td>
      <td>${c.role || '—'}</td>
      <td>${c.email ? `<a href="mailto:${c.email}" class="ft-email-link">${c.email}</a>` : '—'}</td>
      <td>${c.phone || '—'}</td>
      <td>${driveCell}</td>
      <td class="ft-pin-cell">${pinCell}</td>
      <td><button class="btn-ft-icon" style="color:#ff6b6b;" title="Remove" onclick="window.removeCollaborator(${c.id},${clientId})">✕</button></td>
    </tr>`;
  });

  // People with PINs not in the collaborators list
  const collabNameSet = new Set(collaborators.map(c => (c.name||'').toLowerCase().trim()).filter(Boolean));
  const orphanPins = pins.filter(p => !collabNameSet.has((p.name||'').toLowerCase().trim()));
  const orphanRows = orphanPins.map(p => {
    const drivePerm = p.email ? driveByEmail[p.email.toLowerCase()] : null;
    const driveCell = drivePerm
      ? `<span style="color:${ROLE_COLOR[drivePerm.role]||'#5a7a9a'}">${ROLE_LABEL[drivePerm.role]||drivePerm.role}</span>`
      : `<span class="ft-cell-muted">—</span>`;
    return `<tr class="ft-team-row" style="opacity:0.8;">
      <td>${p.name} <span class="ft-cell-muted" style="font-size:0.65rem;">(PIN only)</span></td>
      <td>—</td>
      <td>${p.email || '—'}</td>
      <td>—</td>
      <td>${driveCell}</td>
      <td class="ft-pin-cell">
        <span class="ft-pin-badge">${p.pin}</span>
        <button class="btn-ft-icon" title="Reset PIN" onclick="window.resetPortalPin(${show.id},${p.id})">↺</button>
        ${p.email ? `<button class="btn-ft-icon" title="Resend invite" onclick="window.invitePortalPin(${show.id},${p.id})">✉</button>` : ''}
        <button class="btn-ft-icon" style="color:#ff6b6b;" title="Revoke" onclick="window.revokePortalPin(${show.id},${p.id})">✕</button>
        ${p.last_invited_at ? `<div style="font-size:0.65rem;color:#00b4d8;margin-top:0.1rem;">✉ Invite sent ${formatDate(p.last_invited_at)}</div>` : ''}
        ${p.last_login      ? `<div style="font-size:0.65rem;color:#3a5a7a;margin-top:0.1rem;">Last login: ${formatDate(p.last_login)}</div>`      : ''}
      </td>
      <td>—</td>
    </tr>`;
  });

  // ── Director row — appears as soon as Drive invites have been sent ─────────
  const directorDriveCell = !sharesId
    ? `<span class="ft-cell-muted">No Shares folder</span>`
    : directorDrivePerm
      ? `<select class="ft-role-select" style="color:${ROLE_COLOR[directorDrivePerm.role]||'#5a7a9a'}"
           onchange="window.driveUpdatePermission(${show.id},'${directorDrivePerm.permission_id}',this.value,this)">
           <option value="reader"    ${directorDrivePerm.role==='reader'    ?'selected':''}>Viewer</option>
           <option value="commenter" ${directorDrivePerm.role==='commenter' ?'selected':''}>Commenter</option>
           <option value="writer"    ${directorDrivePerm.role==='writer'    ?'selected':''}>Editor</option>
         </select>`
      : `<span class="ft-cell-muted">Not invited</span>`;

  const directorPinCell = directorPin
    ? `<span class="ft-pin-badge">${directorPin.pin}</span>`
    : (directorRawEmail
        ? `<button class="btn-ft-sm" onclick="window.assignDirectorPin(${show.id})">Assign PIN</button>`
        : `<span class="ft-cell-muted">no email</span>`);

  const showDirectorInTable = directorName || directorRawEmail || directorDrivePerm;
  const directorRow = showDirectorInTable ? `<tr class="ft-team-row" style="background:rgba(0,180,216,0.05);">
    <td><strong>${directorName || '—'}</strong></td>
    <td>Director</td>
    <td>${directorRawEmail ? `<a href="mailto:${directorRawEmail}" class="ft-email-link">${directorRawEmail}</a>` : '—'}</td>
    <td>—</td>
    <td>${directorDriveCell}</td>
    <td class="ft-pin-cell">${directorPinCell}</td>
    <td>—</td>
  </tr>` : '';

  const allRows = [...(directorRow ? [directorRow] : []), ...teamRows, ...orphanRows];
  const teamBlock = `
    <div class="ft-block">
      <div class="ft-block-header">Team &amp; Access</div>
      ${allRows.length
        ? `<table class="ft-team-table">
             <thead><tr>
               <th>Name</th><th>Role</th><th>Email</th><th>Phone</th>
               <th>Drive</th><th>Portal PIN</th><th></th>
             </tr></thead>
             <tbody>${allRows.join('')}</tbody>
           </table>`
        : '<div class="ft-empty">No team members yet</div>'}
      <div style="margin-top:0.75rem;display:flex;flex-wrap:wrap;gap:0.5rem;align-items:center;">
        <button class="btn-ft-sm" onclick="window.openAddCollaboratorModal(${clientId}, ${show.id})">+ Add Person</button>
        ${portalToken && pins.length > 0 ? `
          <button class="btn-ft-sm" style="color:#00b4d8;border-color:rgba(0,180,216,0.4);"
                  onclick="window.resendPortalInvites(${show.id})">Resend All Invites</button>
          <button class="btn-ft-sm" style="color:#f59e0b;border-color:rgba(245,158,11,0.3);"
                  onclick="window.resetAndResendAll(${show.id})">Reset &amp; Resend All</button>
        ` : ''}
      </div>
    </div>`;

  // ── BLOCK 3: Portal Files ──────────────────────────────────────────────────

  const filesBlock = `
    <div class="ft-block">
      <div class="ft-block-header">Portal Files</div>
      <div class="ft-file-row">
        <span class="ft-file-label">Audio</span>
        <input type="url" class="ft-file-input" id="pf-audio-input-${show.id}" placeholder="Google Drive link…" value="${audioUrl.replace(/"/g,'&quot;')}">
        ${audioUrl ? `<a href="${audioUrl}" target="_blank" class="btn-ft-sm">↗</a>` : ''}
        <button class="btn-ft-sm" onclick="window.savePortalFileLink(${show.id},'audio')">Save</button>
        ${audioUrl ? `<button class="btn-ft-sm" style="color:#ff6b6b;border-color:rgba(255,107,107,0.3);" onclick="window.clearPortalFileLink(${show.id},'audio')">Clear</button>` : ''}
      </div>
      <div class="ft-file-row" style="margin-top:0.5rem;">
        <span class="ft-file-label">Score PDF</span>
        <input type="url" class="ft-file-input" id="pf-pdf-input-${show.id}" placeholder="Google Drive link…" value="${pdfUrl.replace(/"/g,'&quot;')}">
        ${pdfUrl ? `<a href="${pdfUrl}" target="_blank" class="btn-ft-sm">↗</a>` : ''}
        <button class="btn-ft-sm" onclick="window.savePortalFileLink(${show.id},'pdf')">Save</button>
        ${pdfUrl ? `<button class="btn-ft-sm" style="color:#ff6b6b;border-color:rgba(255,107,107,0.3);" onclick="window.clearPortalFileLink(${show.id},'pdf')">Clear</button>` : ''}
      </div>
    </div>`;

  // ── BLOCK 4: Portal Conversation (2-level threaded) ────────────────────────

  function renderMsg(m, depth) {
    const isRyan = m.author_type === 'ryan';
    const name   = isRyan ? 'Ryan' : (m.author_name || 'Portal user');
    const ts     = formatDate(m.created_at);
    const indent = depth > 0 ? `style="margin-left:${depth * 1.5}rem;"` : '';
    const replies = (m.replies || []).map(r => depth < 1 ? renderMsg(r, depth + 1) : '').join('');
    const replyBoxId = `pct-reply-${m.id}`;
    const replyBtn = depth < 1
      ? `<button class="btn-ft-reply" onclick="window.openReplyBox('${replyBoxId}')">Reply</button>`
      : '';
    const toggleBtn = depth === 0 && (m.replies||[]).length
      ? `<button class="btn-ft-icon" onclick="window.toggleThread(${m.id})" title="Collapse/expand">−</button>`
      : '';
    return `
      <div class="pct-msg pct-depth-${depth} ${isRyan ? 'pct-ryan' : 'pct-client'}" ${indent}>
        <div class="pct-meta">
          <span class="pct-author">${name}</span>
          <span class="pct-ts">${ts}</span>
          ${toggleBtn}${replyBtn}
        </div>
        <div class="pct-body">${m.body || ''}</div>
        ${depth === 0 ? `<div class="pct-replies" id="pct-replies-${m.id}">${replies}</div>` : replies}
        ${depth < 1 ? `
          <div class="pct-reply-box" id="${replyBoxId}" style="display:none;margin-top:0.4rem;${depth>0?'margin-left:1.5rem;':''}">
            <textarea class="pct-reply-input" rows="2" placeholder="Reply…"></textarea>
            <button class="btn-ft-sm" style="margin-top:0.3rem;" onclick="window.sendPortalReply(${show.id},${m.id},this)">Send Reply</button>
          </div>` : ''}
      </div>`;
  }

  const convoHtml = messages.length
    ? messages.map(m => renderMsg(m, 0)).join('')
    : '<div class="ft-empty">No messages yet</div>';

  const convoBlock = `
    <div class="ft-block">
      <div class="ft-block-header">Portal Conversation</div>
      <div id="pct-feed-${show.id}" class="pct-feed">${convoHtml}</div>
      <div style="margin-top:0.75rem;">
        <textarea id="pct-new-body-${show.id}" class="pct-reply-input" rows="3" placeholder="Post a message visible to all portal users…"></textarea>
        <button class="btn-ft-sm" style="margin-top:0.4rem;color:#00b4d8;border-color:rgba(0,180,216,0.4);" onclick="window.sendPortalMessage(${show.id})">Post Message</button>
      </div>
    </div>`;

  return `<div class="ft-tab">${directorBlock}${teamBlock}${filesBlock}${convoBlock}</div>`;
}

// ── Copyright Tab ──────────────────────────────────────────────────────────

async function renderCopyrightTab(showId, clientId) {
  const records = await apiFetch(`/api/v1/copyright/client/${clientId}`).catch(() => []);

  // Show ALL records for the client (not just the active show — copyright data spans seasons)
  // Sort current show first, then by season desc
  const sorted = [...records].sort((a, b) => {
    const aIsCurrent = a.show_id === showId ? -1 : 0;
    const bIsCurrent = b.show_id === showId ? -1 : 0;
    if (aIsCurrent !== bIsCurrent) return aIsCurrent - bIsCurrent;
    return (b.season_year || 0) - (a.season_year || 0);
  });

  const rowsHtml = sorted.length === 0
    ? '<tr><td colspan="7" class="empty-state">No copyright records</td></tr>'
    : sorted.map(r => {
        const isCurrent = r.show_id === showId;
        const showCtx = r.show_title ? `${r.show_title}${r.season_year ? ' (' + r.season_year + ')' : ''}` : (r.season_year || '—');
        // Songs sub-list if present
        const songs = Array.isArray(r.songs) ? r.songs.filter(s => s && s.title) : [];
        const songsHtml = songs.length
          ? `<div style="margin-top:0.2rem;display:flex;flex-wrap:wrap;gap:0.3rem;">${songs.map(s => `
              <span style="font-size:0.68rem;padding:0.1rem 0.4rem;border-radius:3px;background:rgba(255,255,255,0.06);color:${s.status === 'approved' ? '#00c864' : s.status === 'waived' ? '#5a7a9a' : '#f59e0b'};">
                ${s.title}${s.status ? ' · ' + s.status : ''}
              </span>`).join('')}</div>`
          : '';
        return `
          <tr class="${isCurrent ? 'cr-current-show' : ''}">
            <td style="color:${isCurrent ? '#e8f4fd' : '#7a9ab8'};font-size:0.72rem;">${showCtx}</td>
            <td>${r.song_title || r.request_number || '—'}${songsHtml}</td>
            <td>${r.composer || r.org_name || '—'}</td>
            <td>${r.publisher || r.source || '—'}</td>
            <td>${r.agency || '—'}</td>
            <td><span class="cr-status status-${r.status || 'unknown'}">${r.status || '—'}</span></td>
          </tr>
        `;
      }).join('');

  return `
    <div class="tab-panel active" id="tab-copyright">
      <div class="copyright-table-wrap">
        <table class="copyright-table">
          <thead>
            <tr>
              <th>Show</th><th>Song / Request</th><th>Composer / Org</th><th>Publisher / Source</th><th>Agency</th><th>Status</th>
            </tr>
          </thead>
          <tbody>${rowsHtml}</tbody>
        </table>
      </div>
      ${records.length > 0 ? `<div style="font-size:0.72rem;color:#3a5a7a;margin-top:0.5rem;padding:0 0.25rem;">${records.length} record${records.length !== 1 ? 's' : ''} across all seasons — current show highlighted</div>` : ''}
      ${records.length > 0 ? `
        <div style="display:flex;gap:0.5rem;align-items:center;margin-top:0.75rem;flex-wrap:wrap;">
          <button class="btn-secondary btn-sm"
            onclick="window.runManualAction('send_copyright_cleared_notification',{client_id:${clientId},show_id:${showId}},null,'Send copyright cleared notification')">
            Notify Director — All Cleared
          </button>
          <span class="auto-inline-hint" id="ma-status-cr-notif-${showId}"></span>
        </div>
      ` : ''}
    </div>
  `;
}

// ── Sources Tab ───────────────────────────────────────────────────────────

function spotifyEmbedUrl(url) {
  const m = url.match(/open\.spotify\.com\/(track|album|playlist)\/([A-Za-z0-9]+)/);
  return m ? `https://open.spotify.com/embed/${m[1]}/${m[2]}?utm_source=generator&theme=0` : null;
}

function youtubeEmbedUrl(url) {
  const m = url.match(/(?:v=|youtu\.be\/|embed\/)([A-Za-z0-9_-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

function renderMaterialItem(m) {
  const isSpotify   = m.type === 'spotify' || m.url?.includes('spotify.com');
  const isYoutube   = m.type === 'youtube' || m.url?.includes('youtube.com') || m.url?.includes('youtu.be');
  const isPdf       = m.type === 'pdf';
  const isReference = m.type === 'reference' || !m.url;

  const embedHtml = isSpotify && spotifyEmbedUrl(m.url) ? `
    <iframe src="${spotifyEmbedUrl(m.url)}" width="100%" height="80"
      frameborder="0" allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
      style="border-radius:6px;margin-top:0.4rem;display:block;"></iframe>
  ` : isYoutube && youtubeEmbedUrl(m.url) ? `
    <iframe src="${youtubeEmbedUrl(m.url)}" width="100%" height="160"
      frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      style="border-radius:6px;margin-top:0.4rem;display:block;" allowfullscreen></iframe>
  ` : '';

  const typeBadge = isSpotify ? 'spotify' : isYoutube ? 'youtube' : isPdf ? 'pdf' : isReference ? 'ref' : 'url';
  const typeIcon  = isSpotify ? '♪' : isYoutube ? '▶' : isPdf ? '📄' : isReference ? '★' : '🔗';
  const notesHtml = m.notes ? `<div class="source-item-notes">${m.notes}</div>` : '';
  const linkHtml  = m.url ? `<a href="${m.url}" target="_blank" class="source-link-out" title="Open in new tab">↗</a>` : '';

  return `
    <div class="source-item" data-id="${m.id}" draggable="true">
      <div class="source-item-header">
        <span class="source-drag-handle" title="Drag to reorder">⠿</span>
        <span class="source-type-badge source-type-${typeBadge}">${typeIcon}</span>
        <span class="source-label">${m.label || m.url || 'Untitled'}</span>
        ${linkHtml}
        <button class="source-dismiss-btn" onclick="window.deleteSourceMaterial(${m.id})" title="Remove">✕</button>
      </div>
      ${notesHtml}
      ${embedHtml}
    </div>
  `;
}

// ── Design doc helpers ────────────────────────────────────────────────────

function detectDocType(url) {
  if (!url) return 'url';
  if (/docs\.google\.com\/document/.test(url))     return 'gdoc';
  if (/docs\.google\.com\/presentation/.test(url)) return 'gslides';
  if (/docs\.google\.com\/spreadsheets/.test(url)) return 'gsheet';
  if (/drive\.google\.com\/file/.test(url))        return 'gdrive';
  if (/drive\.google\.com/.test(url))              return 'gdrive';
  if (/\.pdf($|\?)/i.test(url))                    return 'pdf';
  return 'url';
}

function docTypeIcon(type) {
  return { gdoc: '📝', gslides: '📊', gsheet: '📋', gdrive: '📄', pdf: '📄', url: '🔗' }[type] || '🔗';
}

function docTypeLabel(type) {
  return { gdoc: 'Google Doc', gslides: 'Google Slides', gsheet: 'Google Sheets', gdrive: 'Drive PDF', pdf: 'PDF', url: 'Link' }[type] || 'Link';
}

function designDocEmbedUrl(url, type) {
  const fileMatch = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (type === 'gdrive' && fileMatch) return `https://drive.google.com/file/d/${fileMatch[1]}/preview`;

  const docMatch = url.match(/docs\.google\.com\/(document|presentation|spreadsheets)\/d\/([a-zA-Z0-9_-]+)/);
  if (docMatch) return `https://docs.google.com/${docMatch[1]}/d/${docMatch[2]}/preview`;

  if (type === 'pdf') return url;
  return null; // can't embed — open in new tab
}

window.openDesignDoc = function(url, type) {
  const embedUrl = designDocEmbedUrl(url, type);
  if (!embedUrl) { window.open(url, '_blank'); return; }
  const w = window.open('', 'wmm_design_doc',
    'width=1020,height=820,menubar=no,toolbar=no,location=no,status=no,scrollbars=yes,resizable=yes');
  if (!w) { window.open(url, '_blank'); return; }
  w.document.write(`<!DOCTYPE html>
<html><head>
  <meta charset="utf-8">
  <title>Design Document — White Mage Music</title>
  <style>*{margin:0;padding:0;box-sizing:border-box;}body{background:#111;}iframe{display:block;width:100vw;height:100vh;border:none;}</style>
</head>
<body>
  <iframe src="${embedUrl}" allow="autoplay"></iframe>
</body></html>`);
  w.document.close();
};

function renderDesignDocSaved(doc, showId) {
  const icon  = docTypeIcon(doc.doc_type);
  const badge = docTypeLabel(doc.doc_type);
  return `
    <div class="design-doc-row" data-doc-id="${doc.id}"
         style="display:flex;align-items:center;gap:0.6rem;padding:0.5rem 0;border-bottom:1px solid rgba(0,180,216,0.08);">
      <span style="font-size:1rem;">${icon}</span>
      <span style="flex:1;font-size:0.875rem;color:#e8f4fd;">${doc.label}</span>
      <span style="font-size:0.68rem;color:#5a7a9a;border:1px solid rgba(90,122,154,0.25);border-radius:3px;padding:0.1rem 0.4rem;">${badge}</span>
      <button class="btn-ft-sm" onclick="window.openDesignDoc('${doc.url.replace(/'/g,"\\'")}','${doc.doc_type}')">↗ Open</button>
      <button class="btn-ft-sm" style="color:#f59e0b;border-color:rgba(245,158,11,0.3);"
              onclick="window.replaceDesignDoc(${doc.id}, ${showId}, this)">Replace</button>
      <button class="btn-ft-sm" style="color:#ff6b6b;border-color:rgba(255,107,107,0.3);"
              onclick="window.deleteDesignDoc(${doc.id}, ${showId})">✕</button>
    </div>
  `;
}

function renderDesignDocNewRow(showId, label) {
  return `
    <div class="design-doc-new-row"
         style="display:flex;align-items:center;gap:0.5rem;padding:0.5rem 0;flex-wrap:wrap;">
      <input type="text" class="cfg-text design-doc-label-inp" placeholder="Label"
             value="${label || ''}" style="width:180px;">
      <input type="text" class="cfg-text design-doc-url-inp" placeholder="Paste Google Doc, Drive, or PDF link…"
             style="flex:1;min-width:220px;">
      <button class="btn-ft-sm" style="color:#00b4d8;border-color:rgba(0,180,216,0.4);"
              onclick="window.saveDesignDocRow(this, ${showId})">Save</button>
      <button class="btn-ft-sm" style="color:#5a7a9a;"
              onclick="this.closest('.design-doc-new-row').remove()">✕</button>
    </div>
  `;
}

async function renderDesignDocsBlock(showId) {
  const docs = await apiFetch(`/api/design-docs/show/${showId}`).catch(() => []);
  const docsHtml = docs.map(d => renderDesignDocSaved(d, showId)).join('');
  const hasNone  = docs.length === 0;

  return `
    <div class="fin-block" id="design-docs-block">
      <div class="fin-block-header">
        <span class="fin-block-title">Design Documents</span>
      </div>
      <div id="design-docs-list">
        ${hasNone ? renderDesignDocNewRow(showId, 'Show Design Document') : docsHtml}
      </div>
      <div style="margin-top:0.5rem;">
        <button class="btn-fin-small" onclick="window.addDesignDocRow(${showId})">+ Add Additional Document</button>
      </div>
    </div>
    <div class="fin-section-hr"></div>
  `;
}

window.addDesignDocRow = function(showId) {
  const list = document.getElementById('design-docs-list');
  if (!list) return;
  const row = document.createElement('div');
  row.innerHTML = renderDesignDocNewRow(showId, '');
  list.appendChild(row.firstElementChild);
};

window.saveDesignDocRow = async function(btn, showId) {
  const row   = btn.closest('.design-doc-new-row');
  const label = row.querySelector('.design-doc-label-inp')?.value.trim() || 'Show Design Document';
  const url   = row.querySelector('.design-doc-url-inp')?.value.trim();
  if (!url) { alert('Paste a link first.'); return; }
  const type  = detectDocType(url);
  btn.disabled = true; btn.textContent = 'Saving…';
  try {
    await apiFetch(`/api/design-docs/show/${showId}`, {
      method: 'POST',
      body: JSON.stringify({ label, url, doc_type: type })
    });
    // Re-render the whole block
    const block = document.getElementById('design-docs-block');
    if (block) {
      const newBlock = document.createElement('div');
      newBlock.innerHTML = await renderDesignDocsBlock(showId);
      block.replaceWith(newBlock.firstElementChild);
    }
  } catch (err) {
    alert('Failed to save: ' + err.message);
    btn.disabled = false; btn.textContent = 'Save';
  }
};

window.deleteDesignDoc = async function(docId, showId) {
  if (!confirm('Remove this document?')) return;
  try {
    await apiFetch(`/api/design-docs/${docId}`, { method: 'DELETE' });
    const block = document.getElementById('design-docs-block');
    if (block) {
      const newBlock = document.createElement('div');
      newBlock.innerHTML = await renderDesignDocsBlock(showId);
      block.replaceWith(newBlock.firstElementChild);
    }
  } catch (err) {
    alert('Failed to remove: ' + err.message);
  }
};

window.replaceDesignDoc = async function(docId, showId, btn) {
  // Delete the existing doc then add a new row in its place
  try {
    await apiFetch(`/api/design-docs/${docId}`, { method: 'DELETE' });
    const block = document.getElementById('design-docs-block');
    if (block) {
      const newBlock = document.createElement('div');
      newBlock.innerHTML = await renderDesignDocsBlock(showId);
      block.replaceWith(newBlock.firstElementChild);
      // Focus the URL input that appears
      document.querySelector('.design-doc-url-inp')?.focus();
    }
  } catch (err) {
    alert('Failed: ' + err.message);
  }
};

async function renderSourcesTab(clientId, showId) {
  let materials = await apiFetch(`/api/v4/source-materials/client/${clientId}`).catch(() => []);

  // Auto-resolve search URLs to real track/video URLs (enables embeds)
  const hasUnresolvedSpotify = materials.some(
    m => m.type === 'spotify' && m.url?.includes('open.spotify.com/search/')
  );
  const hasUnresolvedYoutube = materials.some(
    m => m.type === 'youtube' && m.url?.includes('youtube.com/results?')
  );
  if (hasUnresolvedSpotify) {
    const result = await apiFetch(`/api/v4/source-materials/client/${clientId}/resolve-spotify`, {
      method: 'POST'
    }).catch(() => null);
    if (result?.resolved > 0) materials = result.items;
  }
  if (hasUnresolvedYoutube) {
    const result = await apiFetch(`/api/v4/source-materials/client/${clientId}/resolve-youtube`, {
      method: 'POST'
    }).catch(() => null);
    if (result?.resolved > 0) materials = result.items;
  }

  const listHtml = materials.length === 0
    ? '<div class="empty-state">No source materials yet</div>'
    : materials.map(renderMaterialItem).join('');

  const designDocsHtml = await renderDesignDocsBlock(showId);

  return `
    <div class="tab-panel active" id="tab-sources">

      <!-- Design Documents -->
      ${designDocsHtml}

      <!-- Materials list -->
      <div class="fin-block">
        <div class="fin-block-header">
          <span class="fin-block-title">Source Materials</span>
          <button class="btn-fin-small" onclick="window.loadSourceSuggestions(${clientId})">✦ Suggest from Copyright</button>
        </div>
        <div id="source-materials-list" class="source-list">
          ${listHtml}
        </div>
      </div>

      <div class="fin-section-hr"></div>

      <!-- Add material -->
      <div class="fin-block">
        <div class="fin-block-header"><span class="fin-block-title">Add Material</span></div>
        <div class="source-add-form">
          <div style="display:flex;gap:0.5rem;flex-wrap:wrap;align-items:flex-end;">
            <div>
              <label class="cfg-label">Type</label>
              <select id="src-type" class="cfg-select" style="width:110px;" onchange="window._onSrcTypeChange()">
                <option value="spotify">Spotify</option>
                <option value="youtube">YouTube</option>
                <option value="url">URL</option>
              </select>
            </div>
            <div style="flex:1;min-width:200px;">
              <label class="cfg-label">URL</label>
              <input type="text" id="src-url" class="cfg-text" style="width:100%;" placeholder="Paste link here…">
            </div>
            <div style="flex:1;min-width:150px;">
              <label class="cfg-label">Label</label>
              <input type="text" id="src-label" class="cfg-text" style="width:100%;" placeholder="Song title or description">
            </div>
            <button class="btn-fin-action" id="src-add-btn" onclick="window.addSourceMaterial(${clientId}, ${showId})">Add</button>
          </div>
          <div id="src-playlist-note" style="display:none;margin-top:0.5rem;">
            <span style="font-size:0.75rem;color:#f59e0b;">Spotify playlist detected — </span>
            <button class="btn-fin-small" onclick="window.parseSpotifyPlaylist(${clientId}, ${showId})">Import all tracks</button>
          </div>
        </div>
      </div>

      <div class="fin-section-hr"></div>

      <!-- PDF upload -->
      <div class="fin-block">
        <div class="fin-block-header"><span class="fin-block-title">Upload PDF Score</span></div>
        <div style="display:flex;gap:0.5rem;align-items:center;flex-wrap:wrap;">
          <input type="file" id="src-pdf-file" accept=".pdf" style="font-size:0.82rem;color:#7a9ab8;">
          <input type="text" id="src-pdf-label" class="cfg-text" placeholder="Label (optional)" style="width:200px;">
          <button class="btn-fin-action" onclick="window.uploadSourcePdf(${clientId})">Upload to Drive</button>
        </div>
        <div id="src-pdf-status" style="font-size:0.75rem;color:#5a7a9a;margin-top:0.4rem;"></div>
      </div>

      <!-- Suggestions panel (hidden until loaded) -->
      <div id="source-suggestions-panel" style="display:none;">
        <div class="fin-section-hr"></div>
        <div class="fin-block">
          <div class="fin-block-header"><span class="fin-block-title">✦ Suggestions</span></div>
          <div id="source-suggestions-list"></div>
        </div>
      </div>

    </div>
  `;
}

function wireSourcesTab(clientId) {
  // Drag-to-reorder
  const list = document.getElementById('source-materials-list');
  if (!list) return;

  let dragSrc = null;

  list.addEventListener('dragstart', e => {
    dragSrc = e.target.closest('.source-item');
    if (dragSrc) { dragSrc.classList.add('dragging'); e.dataTransfer.effectAllowed = 'move'; }
  });
  list.addEventListener('dragend', e => {
    const el = e.target.closest('.source-item');
    if (el) el.classList.remove('dragging');
    document.querySelectorAll('.source-item.drag-over').forEach(el => el.classList.remove('drag-over'));
    window._saveSourceOrder(clientId);
  });
  list.addEventListener('dragover', e => {
    e.preventDefault();
    const target = e.target.closest('.source-item');
    if (target && target !== dragSrc) {
      document.querySelectorAll('.source-item.drag-over').forEach(el => el.classList.remove('drag-over'));
      target.classList.add('drag-over');
      const rect = target.getBoundingClientRect();
      const after = e.clientY > rect.top + rect.height / 2;
      list.insertBefore(dragSrc, after ? target.nextSibling : target);
    }
  });
  list.addEventListener('dragleave', e => {
    const target = e.target.closest('.source-item');
    if (target) target.classList.remove('drag-over');
  });

  // Detect Spotify playlist paste
  const urlInput = document.getElementById('src-url');
  if (urlInput) {
    urlInput.addEventListener('input', () => {
      const val = urlInput.value;
      const isPlaylist = val.includes('spotify.com/playlist');
      const note = document.getElementById('src-playlist-note');
      if (note) note.style.display = isPlaylist ? 'block' : 'none';
    });
  }
}

window._saveSourceOrder = async function(clientId) {
  const items = [...document.querySelectorAll('#source-materials-list .source-item')];
  const payload = items.map((el, i) => ({ id: parseInt(el.dataset.id), sort_order: i }));
  await apiFetch('/api/v4/source-materials/reorder', {
    method: 'PATCH',
    body: JSON.stringify({ items: payload })
  }).catch(() => {});
};

window._onSrcTypeChange = function() {
  const type = document.getElementById('src-type')?.value;
  const urlInput = document.getElementById('src-url');
  if (urlInput) {
    urlInput.placeholder = type === 'spotify' ? 'Spotify track or playlist URL…'
      : type === 'youtube' ? 'YouTube video URL…'
      : 'https://…';
  }
};

window.addSourceMaterial = async function(clientId, showId) {
  const url   = document.getElementById('src-url')?.value.trim();
  const label = document.getElementById('src-label')?.value.trim();
  const type  = document.getElementById('src-type')?.value || 'url';
  if (!url) { alert('Please enter a URL.'); return; }

  const btn = document.getElementById('src-add-btn');
  if (btn) { btn.textContent = 'Adding…'; btn.disabled = true; }

  try {
    await apiFetch(`/api/v4/source-materials/client/${clientId}`, {
      method: 'POST',
      body: JSON.stringify({ type, url, label: label || url, show_id: showId })
    });
    state.currentTab = 'sources';
    await renderClientCard(state.currentClientId);
  } catch (err) {
    alert('Failed to add: ' + err.message);
    if (btn) { btn.textContent = 'Add'; btn.disabled = false; }
  }
};

window.deleteSourceMaterial = async function(id) {
  try {
    await apiFetch(`/api/v4/source-materials/${id}`, { method: 'DELETE' });
    const el = document.querySelector(`.source-item[data-id="${id}"]`);
    if (el) el.remove();
  } catch (err) {
    alert('Failed to remove: ' + err.message);
  }
};

window.parseSpotifyPlaylist = async function(clientId, showId) {
  const url = document.getElementById('src-url')?.value.trim();
  if (!url) return;
  const btn = document.querySelector('[onclick*="parseSpotifyPlaylist"]');
  if (btn) { btn.textContent = 'Importing…'; btn.disabled = true; }

  try {
    const result = await apiFetch('/api/v4/source-materials/parse-spotify-playlist', {
      method: 'POST',
      body: JSON.stringify({ url })
    });
    const tracks = result.tracks || [];
    if (!tracks.length) { alert('No tracks found in playlist.'); return; }

    for (const track of tracks) {
      await apiFetch(`/api/v4/source-materials/client/${clientId}`, {
        method: 'POST',
        body: JSON.stringify({
          type: 'spotify',
          url: track.url,
          label: `${track.title} — ${track.artist}`,
          show_id: showId
        })
      });
    }
    alert(`Imported ${tracks.length} tracks from playlist.`);
    state.currentTab = 'sources';
    await renderClientCard(state.currentClientId);
  } catch (err) {
    alert('Import failed: ' + err.message);
    if (btn) { btn.textContent = 'Import all tracks'; btn.disabled = false; }
  }
};

window.uploadSourcePdf = async function(clientId) {
  const fileInput = document.getElementById('src-pdf-file');
  const label     = document.getElementById('src-pdf-label')?.value.trim();
  const status    = document.getElementById('src-pdf-status');
  if (!fileInput?.files?.length) { alert('Please select a PDF file.'); return; }

  if (status) status.textContent = 'Uploading…';
  const formData = new FormData();
  formData.append('file', fileInput.files[0]);
  if (label) formData.append('label', label);

  try {
    const result = await fetch(`/api/v4/source-materials/client/${clientId}/upload`, {
      method: 'POST',
      body: formData
    }).then(r => r.json());

    if (result.error) throw new Error(result.error);
    if (status) status.textContent = `✓ Uploaded: ${result.label}`;
    state.currentTab = 'sources';
    await renderClientCard(state.currentClientId);
  } catch (err) {
    if (status) status.textContent = 'Upload failed: ' + err.message;
  }
};

window.loadSourceSuggestions = async function(clientId) {
  const panel = document.getElementById('source-suggestions-panel');
  const list  = document.getElementById('source-suggestions-list');
  if (!panel || !list) return;
  panel.style.display = 'block';
  list.innerHTML = '<div class="david-loading">Scanning copyright records and notes…</div>';

  try {
    const result = await apiFetch(`/api/v4/source-materials/client/${clientId}/suggestions`);
    const suggestions = result.suggestions || [];
    if (!suggestions.length) {
      list.innerHTML = '<div class="empty-state">No new suggestions found.</div>';
      return;
    }
    list.innerHTML = suggestions.map((s, i) => `
      <div class="source-suggestion-row" data-idx="${i}">
        <span class="source-type-badge source-type-${s.type}">${s.type === 'spotify' ? '♪' : '▶'}</span>
        <span class="source-suggestion-label">${s.label}</span>
        <a href="${s.url}" target="_blank" class="source-link-out" style="margin-left:auto;">↗</a>
        <button class="btn-fin-small" onclick="window.addSuggestion(${clientId}, ${i})">+ Add</button>
      </div>
    `).join('');
    // Store suggestions for addSuggestion
    window._sourceSuggestions = suggestions;
  } catch (err) {
    list.innerHTML = `<div class="empty-state" style="color:#ff6b6b;">Failed: ${err.message}</div>`;
  }
};

window.addSuggestion = async function(clientId, idx) {
  const s = window._sourceSuggestions?.[idx];
  if (!s) return;
  try {
    await apiFetch(`/api/v4/source-materials/client/${clientId}`, {
      method: 'POST',
      body: JSON.stringify({ type: s.type, url: s.url, label: s.label })
    });
    // Remove from suggestions list
    document.querySelector(`.source-suggestion-row[data-idx="${idx}"]`)?.remove();
    // Reload list
    const materials = await apiFetch(`/api/v4/source-materials/client/${clientId}`).catch(() => []);
    const listEl = document.getElementById('source-materials-list');
    if (listEl) listEl.innerHTML = materials.length ? materials.map(renderMaterialItem).join('') : '<div class="empty-state">No source materials yet</div>';
    wireSourcesTab(clientId);
  } catch (err) {
    alert('Failed to add: ' + err.message);
  }
};

// ── Global window handlers ─────────────────────────────────────────────────

// ── Show Config Save ───────────────────────────────────────────────────────

window.saveShowConfig = async function(showId) {
  if (!_currentShow) return;

  // Read form inputs
  const pct      = parseInt(document.getElementById('cfg-pct-num')?.value ?? _currentShow.percent_complete ?? 0);
  const durStr   = document.getElementById('cfg-duration')?.value?.trim() || '';
  const writStr  = document.getElementById('cfg-written')?.value?.trim()  || '';
  const design   = document.getElementById('cfg-design')?.value  || _currentShow.design_status || 'not_started';
  const feeVal   = document.getElementById('cfg-fee')?.value     || '';
  const deliver  = document.getElementById('cfg-deliver')?.value || null;
  const ready    = document.getElementById('cfg-ready')?.value   || null;

  const parts = [];
  document.querySelectorAll('.cfg-part-cb:checked').forEach(cb => parts.push(cb.value));

  const durSecs  = durStr  ? parseMMSS(durStr)  : (_currentShow.duration_seconds || 0);
  const writSecs = writStr ? parseMMSS(writStr) : (_currentShow.written_seconds  || 0);
  const feeCents = feeVal  ? Math.round(parseFloat(feeVal) * 100) : (_currentShow.fee_cents || 0);

  // Build full payload — spread existing show so no other fields get nulled out
  const payload = {
    title:              _currentShow.title,
    season_year:        _currentShow.season_year,
    status:             _currentShow.status,
    show_type:          _currentShow.show_type,
    contract_sent:      _currentShow.contract_sent,
    contract_signed:    _currentShow.contract_signed,
    contract_completed: _currentShow.contract_completed,
    invoiced:           _currentShow.invoiced,
    paid:               _currentShow.paid,
    invoice_number:     _currentShow.invoice_number,
    invoice_date:       _currentShow.invoice_date,
    payment_method:     _currentShow.payment_method,
    payment_date:       _currentShow.payment_date,
    payment_ref:        _currentShow.payment_ref,
    project_description: _currentShow.project_description,
    extra_line_items:   _currentShow.extra_line_items,
    // Editable fields
    percent_complete:   pct,
    duration_seconds:   durSecs,
    written_seconds:    writSecs,
    design_status:      design,
    fee_cents:          feeCents,
    deliver_by_date:    deliver || _currentShow.deliver_by_date || null,
    ready_to_write_date: ready || _currentShow.ready_to_write_date || null,
    ensemble_parts:     parts,
    due_date:           _currentShow.due_date,
  };

  const ind = document.getElementById('cfg-save-indicator');
  if (ind) { ind.textContent = '⟳ saving…'; ind.className = 'cfg-save-indicator saving'; }

  try {
    const updated = await apiFetch(`/api/v1/shows/${showId}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
    _currentShow = { ..._currentShow, ...updated };
    window._currentShow = _currentShow;

    if (ind) { ind.textContent = '✓ saved'; ind.className = 'cfg-save-indicator saved'; }
    setTimeout(() => {
      if (ind && ind.textContent === '✓ saved') { ind.textContent = ''; ind.className = 'cfg-save-indicator'; }
    }, 2500);
  } catch (err) {
    if (ind) { ind.textContent = '✗ save failed'; ind.className = 'cfg-save-indicator error'; }
    console.error('saveShowConfig failed:', err.message);
  }
};

// ── Portal Token Management ────────────────────────────────────────────────

window.openPortalPreview = async function(showId) {
  const ctx = window._directorPinCtx?.[showId] || {};
  if (!ctx.staffUrl && !ctx.directorUrl) { alert('No portal link generated yet.'); return; }

  // Get an admin-preview session for the Staff Portal (bypasses PIN login)
  let staffPreviewUrl = ctx.staffUrl;
  if (ctx.staffUrl) {
    try {
      const preview = await apiFetch(`/api/v1/portal/show/${showId}/admin-preview`, { method: 'POST' });
      const base = ctx.staffUrl.split('?')[0];
      staffPreviewUrl = `${base}?staff=${preview.portal_token}&staff_preview=${preview.access_token}`;
    } catch (e) {
      staffPreviewUrl = ctx.staffUrl; // fall back to login screen
    }
  }

  const tabs = [
    { key: 'director', label: 'Director Portal', url: ctx.directorUrl   },
    { key: 'staff',    label: 'Staff Portal',    url: staffPreviewUrl   },
    { key: 'student',  label: 'Student Portal',  url: ctx.studentUrl    },
  ].filter(t => t.url);

  document.getElementById('portal-preview-modal')?.remove();

  const modal = document.createElement('div');
  modal.id = 'portal-preview-modal';
  modal.style.cssText = `
    position:fixed;inset:0;z-index:9999;
    background:rgba(9,15,26,0.92);
    display:flex;flex-direction:column;
  `;

  const firstUrl = tabs[0].url;

  modal.innerHTML = `
    <div style="display:flex;align-items:center;gap:0.75rem;padding:0.75rem 1.25rem;
                background:#090f1a;border-bottom:1px solid rgba(0,180,216,0.15);flex-shrink:0;">
      <span style="font-family:var(--font-arcade);font-size:1rem;color:#e8f4fd;margin-right:0.5rem;">Portal Preview</span>
      <div style="display:flex;gap:0.4rem;" id="ppp-tabs">
        ${tabs.map((t, i) => `
          <button class="ppp-tab-btn${i === 0 ? ' active' : ''}"
                  style="font-size:0.78rem;padding:0.3rem 0.85rem;border-radius:4px;cursor:pointer;
                         border:1px solid ${i === 0 ? 'rgba(0,180,216,0.5)' : 'rgba(255,255,255,0.1)'};
                         background:${i === 0 ? 'rgba(0,180,216,0.12)' : 'transparent'};
                         color:${i === 0 ? '#00b4d8' : '#5a7a9a'};"
                  onclick="window._portalPreviewSwitch(this, '${t.url}')">
            ${t.label}
          </button>`).join('')}
      </div>
      <button onclick="document.getElementById('portal-preview-modal').remove()"
              style="margin-left:auto;background:none;border:none;color:#5a7a9a;font-size:1.4rem;cursor:pointer;line-height:1;">×</button>
    </div>
    <iframe id="portal-preview-frame" src="${firstUrl}"
            style="flex:1;border:none;width:100%;background:#0d1520;">
    </iframe>
  `;

  document.body.appendChild(modal);
};

window._portalPreviewSwitch = function(btn, url) {
  document.getElementById('portal-preview-frame').src = url;
  document.querySelectorAll('.ppp-tab-btn').forEach(b => {
    const active = b === btn;
    b.style.borderColor  = active ? 'rgba(0,180,216,0.5)' : 'rgba(255,255,255,0.1)';
    b.style.background   = active ? 'rgba(0,180,216,0.12)' : 'transparent';
    b.style.color        = active ? '#00b4d8' : '#5a7a9a';
  });
};

window.generatePortalToken = async function(showId, isReset) {
  if (isReset && !confirm('Reset the portal link?\n\nThis generates a new staff and student portal URL. ALL staff members with old invite links will lose access — you\'ll need to resend their invites.\n\n(The director\'s personal link is preserved.)')) return;
  try {
    const result = await apiFetch(`/api/v1/portal/show/${showId}/token`, { method: 'POST' });
    state.currentTab = 'files-team';
    await renderClientCard(state.currentClientId);
  } catch (err) {
    alert('Failed: ' + err.message);
  }
};

window.resendPortalInvites = async function(showId) {
  try {
    const r = await apiFetch(`/api/v1/portal/show/${showId}/notify`, { method: 'POST' });
    let msg = `Invites sent: ${r.sent}`;
    if (r.failed)  msg += `, failed: ${r.failed}`;
    if (r.skipped) msg += `, skipped (no email): ${r.skipped}`;
    alert(msg);
    state.currentTab = 'files-team';
    await renderClientCard(state.currentClientId);
  } catch (err) {
    alert('Failed: ' + err.message);
  }
};

// Regenerate the portal token AND immediately resend invites to all staff.
// Use when links have gone stale or a full reset is needed.
window.resetAndResendAll = async function(showId) {
  if (!confirm(
    'Reset all staff portal links and resend invites?\n\n' +
    'A new portal URL will be generated and emailed to every staff member. ' +
    'Anyone who clicks an old link after this will see an expired message.\n\n' +
    "The director's personal link is unaffected."
  )) return;

  try {
    await apiFetch(`/api/v1/portal/show/${showId}/token`, { method: 'POST' });
    const r = await apiFetch(`/api/v1/portal/show/${showId}/notify`, { method: 'POST' });
    let msg = `Links reset. Invites sent: ${r.sent}`;
    if (r.failed)  msg += `, failed: ${r.failed}`;
    if (r.skipped) msg += `, skipped (no email): ${r.skipped}`;
    alert(msg);
    state.currentTab = 'files-team';
    await renderClientCard(state.currentClientId);
  } catch (err) {
    alert('Failed: ' + err.message);
  }
};

window.invitePortalPin = async function(showId, pinId) {
  try {
    await apiFetch(`/api/v1/portal/show/${showId}/pins/${pinId}/invite`, { method: 'POST' });
    alert('Invite sent.');
    state.currentTab = 'files-team';
    await renderClientCard(state.currentClientId);
  } catch (err) {
    alert('Failed: ' + err.message);
  }
};

window.resetPortalPin = async function(showId, pinId) {
  try {
    const r = await apiFetch(`/api/v1/portal/show/${showId}/pins/${pinId}`, { method: 'PATCH' });
    alert(`New PIN: ${r.pin}`);
    state.currentTab = 'files-team';
    await renderClientCard(state.currentClientId);
  } catch (err) {
    alert('Failed: ' + err.message);
  }
};

window.revokePortalPin = async function(showId, pinId) {
  if (!confirm('Revoke this person\'s portal access?')) return;
  try {
    await apiFetch(`/api/v1/portal/show/${showId}/pins/${pinId}`, { method: 'DELETE' });
    state.currentTab = 'files-team';
    await renderClientCard(state.currentClientId);
  } catch (err) {
    alert('Failed: ' + err.message);
  }
};

window.sendDirectorPortalLink = async function(showId, clientId) {
  try {
    const r = await apiFetch('/api/portal/provision', {
      method: 'POST',
      body: JSON.stringify({ client_id: clientId, show_id: showId })
    });
    alert(`Director portal link sent to ${r.email}`);
  } catch (err) {
    alert('Failed to send director link: ' + err.message);
  }
};

window.assignDirectorPin = async function(showId) {
  const ctx = window._directorPinCtx?.[showId];
  if (!ctx?.email) { alert('No email on file for this director.'); return; }
  try {
    const r = await apiFetch(`/api/v1/portal/show/${showId}/pins`, {
      method: 'POST',
      body: JSON.stringify({ name: ctx.name, email: ctx.email })
    });
    alert(`PIN assigned: ${r.pin}\n\nClick "Send Portal Invite" to email it to them.`);
    state.currentTab = 'files-team';
    await renderClientCard(state.currentClientId);
  } catch (err) {
    alert('Failed: ' + err.message);
  }
};

window.grantPortalAccess = async function(showId, collabId) {
  const ctx = window._collabPinCtx?.[collabId];
  if (!ctx?.email) { alert('No email on file for this person.'); return; }
  try {
    const r = await apiFetch(`/api/v1/portal/show/${showId}/pins`, {
      method: 'POST',
      body: JSON.stringify({ name: ctx.name, email: ctx.email })
    });
    alert(`PIN assigned: ${r.pin}\n\nClick the ✉ button to send their invite.`);
    state.currentTab = 'files-team';
    await renderClientCard(state.currentClientId);
  } catch (err) {
    alert('Failed: ' + err.message);
  }
};

// ── Director / Contact Management ─────────────────────────────────────────

window.openDirectorManage = function(clientId) {
  // Remove any existing modal
  document.getElementById('director-modal')?.remove();

  const modal = document.createElement('div');
  modal.id = 'director-modal';
  modal.className = 'modal-overlay';
  modal.innerHTML = `
    <div class="modal-box" style="max-width:480px;">
      <div class="modal-header">
        <span class="modal-title" style="font-family:var(--font-arcade)">Add Director / Contact</span>
        <button class="modal-close" onclick="document.getElementById('director-modal').remove()">✕</button>
      </div>
      <div class="modal-body" style="display:grid;gap:0.65rem;">
        <div>
          <label class="cfg-label">Name</label>
          <input type="text" id="dm-name" class="cfg-text" style="width:100%" placeholder="First Last">
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:0.5rem;">
          <div>
            <label class="cfg-label">Email</label>
            <input type="email" id="dm-email" class="cfg-text" style="width:100%">
          </div>
          <div>
            <label class="cfg-label">Phone</label>
            <input type="tel" id="dm-phone" class="cfg-text" style="width:100%">
          </div>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:0.5rem;">
          <div>
            <label class="cfg-label">Role</label>
            <input type="text" id="dm-role" class="cfg-text" style="width:100%" value="Director">
          </div>
          <div>
            <label class="cfg-label">Start Year</label>
            <input type="number" id="dm-start" class="cfg-num" style="width:100%" placeholder="${new Date().getFullYear()}">
          </div>
          <div>
            <label class="cfg-label">End Year</label>
            <input type="number" id="dm-end" class="cfg-num" style="width:100%" placeholder="present">
          </div>
        </div>
        <div style="display:flex;align-items:center;gap:0.5rem;">
          <input type="checkbox" id="dm-primary" style="accent-color:#00b4d8">
          <label for="dm-primary" style="font-size:0.82rem;color:#7a9ab8;cursor:pointer;">Set as current primary director</label>
        </div>
        <div>
          <label class="cfg-label">Notes</label>
          <input type="text" id="dm-notes" class="cfg-text" style="width:100%" placeholder="Optional">
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn-fin-action" onclick="window.saveDirectorContact(${clientId})">Add Contact</button>
        <button class="btn-fin-secondary" onclick="document.getElementById('director-modal').remove()">Cancel</button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
};

window.saveDirectorContact = async function(clientId) {
  const name = document.getElementById('dm-name')?.value.trim();
  if (!name) { alert('Name is required.'); return; }

  const payload = {
    client_id:  clientId,
    name,
    email:      document.getElementById('dm-email')?.value.trim() || null,
    phone:      document.getElementById('dm-phone')?.value.trim() || null,
    role:       document.getElementById('dm-role')?.value.trim()  || 'Director',
    start_year: document.getElementById('dm-start')?.value ? parseInt(document.getElementById('dm-start').value) : null,
    end_year:   document.getElementById('dm-end')?.value   ? parseInt(document.getElementById('dm-end').value)   : null,
    is_primary: document.getElementById('dm-primary')?.checked || false,
    notes:      document.getElementById('dm-notes')?.value.trim() || null,
  };

  try {
    await apiFetch('/api/contacts/directors', { method: 'POST', body: JSON.stringify(payload) });
    document.getElementById('director-modal')?.remove();
    await renderClientCard(state.currentClientId);
  } catch (err) {
    alert('Failed to save: ' + err.message);
  }
};

window.openDirectorEdit = async function(contactId, clientId) {
  try {
    // Fetch all contacts for this client to find the one we want
    const contacts = await apiFetch(`/api/contacts/directors/${clientId}`);
    const c = contacts.find(x => x.id === contactId);
    if (!c) return;

    document.getElementById('director-modal')?.remove();
    const modal = document.createElement('div');
    modal.id = 'director-modal';
    modal.className = 'modal-overlay';
    modal.innerHTML = `
      <div class="modal-box" style="max-width:480px;">
        <div class="modal-header">
          <span class="modal-title" style="font-family:var(--font-arcade)">Edit Director</span>
          <button class="modal-close" onclick="document.getElementById('director-modal').remove()">✕</button>
        </div>
        <div class="modal-body" style="display:grid;gap:0.65rem;">
          <div>
            <label class="cfg-label">Name</label>
            <input type="text" id="dm-name" class="cfg-text" style="width:100%" value="${c.name || ''}">
          </div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:0.5rem;">
            <div>
              <label class="cfg-label">Email</label>
              <input type="email" id="dm-email" class="cfg-text" style="width:100%" value="${c.email || ''}">
            </div>
            <div>
              <label class="cfg-label">Phone</label>
              <input type="tel" id="dm-phone" class="cfg-text" style="width:100%" value="${c.phone || ''}">
            </div>
          </div>
          <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:0.5rem;">
            <div>
              <label class="cfg-label">Role</label>
              <input type="text" id="dm-role" class="cfg-text" style="width:100%" value="${c.role || 'Director'}">
            </div>
            <div>
              <label class="cfg-label">Start Year</label>
              <input type="number" id="dm-start" class="cfg-num" style="width:100%" value="${c.start_year || ''}">
            </div>
            <div>
              <label class="cfg-label">End Year</label>
              <input type="number" id="dm-end" class="cfg-num" style="width:100%" value="${c.end_year || ''}">
            </div>
          </div>
          <div style="display:flex;align-items:center;gap:0.5rem;">
            <input type="checkbox" id="dm-primary" style="accent-color:#00b4d8" ${c.is_primary ? 'checked' : ''}>
            <label for="dm-primary" style="font-size:0.82rem;color:#7a9ab8;cursor:pointer;">Set as current primary director</label>
          </div>
          <div>
            <label class="cfg-label">Notes</label>
            <input type="text" id="dm-notes" class="cfg-text" style="width:100%" value="${c.notes || ''}">
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-fin-action" onclick="window.updateDirectorContact(${contactId}, ${clientId})">Save</button>
          <button class="btn-fin-secondary" style="color:#ff6b6b;border-color:rgba(255,107,107,0.3);" onclick="window.deleteDirectorContact(${contactId}, ${clientId})">Delete</button>
          <button class="btn-fin-secondary" onclick="document.getElementById('director-modal').remove()">Cancel</button>
        </div>
      </div>
    `;
    document.body.appendChild(modal);
  } catch (err) {
    alert('Failed to load contact: ' + err.message);
  }
};

// ── Canonical current-director edit (calls cascade PATCH) ─────────────────
// Reads prefill values from _currentShow / _currentClient since they're already
// loaded in memory. Always calls PATCH /api/shows/:id/director which cascades to
// all current+future shows, clients table, and client_contacts.
window.openCurrentDirectorEdit = function(showId, clientId) {
  const s = window._currentShow;
  const c = window._currentClient;
  const prefillName  = s?.director_name  || c?.name  || '';
  const prefillEmail = s?.director_email || c?.email || '';
  const prefillPhone = s?.director_phone || c?.phone || '';

  document.getElementById('director-modal')?.remove();
  const modal = document.createElement('div');
  modal.id = 'director-modal';
  modal.className = 'modal-overlay';
  modal.innerHTML = `
    <div class="modal-box" style="max-width:420px;">
      <div class="modal-header">
        <span class="modal-title" style="font-family:var(--font-arcade)">Edit Current Director</span>
        <button class="modal-close" onclick="document.getElementById('director-modal').remove()">✕</button>
      </div>
      <div class="modal-body" style="display:grid;gap:0.65rem;">
        <p style="font-size:0.78rem;color:#7a9ab8;margin:0;">
          Changes cascade to all current &amp; future shows, the client record, and contact history.
        </p>
        <div>
          <label class="cfg-label">Name</label>
          <input type="text" id="cdm-name" class="cfg-text" style="width:100%" value="${(prefillName||'').replace(/"/g,'&quot;')}">
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:0.5rem;">
          <div>
            <label class="cfg-label">Email</label>
            <input type="email" id="cdm-email" class="cfg-text" style="width:100%" value="${(prefillEmail||'').replace(/"/g,'&quot;')}">
          </div>
          <div>
            <label class="cfg-label">Phone</label>
            <input type="tel" id="cdm-phone" class="cfg-text" style="width:100%" value="${(prefillPhone||'').replace(/"/g,'&quot;')}">
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn-fin-action" onclick="window.saveCurrentDirectorEdit(${showId}, ${clientId})">Save &amp; Cascade</button>
        <button class="btn-fin-secondary" onclick="document.getElementById('director-modal').remove()">Cancel</button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
};

window.saveCurrentDirectorEdit = async function(showId, clientId) {
  const name  = document.getElementById('cdm-name')?.value.trim()  || '';
  const email = document.getElementById('cdm-email')?.value.trim() || '';
  const phone = document.getElementById('cdm-phone')?.value.trim() || '';
  if (!name && !email) { alert('Name or email required.'); return; }
  const btn = document.querySelector('#director-modal .btn-fin-action');
  if (btn) { btn.textContent = 'Saving…'; btn.disabled = true; }
  try {
    const payload = {};
    if (name)  payload.director_name  = name;
    if (email) payload.director_email = email;
    if (phone) payload.director_phone = phone;
    await apiFetch(`/api/shows/${showId}/director`, { method: 'PATCH', body: JSON.stringify(payload) });
    document.getElementById('director-modal')?.remove();
    await renderClientCard(clientId);
  } catch (err) {
    alert('Failed to save: ' + err.message);
    if (btn) { btn.textContent = 'Save & Cascade'; btn.disabled = false; }
  }
};

window.updateDirectorContact = async function(contactId, clientId) {
  const payload = {
    name:       document.getElementById('dm-name')?.value.trim(),
    email:      document.getElementById('dm-email')?.value.trim() || null,
    phone:      document.getElementById('dm-phone')?.value.trim() || null,
    role:       document.getElementById('dm-role')?.value.trim()  || 'Director',
    start_year: document.getElementById('dm-start')?.value ? parseInt(document.getElementById('dm-start').value) : null,
    end_year:   document.getElementById('dm-end')?.value   ? parseInt(document.getElementById('dm-end').value)   : null,
    is_primary: document.getElementById('dm-primary')?.checked || false,
    notes:      document.getElementById('dm-notes')?.value.trim() || null,
  };
  try {
    await apiFetch(`/api/contacts/directors/${contactId}`, { method: 'PUT', body: JSON.stringify(payload) });
    document.getElementById('director-modal')?.remove();
    await renderClientCard(state.currentClientId);
  } catch (err) {
    alert('Failed to update: ' + err.message);
  }
};

window.deleteDirectorContact = async function(contactId, clientId) {
  if (!confirm('Remove this director from the history?')) return;
  try {
    await apiFetch(`/api/contacts/directors/${contactId}`, { method: 'DELETE' });
    document.getElementById('director-modal')?.remove();
    await renderClientCard(state.currentClientId);
  } catch (err) {
    alert('Failed to delete: ' + err.message);
  }
};

// ── Milestone Status ───────────────────────────────────────────────────────

window.setMilestoneStatus = async function(milestoneId, currentStatus, box) {
  let newStatus;

  if (box === 'box1') {
    if (currentStatus === 'not_started') newStatus = 'underway';
    else if (currentStatus === 'underway') newStatus = 'not_started';
    else return; // complete — box1 is locked, no action
  } else { // box2
    if (currentStatus === 'underway') newStatus = 'complete';
    else if (currentStatus === 'complete') {
      if (!confirm('Reset this section back to "underway"?')) return;
      newStatus = 'underway';
    } else return; // not_started — box2 does nothing yet
  }

  try {
    await apiFetch(`/api/v1/shows/milestones/${milestoneId}`, {
      method: 'PUT',
      body: JSON.stringify({ status: newStatus })
    });
    // Re-render just the milestone section without full card reload
    if (_currentShow) {
      const newMilestones = await apiFetch(`/api/v1/shows/${_currentShow.id}/milestones`).catch(() => []);
      const container = document.querySelector('.milestone-timeline');
      if (container) {
        container.outerHTML = renderMilestoneTimeline(newMilestones);
      }
    }
  } catch (err) {
    console.error('setMilestoneStatus failed:', err.message);
  }
};

// Legacy alias — keep for any old references
window.generateMilestones = async function(showId) {
  const show = window._currentShow;
  if (!show || !show.deliver_by_date) { alert('Set a Deliver By date first.'); return; }
  try {
    const milestones = await apiFetch(`/api/v1/shows/${showId}/milestones/generate`, {
      method: 'POST',
      body: JSON.stringify({
        deliver_by_date:    show.deliver_by_date.slice(0, 10),
        ready_to_write_date: show.ready_to_write_date ? show.ready_to_write_date.slice(0, 10) : null,
        show_type:          show.show_type || 'marching',
        ensemble_parts:     Array.isArray(show.ensemble_parts) ? show.ensemble_parts
                              : (typeof show.ensemble_parts === 'string' ? JSON.parse(show.ensemble_parts || '[]') : [])
      })
    });
    const container = document.querySelector('.milestone-timeline');
    if (container) container.outerHTML = renderMilestoneTimeline(milestones, show);
  } catch (err) {
    alert('Failed to generate milestones: ' + err.message);
  }
};

window.toggleMilestone = function(milestoneId, currentlyDone) {
  window.setMilestoneStatus(milestoneId, currentlyDone ? 'complete' : 'not_started', 'box2');
};

// ── Collaborator Management ────────────────────────────────────────────────

window.removeCollaborator = async function(collabId, clientId) {
  if (!confirm('Remove this person from the team?')) return;
  try {
    await apiFetch(`/api/v1/collaborators/${collabId}`, { method: 'DELETE' });
    state.currentTab = 'files-team';
    await renderClientCard(state.currentClientId);
  } catch (err) {
    alert('Failed: ' + err.message);
  }
};

window.openAddCollaboratorModal = function(clientId, showId) {
  document.getElementById('ft-add-collab-modal')?.remove();
  const yr = new Date().getFullYear();
  const modal = document.createElement('div');
  modal.id = 'ft-add-collab-modal';
  modal.className = 'modal-overlay';
  modal.innerHTML = `
    <div class="modal-box" style="max-width:420px;">
      <div class="modal-header">
        <span class="modal-title" style="font-family:var(--font-arcade)">Add Team Member</span>
        <button class="modal-close" onclick="document.getElementById('ft-add-collab-modal').remove()">✕</button>
      </div>
      <div class="modal-body" style="display:grid;gap:0.6rem;">
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:0.5rem;">
          <div><label class="cfg-label">Name *</label>
            <input type="text" id="ft-ac-name" class="cfg-text" style="width:100%"></div>
          <div><label class="cfg-label">Role</label>
            <input type="text" id="ft-ac-role" class="cfg-text" style="width:100%" placeholder="Arranger, Designer…"></div>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:0.5rem;">
          <div><label class="cfg-label">Email</label>
            <input type="email" id="ft-ac-email" class="cfg-text" style="width:100%"></div>
          <div><label class="cfg-label">Phone</label>
            <input type="tel" id="ft-ac-phone" class="cfg-text" style="width:100%"></div>
        </div>
        <div style="display:flex;align-items:center;gap:0.5rem;margin-top:0.2rem;">
          <input type="checkbox" id="ft-ac-portal" style="accent-color:#00b4d8">
          <label for="ft-ac-portal" style="font-size:0.82rem;color:#7a9ab8;cursor:pointer;">Also grant portal PIN (requires email)</label>
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn-fin-action" onclick="window.submitAddCollaborator(${clientId}, ${showId})">Add</button>
        <button class="btn-fin-small" onclick="document.getElementById('ft-add-collab-modal').remove()">Cancel</button>
      </div>
    </div>`;
  document.body.appendChild(modal);
  document.getElementById('ft-ac-name')?.focus();
};

window.submitAddCollaborator = async function(clientId, showId) {
  const name  = document.getElementById('ft-ac-name')?.value.trim();
  const role  = document.getElementById('ft-ac-role')?.value.trim();
  const email = document.getElementById('ft-ac-email')?.value.trim();
  const phone = document.getElementById('ft-ac-phone')?.value.trim();
  const grantPortal = document.getElementById('ft-ac-portal')?.checked;
  if (!name) { alert('Name is required.'); return; }
  try {
    await apiFetch('/api/v1/collaborators', {
      method: 'POST',
      body: JSON.stringify({ client_id: clientId, name, role: role||null, email: email||null, phone: phone||null })
    });
    if (grantPortal && email) {
      await apiFetch(`/api/v1/portal/show/${showId}/pins`, {
        method: 'POST',
        body: JSON.stringify({ name, email })
      }).catch(() => {});
    }
    document.getElementById('ft-add-collab-modal')?.remove();
    state.currentTab = 'files-team';
    await renderClientCard(state.currentClientId);
  } catch (err) {
    alert('Failed: ' + err.message);
  }
};

window.switchShow = async function(showId) {
  state.currentShowId = showId;
  await renderClientCard(state.currentClientId);
};

window.markContractSigned = async function(showId) {
  if (!confirm('Mark contract as signed?')) return;
  try {
    await apiFetch(`/api/v1/shows/${showId}/field`, {
      method: 'PATCH',
      body: JSON.stringify({ field: 'contract_signed', value: true })
    });
    // Fire automation trigger
    fetch('/api/automations/trigger', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event: 'contract_signed', client_id: state.currentClientId, show_id: showId })
    }).catch(err => console.warn('Automation trigger failed:', err));
    await renderClientCard(state.currentClientId);
  } catch (err) {
    alert('Failed to mark signed: ' + err.message);
  }
};

window.markInvoicePaid = async function(showId) {
  if (!confirm('Mark invoice as paid?')) return;
  try {
    await apiFetch(`/api/v1/shows/${showId}/field`, {
      method: 'PATCH',
      body: JSON.stringify({ field: 'invoiced', value: true })
    });
    fetch('/api/automations/trigger', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event: 'invoice_paid', client_id: state.currentClientId, show_id: showId })
    }).catch(err => console.warn('Automation trigger failed:', err));
    await renderClientCard(state.currentClientId);
  } catch (err) {
    alert('Failed to mark paid: ' + err.message);
  }
};

// ── Band Website ───────────────────────────────────────────────────────────

window.findBandWebsite = async function(clientId) {
  const btn = document.getElementById(`btn-find-website-${clientId}`);
  if (btn) { btn.textContent = '⌕ Searching…'; btn.classList.add('searching'); btn.disabled = true; }
  try {
    const result = await apiFetch(`/api/v1/clients/${clientId}/find-band-website`, { method: 'POST' });
    if (result.band_website) {
      await renderClientCard(state.currentClientId);
    } else {
      if (btn) { btn.textContent = 'Not found'; btn.disabled = false; btn.classList.remove('searching'); }
      setTimeout(() => { if (btn) { btn.textContent = '⌕ Find Website'; btn.classList.remove('searching'); } }, 2500);
    }
  } catch (err) {
    if (btn) { btn.textContent = 'Error'; btn.disabled = false; btn.classList.remove('searching'); }
    setTimeout(() => { if (btn) { btn.textContent = '⌕ Find Website'; } }, 2000);
  }
};

window.editBandWebsite = function(clientId, currentUrl) {
  const url = prompt('Enter band website URL:', currentUrl || 'https://');
  if (!url || url === currentUrl) return;
  apiFetch(`/api/v1/agent/write/clients/${clientId}`, {
    method: 'PATCH',
    body: JSON.stringify({ band_website: url })
  }).then(() => renderClientCard(state.currentClientId))
    .catch(err => alert('Failed to update: ' + err.message));
};

// ── Itemize Invoice ────────────────────────────────────────────────────────

window.openItemizeInvoiceModal = function(showId, clientId, existingFeeCents) {
  document.getElementById('itemize-modal')?.remove();

  const modal = document.createElement('div');
  modal.id = 'itemize-modal';
  modal.className = 'modal-overlay';
  modal.innerHTML = `
    <div class="modal-box" style="max-width:520px;">
      <div class="modal-header">
        <span class="modal-title" style="font-family:var(--font-arcade)">Itemize Invoice</span>
        <button class="modal-close" onclick="document.getElementById('itemize-modal').remove()">✕</button>
      </div>
      <div class="modal-body">
        <p style="font-size:0.82rem;color:#7a9ab8;margin-bottom:1rem;">Break the invoice into line items. Each line will be included in the Stripe invoice description.</p>
        <div id="itemize-lines">
          <div class="itemize-line">
            <input type="text" placeholder="Description (e.g. Full Score & Parts)" value="">
            <input type="number" placeholder="$" min="0" step="0.01">
            <button class="btn-remove-line" onclick="this.closest('.itemize-line').remove(); window._updateItemizeTotal()">✕</button>
          </div>
        </div>
        <button class="btn-fin-small" style="margin-top:0.5rem;" onclick="window._addItemizeLine()">+ Add Line</button>
        <div style="margin-top:1rem;padding-top:0.75rem;border-top:1px solid rgba(255,255,255,0.08);display:flex;justify-content:space-between;align-items:center;">
          <span style="font-size:0.82rem;color:#5a7a9a;">Total:</span>
          <span id="itemize-total" style="font-size:1.1rem;color:#e8f4fd;font-weight:600;">$0.00</span>
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn-fin-action" onclick="window.submitItemizeInvoice(${showId}, ${clientId})">Send Itemized Invoice via Stripe</button>
        <button class="btn-fin-secondary" onclick="document.getElementById('itemize-modal').remove()">Cancel</button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);

  // Pre-fill with existing fee if available
  if (existingFeeCents > 0) {
    const firstAmt = document.querySelector('#itemize-lines .itemize-line input[type="number"]');
    const firstDesc = document.querySelector('#itemize-lines .itemize-line input[type="text"]');
    if (firstAmt) firstAmt.value = (existingFeeCents / 100).toFixed(2);
    if (firstDesc) firstDesc.value = 'Show Services';
    window._updateItemizeTotal();
  }

  document.querySelectorAll('#itemize-lines input').forEach(i => i.addEventListener('input', window._updateItemizeTotal));
};

window._addItemizeLine = function() {
  const container = document.getElementById('itemize-lines');
  if (!container) return;
  const div = document.createElement('div');
  div.className = 'itemize-line';
  div.innerHTML = `
    <input type="text" placeholder="Description">
    <input type="number" placeholder="$" min="0" step="0.01">
    <button class="btn-remove-line" onclick="this.closest('.itemize-line').remove(); window._updateItemizeTotal()">✕</button>
  `;
  div.querySelectorAll('input').forEach(i => i.addEventListener('input', window._updateItemizeTotal));
  container.appendChild(div);
};

window._updateItemizeTotal = function() {
  const lines = document.querySelectorAll('#itemize-lines .itemize-line');
  let total = 0;
  lines.forEach(line => {
    const amt = parseFloat(line.querySelector('input[type="number"]')?.value || 0);
    if (!isNaN(amt)) total += amt;
  });
  const el = document.getElementById('itemize-total');
  if (el) el.textContent = '$' + total.toLocaleString('en-US', { minimumFractionDigits: 2 });
};

window.submitItemizeInvoice = async function(showId, clientId) {
  const lines = [];
  document.querySelectorAll('#itemize-lines .itemize-line').forEach(line => {
    const desc = line.querySelector('input[type="text"]')?.value.trim();
    const amt  = parseFloat(line.querySelector('input[type="number"]')?.value || 0);
    if (desc && amt > 0) lines.push({ description: desc, amount: amt });
  });

  if (!lines.length) { alert('Add at least one line item.'); return; }

  const totalCents = Math.round(lines.reduce((s, l) => s + l.amount, 0) * 100);
  const description = lines.map(l => `${l.description}: $${l.amount.toFixed(2)}`).join('\n');

  document.getElementById('itemize-modal')?.remove();
  window.openSendInvoiceModal(showId, clientId, totalCents, description);
};

// ── Collaborator Portal Access ─────────────────────────────────────────────

// ── Portal Conversation ────────────────────────────────────────────────────

window.sendPortalMessage = async function(showId) {
  const ta = document.getElementById(`pct-new-body-${showId}`);
  const body = ta?.value?.trim();
  if (!body) { alert('Message is required.'); return; }
  try {
    await apiFetch(`/api/v1/portal/show/${showId}/message`, {
      method: 'POST',
      body: JSON.stringify({ body })
    });
    state.currentTab = 'files-team';
    await renderClientCard(state.currentClientId);
  } catch (err) {
    alert('Failed: ' + err.message);
  }
};

window.sendPortalReply = async function(showId, parentId, btn) {
  const box = btn?.closest('.pct-reply-box');
  const ta  = box?.querySelector('textarea');
  const body = ta?.value?.trim();
  if (!body) { alert('Reply is required.'); return; }
  try {
    await apiFetch(`/api/v1/portal/show/${showId}/message/${parentId}/reply`, {
      method: 'POST',
      body: JSON.stringify({ body })
    });
    state.currentTab = 'files-team';
    await renderClientCard(state.currentClientId);
  } catch (err) {
    alert('Failed: ' + err.message);
  }
};

window.openReplyBox = function(boxId) {
  const box = document.getElementById(boxId);
  if (!box) return;
  box.style.display = box.style.display === 'none' ? 'block' : 'none';
  if (box.style.display === 'block') box.querySelector('textarea')?.focus();
};

window.toggleThread = function(msgId) {
  const replies = document.getElementById(`pct-replies-${msgId}`);
  const box     = document.getElementById(`reply1-${msgId}`);
  if (!replies) return;
  const collapsed = replies.style.display === 'none';
  replies.style.display = collapsed ? '' : 'none';
  if (box) box.style.display = 'none';
};

window.sendContractReminder = async function(showId, clientId) {
  if (!confirm('Send contract reminder email to director via David?')) return;
  try {
    await apiFetch('/api/automations/trigger', {
      method: 'POST',
      body: JSON.stringify({ event: 'contract_unsigned_7d', client_id: clientId, show_id: showId })
    });
    alert('Reminder queued — David will send it shortly.');
  } catch (err) {
    alert('Failed to queue reminder: ' + err.message);
  }
};

window.sendInvoiceReminder = async function(showId, clientId) {
  if (!confirm('Send invoice reminder email to director via David?')) return;
  try {
    await apiFetch('/api/automations/trigger', {
      method: 'POST',
      body: JSON.stringify({ event: 'invoice_unpaid_14d', client_id: clientId, show_id: showId })
    });
    alert('Reminder queued — David will send it shortly.');
  } catch (err) {
    alert('Failed to queue reminder: ' + err.message);
  }
};

// ── Manual Actions — shared dispatcher ───────────────────────────────────────
// All manual action buttons use this. Logs to manual_action_log.
// statusElId: optional DOM id of a <span> to show inline progress feedback.
window.runManualAction = async function(actionKey, ctx = {}, statusElId = null, label = null) {
  const displayLabel = label || actionKey.replace(/_/g, ' ');
  if (!confirm(`Run: ${displayLabel}?`)) return;
  try {
    const result = await runManualAction(actionKey, ctx, statusElId);
    if (!result.success) {
      alert(`Action failed: ${result.error || 'unknown error'}`);
    }
    return result;
  } catch (err) {
    alert(`Failed: ${err.message}`);
  }
};

// ── Survey Chip Handler ────────────────────────────────────────────────────

window.handleSurveyChip = async function(showId, clientId) {
  let surveyStatus;
  try {
    surveyStatus = await apiFetch(`/api/v1/survey/show/${showId}`);
  } catch (err) {
    surveyStatus = null;
  }

  // returned_at → show results
  if (surveyStatus && surveyStatus.returned_at && surveyStatus.id) {
    await window.openSurveyResultsModal(surveyStatus.id, surveyStatus);
    return;
  }

  // Resolve director email/name from client state
  const client = state.clients.find(c => c.id === clientId) || {};
  const shows  = client.shows || window.__wmmLegacyState?.allShows || [];
  const show   = shows.find(s => s.id === showId) || {};
  const defaultEmail = show.director_email || client.email || '';
  const defaultName  = show.director_name  || client.name  || '';

  // Build modal
  const isResend = !!(surveyStatus && surveyStatus.sent_at);
  const sentDate = isResend ? new Date(surveyStatus.sent_at).toLocaleDateString() : null;

  const modalHtml = `
    <div id="survey-send-modal" style="
      position:fixed;inset:0;z-index:9999;background:rgba(0,0,0,0.7);
      display:flex;align-items:center;justify-content:center;">
      <div style="
        background:#111d2e;border:1px solid rgba(0,180,216,0.25);border-radius:12px;
        padding:1.75rem 2rem;width:420px;max-width:calc(100vw - 2rem);
        box-shadow:0 20px 60px rgba(0,0,0,0.6);">
        <div style="font-size:0.75rem;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:#00b4d8;margin-bottom:0.75rem;">
          ${isResend ? 'Resend Ensemble Survey' : 'Send Ensemble Survey'}
        </div>
        ${isResend ? `<div style="font-size:0.85rem;color:#5a7a9a;margin-bottom:1rem;">Previously sent ${sentDate} — no response yet.</div>` : `<div style="font-size:0.85rem;color:#5a7a9a;margin-bottom:1rem;">Claude will draft the email in Ryan's voice.</div>`}
        <label style="display:block;font-size:0.75rem;color:#5a7a9a;margin-bottom:0.35rem;">Send to</label>
        <input id="survey-send-email" type="email" value="${defaultEmail}"
          style="width:100%;background:#0d1520;border:1px solid rgba(0,180,216,0.2);border-radius:6px;
                 color:#e8f4fd;padding:0.6rem 0.75rem;font-size:0.9rem;outline:none;box-sizing:border-box;"
          placeholder="director@school.edu" />
        <div style="font-size:0.72rem;color:#5a7a9a;margin-top:0.35rem;">
          Edit to override — defaults to the show's director email.
        </div>
        <div style="display:flex;gap:0.75rem;justify-content:flex-end;margin-top:1.5rem;">
          <button id="survey-send-cancel"
            style="background:transparent;border:1px solid rgba(0,180,216,0.2);border-radius:6px;
                   color:#5a7a9a;padding:0.5rem 1.25rem;cursor:pointer;font-size:0.875rem;">
            Cancel
          </button>
          <button id="survey-send-confirm"
            style="background:#00b4d8;border:none;border-radius:6px;
                   color:#fff;padding:0.5rem 1.5rem;cursor:pointer;font-weight:700;font-size:0.875rem;">
            ${isResend ? 'Resend' : 'Send'}
          </button>
        </div>
      </div>
    </div>`;

  document.body.insertAdjacentHTML('beforeend', modalHtml);
  const modal = document.getElementById('survey-send-modal');
  const emailInput = document.getElementById('survey-send-email');
  emailInput.focus();
  emailInput.select();

  await new Promise((resolve) => {
    document.getElementById('survey-send-cancel').onclick = () => {
      modal.remove();
      resolve(null);
    };
    modal.addEventListener('click', e => { if (e.target === modal) { modal.remove(); resolve(null); } });
    document.getElementById('survey-send-confirm').onclick = async () => {
      const toEmail = emailInput.value.trim();
      if (!toEmail) { emailInput.style.borderColor = '#ff2d78'; return; }
      const confirmBtn = document.getElementById('survey-send-confirm');
      confirmBtn.disabled = true;
      confirmBtn.textContent = 'Sending…';
      try {
        await apiFetch(`/api/v1/survey/show/${showId}/send`, {
          method: 'POST',
          body: JSON.stringify({ to_email: toEmail, to_name: defaultName })
        });
        modal.remove();
        alert(`Survey sent to ${toEmail}`);
        await renderClientCard(state.currentClientId);
      } catch (err) {
        confirmBtn.disabled = false;
        confirmBtn.textContent = isResend ? 'Resend' : 'Send';
        alert('Failed to send survey: ' + err.message);
      }
      resolve();
    };
  });
};

window.openSurveyResultsModal = async function(surveyId, surveyStatus) {
  document.getElementById('survey-results-modal')?.remove();

  let data;
  try {
    data = await apiFetch(`/api/v1/survey/${surveyId}/data`);
  } catch (err) {
    alert('Failed to load survey data: ' + err.message);
    return;
  }

  // survey_data may be a JSON string (TEXT col) or a parsed object (JSON col)
  let rawData = data.survey_data || {};
  if (typeof rawData === 'string') { try { rawData = JSON.parse(rawData); } catch (_) {} }
  const d = rawData;

  const returnedDate = (data.returned_at || surveyStatus?.returned_at)
    ? new Date(data.returned_at || surveyStatus.returned_at).toLocaleDateString()
    : '';
  const orgName = data.org_name || '';
  const year    = data.season_year || '';

  const instrRow = (label, count, notes) => {
    if (!count && !notes) return '';
    return `<tr>
      <td style="color:#7a9ab8;font-size:0.82rem;padding:0.2rem 0.6rem 0.2rem 0;">${label}</td>
      <td style="font-size:0.82rem;color:#e8f4fd;padding:0.2rem 0.4rem;">${count || '—'}</td>
      <td style="font-size:0.78rem;color:#5a7a9a;padding:0.2rem 0;">${notes || ''}</td>
    </tr>`;
  };

  const instrSection = (title, rows) => {
    const content = rows.join('');
    if (!content) return '';
    return `
      <div style="margin-bottom:1rem;">
        <div style="font-size:0.68rem;color:#00b4d8;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:0.35rem;font-weight:600;">${title}</div>
        <table style="width:100%;border-collapse:collapse;">${content}</table>
      </div>
    `;
  };

  const woodwinds = instrSection('Woodwinds', [
    instrRow('Piccolo',       d.piccolo_count,       d.piccolo_notes),
    instrRow('Flute',         d.flute_count,         d.flute_notes),
    instrRow('Clarinet',      d.clarinet_count,       d.clarinet_notes),
    instrRow('Bass Clarinet', d.bass_clarinet_count,  d.bass_clarinet_notes),
    instrRow('Alto Sax',      d.alto_sax_count,       d.alto_sax_notes),
    instrRow('Tenor Sax',     d.tenor_sax_count,      d.tenor_sax_notes),
    instrRow('Bari Sax',      d.bari_sax_count,       d.bari_sax_notes),
  ]);

  const brass = instrSection('Brass', [
    instrRow('Trumpet',    d.trumpet_count,    d.trumpet_notes),
    instrRow('Mellophone', d.mellophone_count, d.mellophone_notes),
    instrRow('Trombone',   d.trombone_count,   d.trombone_notes),
    instrRow('Baritone',   d.baritone_count,   d.baritone_notes),
    instrRow('Tuba',       d.tuba_count,       d.tuba_notes),
  ]);

  const battery = instrSection('Battery', [
    instrRow('Snare',            d.snare_count,      d.snare_notes),
    instrRow('Tenor Drums',      d.tenor_count,      d.tenor_notes),
    instrRow('Bass Drums',       d.bass_drum_count,  d.bass_drum_notes),
    instrRow('Marching Cymbals', d.cymbals_count,    d.cymbals_notes),
  ]);

  const frontEnsemble = instrSection('Front Ensemble', [
    instrRow('Marimba',        d.marimba_count,   d.marimba_notes),
    instrRow('Vibraphone',     d.vibe_count,      d.vibe_notes),
    instrRow('Xylophone',      d.xylo_count,      d.xylo_notes),
    instrRow('Glock',          d.glock_count,     d.glock_notes),
    instrRow('Timpani',        d.timp_count,      d.timp_notes),
    instrRow('Accessory/Rack', d.accessory_count, d.accessory_notes),
    instrRow('Other',          d.other_pit_count, d.other_pit_notes),
  ]);

  const synthHtml = (d.synth_count || d.synth_type || d.synth_software) ? `
    <div style="margin-bottom:1rem;">
      <div style="font-size:0.68rem;color:#00b4d8;text-transform:uppercase;letter-spacing:0.1em;margin-bottom:0.35rem;font-weight:600;">Synthesizer</div>
      <div style="font-size:0.82rem;color:#e8f4fd;">
        ${[d.synth_count ? `Count: ${d.synth_count}` : '', d.synth_type ? `Type: ${d.synth_type}` : '', d.synth_software ? `Software: ${d.synth_software}` : ''].filter(Boolean).join(' · ')}
      </div>
    </div>
  ` : '';

  const miscHtml = [
    d.brass_miking  ? `<div style="font-size:0.82rem;color:#7a9ab8;margin-bottom:0.3rem;">Brass mic'ing: <span style="color:#e8f4fd;">${d.brass_miking}</span></div>` : '',
    d.sound_system  ? `<div style="font-size:0.82rem;color:#7a9ab8;margin-bottom:0.3rem;">Sound system: <span style="color:#e8f4fd;">${d.sound_system}</span></div>` : '',
    d.school_name   ? `<div style="font-size:0.82rem;color:#7a9ab8;margin-bottom:0.3rem;">School: <span style="color:#e8f4fd;">${d.school_name}</span></div>` : '',
  ].join('');

  const hasInstrData = woodwinds || brass || battery || frontEnsemble || synthHtml;
  let bodyHtml = '';
  if (hasInstrData || miscHtml) {
    bodyHtml = miscHtml + woodwinds + brass + battery + frontEnsemble + synthHtml;
  } else if (Object.keys(d).length > 0) {
    // Fallback: render whatever fields exist
    bodyHtml = `<pre style="font-size:0.82rem;color:#e8f4fd;white-space:pre-wrap;overflow-x:auto;">${JSON.stringify(d, null, 2)}</pre>`;
  }

  if (!bodyHtml) {
    bodyHtml = '<div style="color:#5a7a9a;font-size:0.85rem;">Survey was returned but no response data found.</div>';
  }

  const modal = document.createElement('div');
  modal.id = 'survey-results-modal';
  modal.className = 'modal-overlay';
  modal.innerHTML = `
    <div class="modal-box" style="max-width:600px;max-height:80vh;overflow-y:auto;">
      <div class="modal-header" style="position:sticky;top:0;background:#111d2e;z-index:1;">
        <span class="modal-title" style="font-family:var(--font-arcade)">Ensemble Survey${orgName ? ' — ' + orgName : ''}${year ? ' ' + year : ''}</span>
        <button class="modal-close" onclick="document.getElementById('survey-results-modal').remove()">✕</button>
      </div>
      <div class="modal-body">
        ${returnedDate ? `<div style="font-size:0.72rem;color:#5a7a9a;margin-bottom:1rem;">Returned ${returnedDate}</div>` : ''}
        ${bodyHtml}
      </div>
      <div class="modal-footer">
        <button class="btn-fin-secondary" onclick="document.getElementById('survey-results-modal').remove()">Close</button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
};

window.openNewQuoteModal = async function(showId, clientId) {
  // Remove any existing modal
  document.getElementById('new-quote-modal')?.remove();

  let nextNum = 'QUO??';
  try {
    const r = await apiFetch('/api/v1/quotes/next-number');
    nextNum = r.quote_number;
  } catch (_) {}

  const modal = document.createElement('div');
  modal.id = 'new-quote-modal';
  modal.className = 'modal-overlay';
  modal.innerHTML = `
    <div class="modal-dialog">
      <div class="modal-header">
        <span>New Quote — ${nextNum}</span>
        <button class="modal-close-btn" onclick="document.getElementById('new-quote-modal').remove()">×</button>
      </div>
      <div class="modal-body">
        <input type="hidden" id="nq-show-id" value="${showId}">
        <input type="hidden" id="nq-client-id" value="${clientId}">
        <input type="hidden" id="nq-number" value="${nextNum}">
        <div class="form-field">
          <label>Amount ($)</label>
          <input type="number" id="nq-amount" class="form-input" placeholder="e.g. 3500" step="0.01">
        </div>
        <div class="form-field">
          <label>Description / Line Items</label>
          <textarea id="nq-description" class="form-input" rows="3" placeholder="Marching band show arrangement, full score, parts…"></textarea>
        </div>
        <div class="form-field">
          <label>Status</label>
          <select id="nq-status" class="form-input">
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
          </select>
        </div>
      </div>
      <div class="modal-footer">
        <button onclick="window.submitNewQuote()" class="btn-primary">Create Quote</button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
};

window.submitNewQuote = async function() {
  const showId    = document.getElementById('nq-show-id')?.value;
  const clientId  = document.getElementById('nq-client-id')?.value;
  const number    = document.getElementById('nq-number')?.value;
  const amountVal = document.getElementById('nq-amount')?.value;
  const desc      = document.getElementById('nq-description')?.value?.trim();
  const status    = document.getElementById('nq-status')?.value;

  if (!amountVal) { alert('Enter an amount'); return; }
  const amountCents = Math.round(parseFloat(amountVal) * 100);

  try {
    // Create quote record (no PDF send — staged for later)
    await apiFetch('/api/v1/quotes', {
      method: 'POST',
      body: JSON.stringify({
        quote_number: number,
        show_id: parseInt(showId),
        client_id: parseInt(clientId),
        amount_cents: amountCents,
        line_items: desc || null,
        status
      })
    });
    document.getElementById('new-quote-modal')?.remove();
    await renderClientCard(state.currentClientId);
  } catch (err) {
    alert('Failed to create quote: ' + err.message);
  }
};

window.openSplitInvoiceModal = async function(showId, clientId) {
  document.getElementById('split-invoice-modal')?.remove();

  let num1 = '??', num2 = '??';
  try {
    const r1 = await apiFetch('/api/v1/contracts/next-invoice-number');
    num1 = r1.invoice_number;
    const r2 = await apiFetch('/api/v1/contracts/next-invoice-number?offset=1');
    num2 = r2.invoice_number;
  } catch (_) {}

  const modal = document.createElement('div');
  modal.id = 'split-invoice-modal';
  modal.className = 'modal-overlay';
  modal.innerHTML = `
    <div class="modal-dialog">
      <div class="modal-header">
        <span>Split Invoice</span>
        <button class="modal-close-btn" onclick="document.getElementById('split-invoice-modal').remove()">×</button>
      </div>
      <div class="modal-body">
        <input type="hidden" id="si-show-id" value="${showId}">
        <p style="color:var(--text-muted);font-size:0.82rem;margin:0 0 12px">
          Splits the payment into two invoices with separate payee names.
          Invoice #s: <strong>${num1}</strong> and <strong>${num2}</strong>.
        </p>
        <div class="fin-split-grid">
          <div class="form-field">
            <label>Payee 1 Name</label>
            <input type="text" id="si-payee1" class="form-input" placeholder="White Mage Music">
          </div>
          <div class="form-field">
            <label>Amount 1 ($)</label>
            <input type="number" id="si-amt1" class="form-input" placeholder="2000" step="0.01">
          </div>
          <div class="form-field">
            <label>Payee 2 Name</label>
            <input type="text" id="si-payee2" class="form-input" placeholder="Arranger Name">
          </div>
          <div class="form-field">
            <label>Amount 2 ($)</label>
            <input type="number" id="si-amt2" class="form-input" placeholder="1500" step="0.01">
          </div>
        </div>
        <p style="color:var(--text-muted);font-size:0.75rem;margin:8px 0 0">
          Note: emailing split invoices requires PDFs — stage this in David's queue after saving.
        </p>
      </div>
      <div class="modal-footer">
        <button onclick="window.saveSplitInvoiceDraft()" class="btn-primary">Save Draft</button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
};

window.saveSplitInvoiceDraft = async function() {
  const showId = document.getElementById('si-show-id')?.value;
  const payee1 = document.getElementById('si-payee1')?.value?.trim();
  const payee2 = document.getElementById('si-payee2')?.value?.trim();
  const amt1   = document.getElementById('si-amt1')?.value;
  const amt2   = document.getElementById('si-amt2')?.value;

  if (!payee1 || !payee2 || !amt1 || !amt2) { alert('Fill in all fields'); return; }

  try {
    // Stage in David queue for review before emailing
    await apiFetch('/api/v1/david/queue', {
      method: 'POST',
      body: JSON.stringify({
        action_type: 'note',
        show_id: parseInt(showId),
        title: `Split Invoice Pending: ${payee1} + ${payee2}`,
        body: `Split invoice staged.\n${payee1}: $${amt1}\n${payee2}: $${amt2}\nReady to send once PDFs are attached.`,
        priority: 'normal',
        source: 'manual'
      })
    });
    document.getElementById('split-invoice-modal')?.remove();
    alert('Split invoice info saved to David queue. Attach PDFs to send.');
  } catch (err) {
    alert('Failed to save: ' + err.message);
  }
};

window.openPOIntakeModal = function(showId, clientId) {
  document.getElementById('po-intake-modal')?.remove();

  const modal = document.createElement('div');
  modal.id = 'po-intake-modal';
  modal.className = 'modal-overlay';
  modal.innerHTML = `
    <div class="modal-dialog">
      <div class="modal-header">
        <span>Log Purchase Order</span>
        <button class="modal-close-btn" onclick="document.getElementById('po-intake-modal').remove()">×</button>
      </div>
      <div class="modal-body">
        <input type="hidden" id="po-show-id" value="${showId}">
        <div class="form-field">
          <label>PO Number</label>
          <input type="text" id="po-number" class="form-input" placeholder="e.g. PO-2026-0041">
        </div>
        <div class="form-field">
          <label>PO Amount ($, optional)</label>
          <input type="number" id="po-amount" class="form-input" placeholder="3500" step="0.01">
        </div>
        <div class="form-field">
          <label>Notes</label>
          <textarea id="po-notes" class="form-input" rows="2" placeholder="Any relevant notes…"></textarea>
        </div>
      </div>
      <div class="modal-footer">
        <button onclick="window.savePOIntake()" class="btn-primary">Save PO</button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
};

window.savePOIntake = async function() {
  const showId = document.getElementById('po-show-id')?.value;
  const poNum  = document.getElementById('po-number')?.value?.trim();
  const notes  = document.getElementById('po-notes')?.value?.trim();

  if (!poNum) { alert('PO number required'); return; }

  try {
    await apiFetch(`/api/v1/shows/${showId}/field`, {
      method: 'PATCH',
      body: JSON.stringify({ field: 'po_number', value: poNum })
    });
    if (notes) {
      await apiFetch(`/api/v1/clients/${state.currentClientId}/notes`, {
        method: 'POST',
        body: JSON.stringify({ body: `PO Received: ${poNum}${notes ? ' — ' + notes : ''}`, source: 'manual' })
      });
    }
    document.getElementById('po-intake-modal')?.remove();
    await renderClientCard(state.currentClientId);
  } catch (err) {
    alert('Failed to save PO: ' + err.message);
  }
};

window.openSendInvoiceModal = async function(showId, clientId, feeCents, prefillDescription) {
  document.getElementById('send-invoice-modal')?.remove();

  let nextNum = '??';
  try {
    const r = await apiFetch('/api/v1/contracts/next-invoice-number');
    nextNum = r.invoice_number;
  } catch (_) {}

  const defaultAmount = feeCents ? (feeCents / 100).toFixed(2) : '';

  const modal = document.createElement('div');
  modal.id = 'send-invoice-modal';
  modal.className = 'modal-overlay';
  modal.innerHTML = `
    <div class="modal-dialog">
      <div class="modal-header">
        <span>Send Invoice via Stripe</span>
        <button class="modal-close-btn" onclick="document.getElementById('send-invoice-modal').remove()">×</button>
      </div>
      <div class="modal-body">
        <input type="hidden" id="si-show-id-2" value="${showId}">
        <div class="form-field">
          <label>Invoice Number</label>
          <input type="text" id="si-inv-num" class="form-input" value="${nextNum}">
        </div>
        <div class="form-field">
          <label>Amount ($)</label>
          <input type="number" id="si-amount" class="form-input" value="${defaultAmount}" step="0.01" placeholder="3500.00">
        </div>
        <div class="form-field">
          <label>Description (shown on Stripe checkout)</label>
          <textarea id="si-desc" class="form-input" rows="3" placeholder="Show arrangement services — Lakewood HS 2026" style="resize:vertical;">${prefillDescription || ''}</textarea>
        </div>
        <p class="fin-meta" style="margin-top:8px">
          A Stripe payment link will be generated and emailed to the director from David's account.
          The link opens a hosted Stripe checkout page — card payment only.
          When paid, the show automatically marks as paid.
        </p>
      </div>
      <div class="modal-footer">
        <button onclick="window.submitSendInvoice()" class="btn-primary">Generate & Send</button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
};

window.submitSendInvoice = async function() {
  const showId    = document.getElementById('si-show-id-2')?.value;
  const invNum    = document.getElementById('si-inv-num')?.value?.trim();
  const amountVal = document.getElementById('si-amount')?.value;
  const desc      = document.getElementById('si-desc')?.value?.trim();

  if (!amountVal || isNaN(parseFloat(amountVal))) { alert('Enter a valid amount'); return; }
  const amountCents = Math.round(parseFloat(amountVal) * 100);
  if (amountCents <= 0) { alert('Amount must be greater than zero'); return; }

  const btn = document.querySelector('#send-invoice-modal .btn-primary');
  if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }

  try {
    const result = await apiFetch('/api/stripe/send-invoice', {
      method: 'POST',
      body: JSON.stringify({
        show_id: parseInt(showId),
        amount_cents: amountCents,
        description: desc || undefined,
        invoice_number: invNum || undefined,
      })
    });
    document.getElementById('send-invoice-modal')?.remove();
    alert(`Invoice sent!\n\nStripe payment link:\n${result.payment_url}\n\nThis was also emailed to the director from David.`);
    await renderClientCard(state.currentClientId);
  } catch (err) {
    alert('Failed to send invoice: ' + err.message);
    if (btn) { btn.disabled = false; btn.textContent = 'Generate & Send'; }
  }
};

// ── Google Drive Folder Modal ──────────────────────────────────────────────

window.openDriveFolderModal = function(showId, type) {
  document.getElementById('ft-drive-modal')?.remove();
  const label = type === 'year' ? 'Year Folder' : 'Shares Folder';
  const field = type === 'year' ? 'drive_folder_id' : 'drive_shares_folder_id';
  const modal = document.createElement('div');
  modal.id = 'ft-drive-modal';
  modal.className = 'modal-overlay';
  modal.innerHTML = `
    <div class="modal-box" style="max-width:400px;">
      <div class="modal-header">
        <span class="modal-title">Link ${label}</span>
        <button class="modal-close" onclick="document.getElementById('ft-drive-modal').remove()">✕</button>
      </div>
      <div class="modal-body">
        <p style="font-size:0.8rem;color:#7a9ab8;margin:0 0 0.6rem;">Paste the Google Drive folder URL or folder ID.</p>
        <input type="text" id="ft-drive-modal-input" class="cfg-text" style="width:100%"
          placeholder="https://drive.google.com/drive/folders/…  or  folder ID">
      </div>
      <div class="modal-footer">
        <button class="btn-fin-action" onclick="window.saveDriveFolder(${showId}, '${field}')">Save</button>
        <button class="btn-fin-small" onclick="document.getElementById('ft-drive-modal').remove()">Cancel</button>
      </div>
    </div>`;
  document.body.appendChild(modal);
  document.getElementById('ft-drive-modal-input')?.focus();
};

window.saveDriveFolder = async function(showId, field) {
  const raw = document.getElementById('ft-drive-modal-input')?.value?.trim() || '';
  const m   = raw.match(/\/folders\/([a-zA-Z0-9_-]+)/);
  const id  = m ? m[1] : (raw || null);
  try {
    await apiFetch(`/api/v1/shows/${showId}/field`, {
      method: 'PATCH',
      body: JSON.stringify({ field, value: id })
    });
    document.getElementById('ft-drive-modal')?.remove();
    state.currentTab = 'files-team';
    await renderClientCard(state.currentClientId);
  } catch (err) {
    alert('Failed: ' + err.message);
  }
};

// ── Google Drive Actions ───────────────────────────────────────────────────

// Extract folder ID from a Drive URL or pass through a raw ID
function parseDriveFolderId(val) {
  if (!val) return null;
  const m = val.match(/\/folders\/([a-zA-Z0-9_-]+)/);
  return m ? m[1] : val.trim() || null;
}

window.driveSaveFolderIds = async function(showId) {
  const sharesRaw = document.getElementById(`drive-shares-input-${showId}`)?.value || '';
  const yearRaw   = document.getElementById(`drive-year-input-${showId}`)?.value   || '';
  const sharesId  = parseDriveFolderId(sharesRaw);
  const yearId    = parseDriveFolderId(yearRaw);

  try {
    await apiFetch(`/api/v1/shows/${showId}`, {
      method: 'PUT',
      body: JSON.stringify({
        drive_shares_folder_id: sharesId,
        drive_folder_id: yearId
      })
    });
    state.currentTab = 'files-team';
    await renderClientCard(state.currentClientId);
  } catch (err) {
    alert('Failed to save folder IDs: ' + err.message);
  }
};

window.driveProvisionFolder = async function(showId) {
  const btn = event?.target;
  if (btn) { btn.disabled = true; btn.textContent = 'Creating…'; }
  try {
    await apiFetch('/api/v1/drive/provision-folder', {
      method: 'POST',
      body: JSON.stringify({ show_id: showId })
    });
    // Reload the tab so the new folder IDs appear
    state.currentTab = 'files-team';
    await renderClientCard(state.currentClientId);
  } catch (err) {
    alert('Failed to provision Drive folder: ' + err.message);
    if (btn) { btn.disabled = false; btn.textContent = 'Create Drive Folder'; }
  }
};

window.driveInviteAll = async function(showId) {
  const btn = event?.target;
  if (btn) { btn.disabled = true; btn.textContent = 'Sending…'; }
  try {
    const data = await apiFetch('/api/v1/drive/invite', {
      method: 'POST',
      body: JSON.stringify({ show_id: showId })
    });
    const results  = data.results || [];
    const summary  = results.map(r => `${r.email}: ${r.status}${r.message ? ' — ' + r.message : ''}`).join('\n');
    alert(`Drive invite results:\n\n${summary || '(no emails found for this show)'}`);
    if (btn) { btn.disabled = false; btn.textContent = 'Send Invites'; }
    // Reload to pick up any newly accepted permissions
    state.currentTab = 'files-team';
    await renderClientCard(state.currentClientId);
  } catch (err) {
    alert('Failed to send invites: ' + err.message);
    if (btn) { btn.disabled = false; btn.textContent = 'Send Invites'; }
  }
};

window.driveUpdatePermission = async function(showId, permissionId, role, selectEl) {
  const original = selectEl.dataset.prevRole || selectEl.querySelector('[selected]')?.value || 'reader';
  selectEl.dataset.prevRole = selectEl.value;
  try {
    await apiFetch('/api/v1/drive/permission', {
      method: 'PATCH',
      body: JSON.stringify({ show_id: showId, permission_id: permissionId, role })
    });
    // Flash the row to confirm the save
    const row = selectEl.closest('.drive-perm-row');
    if (row) { row.style.background = 'rgba(0,180,216,0.08)'; setTimeout(() => row.style.background = '', 1200); }
    selectEl.dataset.prevRole = role;
  } catch (err) {
    alert('Failed to update permission: ' + err.message);
    selectEl.value = original;
  }
};

// ── Portal File Links (Drive references) ──────────────────────────────────

window.savePortalFileLink = async function(showId, fileType) {
  const inputEl = document.getElementById(`pf-${fileType}-input-${showId}`);
  const url = inputEl?.value?.trim() || '';
  const body = fileType === 'audio' ? { audio_url: url || null } : { pdf_url: url || null };
  try {
    await apiFetch(`/api/v1/portal/show/${showId}/media`, { method: 'PATCH', body: JSON.stringify(body) });
    // Reload the tab to reflect the saved link and update the open/clear buttons
    state.currentTab = 'files-team';
    await renderClientCard(state.currentClientId);
  } catch (err) {
    alert('Failed to save link: ' + err.message);
  }
};

window.clearPortalFileLink = async function(showId, fileType) {
  try {
    await apiFetch(`/api/v1/portal/show/${showId}/media/${fileType === 'audio' ? 'audio_url' : 'pdf_url'}`, { method: 'DELETE' });
    state.currentTab = 'files-team';
    await renderClientCard(state.currentClientId);
  } catch (err) {
    alert('Failed to clear link: ' + err.message);
  }
};

// ── Show Switcher Actions ──────────────────────────────────────────────────

window.switchShow = async function(showId) {
  state.currentShowId = showId;
  await renderClientCard(state.currentClientId);
};

window.editShowTitle = function(showId) {
  document.getElementById('show-title-edit-overlay')?.remove();

  const tab = document.querySelector(`.show-tab[data-show-id="${showId}"]`);
  if (!tab) return;

  const currentTitle = tab.querySelector('.show-tab-title')?.textContent?.trim() || '';

  const rect = tab.getBoundingClientRect();
  const overlay = document.createElement('div');
  overlay.id = 'show-title-edit-overlay';
  overlay.style.cssText = `position:fixed;left:${rect.left}px;top:${rect.top}px;z-index:2000;` +
    `background:#111d2e;border:1px solid #00b4d8;border-radius:6px;padding:4px 6px;` +
    `display:flex;align-items:center;gap:4px;box-shadow:0 4px 16px rgba(0,0,0,0.5);`;
  overlay.innerHTML = `
    <input id="show-title-edit-input" class="show-title-edit-input"
           value="${currentTitle.replace(/"/g, '&quot;')}" style="width:180px">
    <button class="show-tab-edit-btn" id="show-title-save-btn" title="Save">✓</button>
    <button class="show-tab-edit-btn" id="show-title-cancel-btn" title="Cancel">✕</button>
  `;
  document.body.appendChild(overlay);

  const input = document.getElementById('show-title-edit-input');
  input.focus();
  input.select();

  input.addEventListener('keydown', e => {
    if (e.key === 'Enter')  { e.preventDefault(); window.saveShowTitle(showId); }
    if (e.key === 'Escape') { window.cancelEditShowTitle(); }
  });
  document.getElementById('show-title-save-btn').addEventListener('click',  () => window.saveShowTitle(showId));
  document.getElementById('show-title-cancel-btn').addEventListener('click', () => window.cancelEditShowTitle());
};

window.saveShowTitle = async function(showId) {
  const input = document.getElementById('show-title-edit-input');
  const newTitle = input?.value?.trim();
  if (!newTitle) { alert('Title cannot be empty.'); return; }

  try {
    await apiFetch(`/api/v1/shows/${showId}/field`, {
      method: 'PATCH',
      body: JSON.stringify({ field: 'title', value: newTitle })
    });
    window.cancelEditShowTitle();
    await renderClientCard(state.currentClientId);
  } catch (err) {
    alert('Could not save title: ' + err.message);
  }
};

window.cancelEditShowTitle = function() {
  document.getElementById('show-title-edit-overlay')?.remove();
};

// ── Yeet Modal ─────────────────────────────────────────────────────────────

window.openYeetModal = function(client, show) {
  if (!client || !show) return;
  document.getElementById('yeet-modal')?.remove();

  const clientName = client.org_name || client.name || 'this client';
  const showLabel  = show.title || `${show.season_year} Season`;

  const overlay = document.createElement('div');
  overlay.id = 'yeet-modal';
  overlay.className = 'modal-overlay';
  overlay.style.display = 'flex';
  overlay.innerHTML = `
    <div class="modal-box yeet-modal-box">
      <div class="yeet-modal-title">⚠ CHOOSE YOUR FATE</div>
      <div class="yeet-modal-client">${clientName}</div>
      <div class="yeet-modal-desc">What exactly are we yeeting?</div>

      <button class="btn btn-yeet-season" id="yeet-delete-show">
        DELETE THIS SHOW
        <span class="yeet-sub">"${showLabel}" only — client and other shows stay</span>
      </button>

      <div class="yeet-divider">— or go further —</div>

      <button class="btn btn-yeet-full" id="yeet-finish-him">FINISH HIM!</button>
      <div id="yeet-permadeath-wrap" style="display:none">
        <div class="yeet-permadeath-warn">This will permanently erase <strong>${clientName}</strong> and ALL associated data. There is no undo.</div>
        <button class="btn btn-permadeath" id="yeet-permadeath">☠ PERMADEATH ☠</button>
      </div>

      <button class="btn btn-ghost btn-sm" id="yeet-cancel" style="margin-top:20px;width:100%">Never mind, they live another day</button>
    </div>
  `;
  document.body.appendChild(overlay);

  document.getElementById('yeet-cancel').addEventListener('click', () => overlay.remove());
  overlay.addEventListener('click', e => { if (e.target === overlay) overlay.remove(); });

  document.getElementById('yeet-delete-show').addEventListener('click', async () => {
    const btn = document.getElementById('yeet-delete-show');
    btn.disabled = true;
    btn.textContent = 'Deleting…';
    try {
      await apiFetch(`/api/v1/shows/${show.id}`, { method: 'DELETE' });
      overlay.remove();
      state.currentShowId = null;
      await renderClientCard(client.id);
    } catch (err) {
      btn.disabled = false;
      btn.innerHTML = `DELETE THIS SHOW<span class="yeet-sub">"${showLabel}" only — client and other shows stay</span>`;
      alert('Error: ' + err.message);
    }
  });

  document.getElementById('yeet-finish-him').addEventListener('click', () => {
    document.getElementById('yeet-permadeath-wrap').style.display = 'block';
    document.getElementById('yeet-finish-him').style.display = 'none';
  });

  document.getElementById('yeet-permadeath').addEventListener('click', async () => {
    const btn = document.getElementById('yeet-permadeath');
    btn.disabled = true;
    btn.textContent = '☠ Banishing…';
    try {
      await apiFetch(`/api/v1/clients/${client.id}`, { method: 'DELETE' });
      overlay.remove();
      state.currentClientId = null;
      state.currentShowId   = null;
      // Navigate back to dashboard
      if (typeof window.showView === 'function') window.showView('dashboard');
      else document.getElementById('client-detail-content')?.replaceWith(Object.assign(document.createElement('div'), { id: 'client-detail-content' }));
    } catch (err) {
      btn.disabled = false;
      btn.textContent = '☠ PERMADEATH ☠';
      alert('Error: ' + err.message);
    }
  });
};

window.addNoteForClient = function(clientId) {
  // Delegate to app-legacy.js's note add modal if present
  if (typeof window.openNoteModal === 'function') {
    window.openNoteModal(clientId);
  } else {
    const body = prompt('Note:');
    if (!body) return;
    apiFetch(`/api/v1/clients/${clientId}/notes`, {
      method: 'POST',
      body: JSON.stringify({ body, source: 'manual' })
    }).then(() => renderClientCard(clientId))
      .catch(err => alert('Failed to add note: ' + err.message));
  }
};

// ── Schedule Tab ───────────────────────────────────────────────────────────

async function renderScheduleTab(show, client) {
  const year = show.season_year || new Date().getFullYear();
  const events = await apiFetch(`/api/clients/${client.id}/schedule?season_year=${year}`).catch(() => []);

  const typeClass = { competition: 'event-competition', deadline: 'event-deadline', meeting: 'event-meeting', other: 'event-other' };
  const typeLabel = { competition: 'Competition', deadline: 'Deadline', meeting: 'Meeting', other: 'Other' };

  const rows = events.length > 0
    ? events.map(e => `
        <tr class="sched-row ${typeClass[e.event_type] || 'event-other'}">
          <td>${e.event_date || ''}</td>
          <td>${e.event_name}${e.source === 'production_milestone' ? ' <span class="milestone-lock" title="Production milestone">&#128274;</span>' : ''}</td>
          <td>${typeLabel[e.event_type] || e.event_type}</td>
          <td>${e.location || '—'}</td>
          <td>${e.notes || '—'}</td>
          <td>${e.source === 'production_milestone' ? '' : `<button class="btn-del-sched" data-id="${e.id}" title="Delete">×</button>`}</td>
        </tr>
      `).join('')
    : `<tr><td colspan="6" class="empty-state">No events yet. Add an event or link shows to populate milestones.</td></tr>`;

  return `
    <div class="tab-section">
      <div class="tab-section-header">
        <span class="tab-section-title">Schedule</span>
        <button class="btn-add-inline" onclick="window.openAddScheduleEventModal(${client.id})">+ Add Event</button>
      </div>
      <table class="schedule-tab-table">
        <thead><tr><th>Date</th><th>Event</th><th>Type</th><th>Location</th><th>Notes</th><th></th></tr></thead>
        <tbody>${rows}</tbody>
      </table>

      <div id="add-sched-modal" class="modal-overlay hidden">
        <div class="modal-dialog">
          <div class="modal-header">
            <span>Add Schedule Event</span>
            <button class="modal-close-btn" onclick="document.getElementById('add-sched-modal').classList.add('hidden')">×</button>
          </div>
          <div class="modal-body">
            <div class="form-field">
              <label>Event Type</label>
              <select id="sched-type" class="form-input">
                <option value="competition">Competition</option>
                <option value="deadline">Deadline</option>
                <option value="meeting">Meeting</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div class="form-field">
              <label>Event Name</label>
              <input type="text" id="sched-name" class="form-input" placeholder="e.g. BOA San Antonio Regional">
            </div>
            <div class="form-field">
              <label>Date</label>
              <input type="date" id="sched-date" class="form-input">
            </div>
            <div class="form-field">
              <label>Time (optional)</label>
              <input type="time" id="sched-time" class="form-input">
            </div>
            <div class="form-field">
              <label>Location (optional)</label>
              <input type="text" id="sched-location" class="form-input">
            </div>
            <div class="form-field">
              <label>Notes (optional)</label>
              <input type="text" id="sched-notes" class="form-input">
            </div>
          </div>
          <div class="modal-footer">
            <button onclick="window.saveScheduleEvent(${client.id})" class="btn-primary">Save Event</button>
          </div>
        </div>
      </div>
    </div>
  `;
}

function wireScheduleTab(clientId) {
  document.querySelectorAll('.btn-del-sched').forEach(btn => {
    btn.addEventListener('click', async () => {
      if (!confirm('Delete this event?')) return;
      try {
        await apiFetch(`/api/clients/${clientId}/schedule/${btn.dataset.id}`, { method: 'DELETE' });
        await loadTabContent('schedule', window._currentShow, window._currentClient);
      } catch (err) {
        alert('Failed to delete: ' + err.message);
      }
    });
  });
}

window.openAddScheduleEventModal = function(clientId) {
  document.getElementById('add-sched-modal')?.classList.remove('hidden');
};

window.saveScheduleEvent = async function(clientId) {
  const data = {
    event_type: document.getElementById('sched-type')?.value,
    event_name: document.getElementById('sched-name')?.value,
    event_date: document.getElementById('sched-date')?.value,
    event_time: document.getElementById('sched-time')?.value || null,
    location:   document.getElementById('sched-location')?.value || null,
    notes:      document.getElementById('sched-notes')?.value || null,
  };
  if (!data.event_name || !data.event_date) {
    alert('Event name and date are required');
    return;
  }
  try {
    await apiFetch(`/api/clients/${clientId}/schedule`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
    document.getElementById('add-sched-modal')?.classList.add('hidden');
    await loadTabContent('schedule', window._currentShow, window._currentClient);
  } catch (err) {
    alert('Failed to save: ' + err.message);
  }
};

// ── Results Tab — F.2 (full competitive sheet view + import pipeline) ────────

async function renderResultsTab(show, client) {
  const season = show?.season_year || new Date().getFullYear();

  let entries = [];
  try {
    entries = await apiFetch(`/api/competition/entries?client_id=${client.id}&season=${season}`);
  } catch (e) {
    console.warn('[results] entries fetch failed:', e.message);
  }

  // Group by competition
  const byComp = {};
  for (const e of entries) {
    if (!byComp[e.competition_id]) byComp[e.competition_id] = { meta: e, entries: [] };
    byComp[e.competition_id].entries.push(e);
  }
  const competitions = Object.values(byComp).sort((a, b) => (a.meta.event_date || '').localeCompare(b.meta.event_date || ''));

  const compCards = competitions.length === 0
    ? `<div class="empty-state" style="padding:1.5rem;text-align:center">No results imported for ${season}. Use "Import Recap" to add a scoresheet.</div>`
    : competitions.map(g => {
        const m = g.meta;
        const placement = m.placement != null ? `#${m.placement}` : '—';
        const total = m.total_score != null ? parseFloat(m.total_score).toFixed(3) : '—';
        const circuitBadge = m.circuit ? `<span class="comp-badge">${m.circuit}</span>` : '';
        const roundBadge   = m.round && m.round !== 'regular' ? `<span class="comp-badge comp-badge-round">${m.round}</span>` : '';
        return `
          <div class="results-comp-card" data-comp-id="${m.competition_id}">
            <div class="results-comp-header" onclick="window.toggleResultSheet(${m.competition_id})">
              <div class="results-comp-title">
                <strong>${m.competition_name}</strong>
                ${circuitBadge}${roundBadge}
                <span class="results-comp-date">${m.event_date || ''}</span>
              </div>
              <div class="results-comp-summary">
                ${placement} of ${m.class_size || '?'} &nbsp;·&nbsp; ${total} pts
                <span class="results-expand-icon" id="expand-icon-${m.competition_id}">▶</span>
              </div>
            </div>
            <div class="results-sheet-panel" id="sheet-${m.competition_id}" style="display:none">
              <div class="results-sheet-loading" style="padding:1rem;color:var(--text-muted)">Loading sheet…</div>
            </div>
          </div>`;
      }).join('');

  return `
    <div class="tab-section" id="results-tab-root">
      <div class="tab-section-header">
        <span class="tab-section-title">Results — ${season} Season</span>
        <button class="btn-add-inline" onclick="window.openRecapImportModal(${client.id})">+ Import Recap</button>
      </div>

      <!-- Feature G: Generate Analysis + Publish to Portal -->
      <div class="forecast-publish-bar" id="forecast-bar-${client.id}" style="margin-bottom:1rem;padding:0.85rem 1rem;background:rgba(0,180,216,0.04);border:1px solid rgba(0,180,216,0.12);border-radius:8px;">
        <div style="display:flex;align-items:center;gap:0.75rem;flex-wrap:wrap;">
          <button class="btn-secondary btn-sm" onclick="window.generateCompAnalysis(${client.id}, '${season}')">Generate Analysis</button>
          <button class="btn-secondary btn-sm" onclick="window.runManualAction('run_weekly_analysis_for_band',{client_id:${client.id}},null,'Run weekly analysis for this band')">Weekly Analysis</button>
          <span id="generate-status-${client.id}" style="font-size:0.78rem;color:var(--text-muted)"></span>
          <span class="auto-inline-hint" id="ma-status-weekly-${client.id}"></span>
        </div>
        <div id="narrative-preview-${client.id}" style="display:none;margin-top:0.85rem;">
          <label style="font-size:0.72rem;text-transform:uppercase;letter-spacing:0.07em;color:var(--text-muted);display:block;margin-bottom:0.35rem;">David's narrative (edit before publishing)</label>
          <textarea id="narrative-text-${client.id}" rows="5" style="width:100%;background:rgba(0,0,0,0.2);border:1px solid rgba(0,180,216,0.2);color:var(--text-primary);padding:0.6rem 0.75rem;border-radius:6px;font-size:0.85rem;font-family:inherit;resize:vertical;"></textarea>
          <input type="text" id="portal-note-${client.id}" placeholder="Personal note to director (optional — shown above analysis in portal)"
            style="width:100%;margin-top:0.5rem;background:rgba(0,0,0,0.2);border:1px solid rgba(0,180,216,0.15);color:var(--text-primary);padding:0.5rem 0.75rem;border-radius:6px;font-size:0.85rem;font-family:inherit;">
          <div style="display:flex;align-items:center;gap:0.75rem;margin-top:0.6rem;flex-wrap:wrap;">
            <button class="btn-primary btn-sm" onclick="window.publishForecastToPortal(${client.id})">Publish to Portal</button>
            <span id="publish-status-${client.id}" style="font-size:0.78rem;color:var(--text-muted)"></span>
          </div>
        </div>
      </div>

      <div id="results-competitions-list">
        ${compCards}
      </div>

      <!-- Import Recap Modal -->
      <div id="recap-import-modal" class="modal-overlay hidden">
        <div class="modal-dialog" style="max-width:680px;max-height:90vh;display:flex;flex-direction:column">
          <div class="modal-header" style="flex-shrink:0">
            <span>Import Recap Scoresheet</span>
            <button class="modal-close-btn" onclick="document.getElementById('recap-import-modal').classList.add('hidden')">×</button>
          </div>
          <div class="modal-body" id="recap-import-body" style="overflow-y:auto;flex:1;min-height:0">
            <!-- Step 1: upload -->
            <div id="recap-step-upload">
              <p style="margin-bottom:1rem;color:var(--text-muted);font-size:.875rem">
                Upload a JPEG, PNG, or PDF scoresheet. Claude will extract the structure — review before confirming.
              </p>
              <div class="form-field">
                <label>Scoresheet Image / PDF</label>
                <input type="file" id="recap-file-input" accept=".jpg,.jpeg,.png,.pdf" style="font-size:.85rem">
              </div>
              <div id="recap-extract-status" style="margin-top:.5rem;color:var(--text-muted);font-size:.85rem"></div>
              <div class="modal-footer" style="margin-top:1rem">
                <button onclick="window.runRecapExtract(${client.id})" class="btn-primary" id="recap-extract-btn">Extract →</button>
              </div>
            </div>

            <!-- Step 2: review (hidden until extraction) -->
            <div id="recap-step-review" style="display:none">
              <div id="recap-review-content"></div>
              <div id="recap-format-warning" style="display:none;margin-top:.75rem;padding:.75rem;background:rgba(255,200,0,.1);border:1px solid rgba(255,200,0,.3);border-radius:6px;font-size:.8rem;color:#e5c900"></div>
              <div class="modal-footer" style="position:sticky;bottom:0;gap:.5rem;display:flex;background:#1b2535;padding:.75rem 0 .25rem;margin-top:.75rem">
                <button onclick="window.resetRecapImport()" class="btn-secondary">← Back</button>
                <button onclick="window.confirmRecapImport(${client.id})" class="btn-primary" id="recap-confirm-btn">Confirm Import</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Feature G: Portal David Conversations (read-only) -->
      <div style="margin-top:1.25rem;padding:0.85rem 1rem;background:rgba(255,255,255,0.02);border:1px solid rgba(255,255,255,0.06);border-radius:8px;" id="portal-convos-panel-${client.id}">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:0.5rem;">
          <span style="font-size:0.72rem;text-transform:uppercase;letter-spacing:0.07em;color:var(--text-muted);">Portal Chat Sessions</span>
          <button class="btn-ghost btn-xs" onclick="window.loadPortalConvos(${client.id}, '${season}')" style="font-size:0.75rem;padding:0.2rem 0.6rem;">Load</button>
        </div>
        <div id="portal-convos-body-${client.id}" style="font-size:0.82rem;color:var(--text-muted);">
          Click Load to view director chat sessions.
        </div>
      </div>

      <!-- Edit judge modal (per caption column, applies to all bands on sheet) -->
      <div id="edit-judges-modal" class="modal-overlay hidden">
        <div class="modal-dialog" style="max-width:400px">
          <div class="modal-header">
            <span>Set Judge — <span id="edit-judge-caption-label" style="opacity:.7"></span></span>
            <button class="modal-close-btn" onclick="document.getElementById('edit-judges-modal').classList.add('hidden')">×</button>
          </div>
          <div class="modal-body">
            <p style="color:var(--text-muted);font-size:.85rem;margin-bottom:.75rem">
              Applies this judge name to every band's score for this caption (and panel, if applicable).
            </p>
            <input type="hidden" id="edit-judge-comp-id">
            <input type="hidden" id="edit-judge-caption-raw">
            <input type="hidden" id="edit-judge-panel-id">
            <div class="form-field">
              <label>Judge Name</label>
              <input type="text" id="edit-judge-name" class="form-input" placeholder="e.g. B. Mascaro">
            </div>
          </div>
          <div class="modal-footer">
            <button onclick="window.applyJudgeName()" class="btn-primary">Apply to all bands</button>
          </div>
        </div>
      </div>
    </div>`;
}

function wireResultsTab(clientId) {
  // Delegate clicks handled by window.* handlers below
}

// Feature G — Generate Analysis + Publish to Portal handlers
// TWO SEPARATE CLAUDE PATHS — do not confuse them:
//   generateCompAnalysis → POST /api/competition-analysis/forecast/:bandId/narrative
//     Uses internal full-context forecasting service. This is Ryan's internal view.
//   Portal David chat → POST /api/portal/competition/david
//     Uses scoped director-facing system prompt. Directors only.

window._forecastCache = {}; // clientId → { forecastId, bandId }

window.generateCompAnalysis = async function(clientId, season) {
  const statusEl = document.getElementById(`generate-status-${clientId}`);
  const previewEl = document.getElementById(`narrative-preview-${clientId}`);
  if (statusEl) { statusEl.textContent = 'Generating…'; }

  try {
    // Step 1: look up forecast for this client+season
    const { forecast, band } = await apiFetch(`/api/competition-analysis/forecast-for-client/${clientId}?season=${season}`);
    if (!forecast) {
      if (statusEl) statusEl.textContent = 'No competition data found for this season.';
      return;
    }

    window._forecastCache[clientId] = { forecastId: forecast.id, bandId: band?.id };

    // Step 2: generate narrative if missing (internal Claude Sonnet — NOT portal prompt)
    let narrative = forecast.narrative;
    if (!narrative) {
      if (statusEl) statusEl.textContent = 'Drafting narrative…';
      const updated = await apiFetch(`/api/competition-analysis/forecast/${band.id}/narrative?season=${season}`, { method: 'POST' });
      narrative = updated.narrative;
      // Re-fetch to get updated forecast.id in cache
      window._forecastCache[clientId].forecastId = updated.id;
    }

    // Step 3: populate preview
    const narrativeEl = document.getElementById(`narrative-text-${clientId}`);
    if (narrativeEl) narrativeEl.value = narrative || '';
    if (previewEl) previewEl.style.display = 'block';
    if (statusEl) {
      const pub = forecast.portal_published_at
        ? `Last published ${new Date(forecast.portal_published_at).toLocaleDateString()}.`
        : 'Not yet published.';
      statusEl.textContent = pub;
    }
  } catch (err) {
    if (statusEl) statusEl.textContent = 'Error: ' + err.message;
  }
};

window.publishForecastToPortal = async function(clientId) {
  const cache     = window._forecastCache[clientId];
  const statusEl  = document.getElementById(`publish-status-${clientId}`);
  if (!cache?.forecastId) {
    if (statusEl) statusEl.textContent = 'Run Generate Analysis first.';
    return;
  }

  const narrative = document.getElementById(`narrative-text-${clientId}`)?.value || null;
  const note      = document.getElementById(`portal-note-${clientId}`)?.value?.trim() || null;
  if (statusEl) statusEl.textContent = 'Publishing…';

  try {
    await apiFetch(`/api/competition-analysis/forecast/${cache.forecastId}/publish`, {
      method: 'PATCH',
      body: JSON.stringify({ narrative, ryan_portal_note: note })
    });
    if (statusEl) {
      statusEl.textContent = `Published to portal ✓ — ${new Date().toLocaleTimeString()}`;
      statusEl.style.color = 'var(--accent-green, #22c55e)';
    }
  } catch (err) {
    if (statusEl) { statusEl.textContent = 'Publish failed: ' + err.message; statusEl.style.color = 'var(--accent-pink)'; }
  }
};

window.loadPortalConvos = async function(clientId, season) {
  const bodyEl  = document.getElementById(`portal-convos-body-${clientId}`);
  const cache   = window._forecastCache[clientId];
  const bandId  = cache?.bandId;

  if (!bandId) {
    // Try to fetch band if not cached yet
    try {
      const { band } = await apiFetch(`/api/competition-analysis/forecast-for-client/${clientId}?season=${season}`);
      if (!band) { if (bodyEl) bodyEl.textContent = 'No competition band linked to this client.'; return; }
      if (!window._forecastCache[clientId]) window._forecastCache[clientId] = {};
      window._forecastCache[clientId].bandId = band.id;
      return window.loadPortalConvos(clientId, season);
    } catch (e) {
      if (bodyEl) bodyEl.textContent = 'Failed to load: ' + e.message;
      return;
    }
  }

  if (bodyEl) bodyEl.innerHTML = '<span style="color:var(--text-muted)">Loading…</span>';
  try {
    const sessions = await apiFetch(`/api/portal/david-sessions?band_id=${bandId}&season=${season}`);
    if (!sessions.length) {
      bodyEl.textContent = 'No portal chat sessions yet.';
      return;
    }
    bodyEl.innerHTML = sessions.map(s => {
      const msgs = Array.isArray(s.messages) ? s.messages : [];
      const preview = msgs.map(m =>
        `<div style="margin-top:0.35rem;${m.role === 'user' ? 'color:#b0cce0' : 'color:#7a9ab8'}"><strong>${m.role === 'user' ? (s.director_name || 'Director') : 'David'}:</strong> ${String(m.content || '').slice(0, 200)}${(m.content || '').length > 200 ? '…' : ''}</div>`
      ).join('');
      const dateStr = s.updated_at ? new Date(s.updated_at).toLocaleDateString() : '';
      return `<div style="margin-bottom:0.85rem;padding-bottom:0.85rem;border-bottom:1px solid rgba(255,255,255,0.05);">
        <div style="font-size:0.72rem;color:var(--text-muted);margin-bottom:0.25rem;">${s.director_name || 'Director'} · ${msgs.length} messages · ${dateStr}</div>
        ${preview}
      </div>`;
    }).join('');
  } catch (err) {
    if (bodyEl) bodyEl.textContent = 'Failed to load sessions: ' + err.message;
  }
};

// ── Results Tab — window handlers ─────────────────────────────────────────

window.toggleResultSheet = async function(competitionId) {
  const panel = document.getElementById(`sheet-${competitionId}`);
  const icon  = document.getElementById(`expand-icon-${competitionId}`);
  if (!panel) return;

  if (panel.style.display !== 'none') {
    panel.style.display = 'none';
    if (icon) icon.textContent = '▶';
    return;
  }

  panel.style.display = 'block';
  if (icon) icon.textContent = '▼';

  if (panel.dataset.loaded) return;
  panel.dataset.loaded = '1';

  try {
    const data = await apiFetch(`/api/competition/competitions/${competitionId}/sheet`);
    panel.innerHTML = renderCompetitiveSheet(data);
  } catch (err) {
    panel.innerHTML = `<div style="padding:.75rem;color:var(--accent-pink)">Failed to load sheet: ${err.message}</div>`;
  }
};

function renderCompetitiveSheet(data) {
  const { competition, entries } = data;
  if (!entries || entries.length === 0) return '<div class="empty-state" style="padding:.75rem">No entries on this sheet.</div>';

  // Collect unique captions in sort order across all entries.
  // Use caption_raw_name as the stable key (covers matched + unmatched captions).
  const captionOrder = [];
  const captionSeen = new Set();
  const captionMeta = {}; // raw_name → { sub1_label, sub2_label, caption_group }
  for (const e of entries) {
    // Group by raw_name to collect all score_components for this caption
    const grouped = {};
    for (const cs of (e.caption_scores || [])) {
      const key = cs.caption_raw_name || '?';
      if (!grouped[key]) grouped[key] = { cs, scores: {} };
      grouped[key].scores[cs.score_component] = cs;
    }
    for (const [rawName, g] of Object.entries(grouped)) {
      if (!captionSeen.has(rawName)) {
        captionSeen.add(rawName);
        captionOrder.push({ rawName, sort_order: g.cs.sort_order || 999 });
        captionMeta[rawName] = {
          sub1_label: g.cs.sub_score_1_label,
          sub2_label: g.cs.sub_score_2_label,
          caption_group: g.cs.caption_group
        };
      }
    }
  }
  captionOrder.sort((a, b) => a.sort_order - b.sort_order);

  // Collect judges per caption per panel — { rawName → { panelId → judge_name } }
  const captionJudges = {};
  for (const e of entries) {
    for (const cs of (e.caption_scores || [])) {
      const key = cs.caption_raw_name || '?';
      if (cs.judge_name) {
        if (!captionJudges[key]) captionJudges[key] = {};
        const pid = cs.panel_id ?? 0;
        if (!captionJudges[key][pid]) captionJudges[key][pid] = cs.judge_name;
      }
    }
  }

  const fmtVal = v => v != null ? parseFloat(v).toFixed(3) : null;
  const compId = competition?.id;

  const headerCols = captionOrder.map(({ rawName }) => {
    const meta = captionMeta[rawName];
    const sub1 = meta?.sub1_label || '';
    const sub2 = meta?.sub2_label || '';
    const subLabels = [sub1, sub2].filter(Boolean).join('/');
    const judges = captionJudges[rawName] || {};
    const judgeEntries = Object.entries(judges).sort(([a], [b]) => +a - +b);
    const safeRaw = rawName.replace(/\\/g, '\\\\').replace(/'/g, "\\'");

    let judgeButtons;
    if (judgeEntries.length > 1) {
      // Dual-panel: one edit button per panel judge
      judgeButtons = judgeEntries.map(([pid, jName]) => {
        const safeJ = (jName || '').replace(/\\/g, '\\\\').replace(/'/g, "\\'");
        return `<button class="btn-judge-edit" style="font-size:.62rem;margin:.1rem .1rem 0"
          onclick="window.openCaptionJudgeModal(${compId},'${safeRaw}','${safeJ}',${pid})">⚖ ${jName || 'set'}</button>`;
      }).join('');
    } else {
      const safeJudge = (judgeEntries[0]?.[1] || '').replace(/\\/g, '\\\\').replace(/'/g, "\\'");
      const judgeLabel = judgeEntries[0]?.[1] || 'set judge';
      judgeButtons = `<button class="btn-judge-edit" style="font-size:.65rem;margin-top:.2rem"
        onclick="window.openCaptionJudgeModal(${compId},'${safeRaw}','${safeJudge}',null)">⚖ ${judgeLabel}</button>`;
    }

    return `<th class="sheet-caption-th" title="${meta?.caption_group || ''}">
      ${rawName}
      ${subLabels ? `<br><span style="font-size:.62rem;opacity:.6">${subLabels}</span>` : ''}
      <br>${judgeButtons}
    </th>`;
  }).join('');

  const renderEntryRow = (e) => {
    const isClient = e.client_id != null;
    // Build per-caption lookup keyed by (rawName, panel_id) so dual panels don't overwrite each other.
    // Structure: { rawName → { panels: { panelId → { sub1, sub2, total, judge_name } } } }
    const byCaption = {};
    for (const cs of (e.caption_scores || [])) {
      const key = cs.caption_raw_name || '?';
      if (!byCaption[key]) byCaption[key] = { panels: {} };
      const pid = cs.panel_id ?? 0;
      if (!byCaption[key].panels[pid]) byCaption[key].panels[pid] = {};
      byCaption[key].panels[pid][cs.score_component] = cs;
      if (cs.judge_name) byCaption[key].panels[pid].judge_name = cs.judge_name;
    }

    const captionCells = captionOrder.map(({ rawName }) => {
      const cap = byCaption[rawName];
      if (!cap) return `<td class="sheet-score-cell">—</td>`;

      const panelEntries = Object.entries(cap.panels).sort(([a], [b]) => +a - +b);

      if (panelEntries.length > 1) {
        // Dual-panel: stack each judge's full sub-score pair + total, then combined at bottom
        const panelRows = panelEntries.map(([, p]) => {
          const jName = p.judge_name || '—';
          const sub1Val = p.sub1 ? fmtVal(p.sub1.score_value) : null;
          const sub2Val = p.sub2 ? fmtVal(p.sub2.score_value) : null;
          const totalVal = p.total ? fmtVal(p.total.score_value) : '—';
          const subVals = [sub1Val, sub2Val].filter(Boolean);
          return `<div style="display:flex;flex-direction:column;align-items:center;line-height:1.2;margin-bottom:.2rem">
            <span style="font-size:.54rem;opacity:.55;white-space:nowrap;max-width:5.5rem;overflow:hidden;text-overflow:ellipsis" title="${jName}">${jName}</span>
            ${subVals.length ? `<span class="sheet-sub-vals">${subVals.join(' / ')}</span>` : ''}
            <span class="sheet-total-val">${totalVal}</span>
          </div>`;
        });
        const combined = panelEntries.reduce((s, [, p]) => s + (parseFloat(p.total?.score_value) || 0), 0);
        return `<td class="sheet-score-cell sheet-score-multi" style="vertical-align:top;padding-top:.4rem">
          ${panelRows.join('')}
          <div style="border-top:1px solid rgba(255,255,255,.12);padding-top:.15rem;font-size:.7rem;font-weight:700;text-align:center">${fmtVal(combined)}</div>
        </td>`;
      }

      // Single panel — original rendering
      const panel = panelEntries[0]?.[1] || {};
      const sub1 = panel.sub1 ? fmtVal(panel.sub1.score_value) : null;
      const sub2 = panel.sub2 ? fmtVal(panel.sub2.score_value) : null;
      const total = panel.total ? fmtVal(panel.total.score_value) : null;
      const judgeIcon = panel.judge_name ? `<span class="sheet-judge" title="${panel.judge_name}">⚖</span>` : '';

      if (sub1 || sub2) {
        return `<td class="sheet-score-cell sheet-score-multi">
          <span class="sheet-sub-vals">${[sub1, sub2].filter(Boolean).join(' / ')}</span>
          <span class="sheet-total-val">${total || '—'}${judgeIcon}</span>
        </td>`;
      }
      return `<td class="sheet-score-cell">${total || '—'}${judgeIcon}</td>`;
    }).join('');

    const classSize = e.class_size ? ` of ${e.class_size}` : '';
    return `
      <tr class="sheet-row${isClient ? ' sheet-row-client' : ''}" data-entry-id="${e.id}">
        <td class="sheet-placement">${e.placement != null ? '#' + e.placement + classSize : '—'}</td>
        <td class="sheet-band">${e.band_name}${isClient ? ' ★' : ''}</td>
        <td class="sheet-total">${e.total_score != null ? fmtVal(e.total_score) : '—'}</td>
        ${captionCells}
      </tr>`;
  };

  // Group by classification when multiple classes are present
  const entryClasses = [...new Set(entries.map(e => e.classification).filter(Boolean))];
  const grouped = entryClasses.length > 1;
  let rows;
  if (grouped) {
    const classGroups = {};
    const classOrder = [];
    for (const e of entries) {
      const k = e.classification || '';
      if (!classGroups[k]) { classGroups[k] = []; classOrder.push(k); }
      classGroups[k].push(e);
    }
    const colspan = 3 + captionOrder.length;
    rows = classOrder.map(cls => {
      const label = cls ? `Marching Band: ${cls}` : 'Unclassified';
      const header = `<tr><td colspan="${colspan}" style="padding:.45rem .6rem .25rem;font-size:.72rem;font-weight:700;color:#0ea5e9;text-transform:uppercase;letter-spacing:.07em;background:rgba(14,165,233,.06);border-top:1px solid rgba(14,165,233,.18);border-bottom:1px solid rgba(14,165,233,.1)">${label}</td></tr>`;
      return header + classGroups[cls].map(renderEntryRow).join('');
    }).join('');
  } else {
    rows = entries.map(renderEntryRow).join('');
  }

  return `
    <div class="sheet-wrapper" style="overflow-x:auto;padding:.5rem 1rem 1rem">
      <div style="display:flex;justify-content:flex-end;margin-bottom:.4rem">
        <button class="btn-del-sched" style="font-size:.75rem;color:#c0392b;background:none;border:none;cursor:pointer;opacity:.6"
          onclick="window.deleteCompetition(${compId})" title="Delete this competition and all its scores">✕ Delete competition</button>
      </div>
      <table class="sheet-table">
        <thead>
          <tr>
            <th>Placement</th><th>Band</th><th>Total</th>
            ${headerCols}
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
      <p style="font-size:.75rem;color:var(--text-muted);margin-top:.5rem">★ = WMM client &nbsp; ⚖ click caption header to assign judge. Sub-scores shown as Sub1 / Sub2 above total.</p>
    </div>`;
}

window.deleteCompetition = async function(competitionId) {
  if (!confirm('Delete this competition and all its scores? This cannot be undone.')) return;
  try {
    await apiFetch(`/api/competition/competitions/${competitionId}`, { method: 'DELETE' });
    await loadTabContent('results', window._currentShow, window._currentClient);
  } catch (err) {
    alert('Failed to delete: ' + err.message);
  }
};

window.openCaptionJudgeModal = function(compId, captionRaw, currentJudge, panelId) {
  const modal = document.getElementById('edit-judges-modal');
  if (!modal) return;
  document.getElementById('edit-judge-comp-id').value    = compId;
  document.getElementById('edit-judge-caption-raw').value = captionRaw;
  document.getElementById('edit-judge-panel-id').value   = panelId != null ? panelId : '';
  const panelLabel = panelId != null ? ` (Panel ${panelId})` : '';
  document.getElementById('edit-judge-caption-label').textContent = captionRaw + panelLabel;
  document.getElementById('edit-judge-name').value = currentJudge || '';
  modal.classList.remove('hidden');
};

window.applyJudgeName = async function() {
  const compId     = document.getElementById('edit-judge-comp-id')?.value;
  const captionRaw = document.getElementById('edit-judge-caption-raw')?.value;
  const judgeName  = document.getElementById('edit-judge-name')?.value?.trim();
  const panelIdStr = document.getElementById('edit-judge-panel-id')?.value;
  const panelId    = panelIdStr !== '' && panelIdStr != null ? parseInt(panelIdStr) : null;
  if (!compId || !captionRaw) return;

  try {
    await apiFetch(`/api/competition/competitions/${compId}/caption-judge`, {
      method: 'PATCH',
      body: JSON.stringify({ caption_raw_name: captionRaw, judge_name: judgeName || null, panel_id: panelId })
    });
    document.getElementById('edit-judges-modal')?.classList.add('hidden');
    const panel = document.getElementById(`sheet-${compId}`);
    if (panel) {
      panel.dataset.loaded = '';
      panel.innerHTML = '<div style="padding:1rem;color:var(--text-muted)">Reloading…</div>';
      const data = await apiFetch(`/api/competition/competitions/${compId}/sheet`);
      panel.innerHTML = renderCompetitiveSheet(data);
    }
  } catch (err) {
    alert('Failed to apply judge name: ' + err.message);
  }
};

// ── Import Recap Modal handlers ───────────────────────────────────────────

let _recapExtracted  = null;
let _recapDetection  = null;
let _recapClientId   = null;
let _recapFile       = null;   // retained across failures so retry skips re-upload

window.openRecapImportModal = function(clientId) {
  _recapClientId = clientId;
  window.resetRecapImport();
  document.getElementById('recap-import-modal')?.classList.remove('hidden');
};

window.resetRecapImport = function() {
  _recapExtracted = null;
  _recapDetection = null;
  _recapFile      = null;
  const upload = document.getElementById('recap-step-upload');
  const review = document.getElementById('recap-step-review');
  if (upload) upload.style.display = '';
  if (review) review.style.display = 'none';
  const status = document.getElementById('recap-extract-status');
  if (status) status.textContent = '';
  const fi = document.getElementById('recap-file-input');
  if (fi) fi.value = '';
};

window.runRecapExtract = async function(clientId) {
  const fileInput = document.getElementById('recap-file-input');
  // Use a previously stored file (retry path) or the newly selected file
  if (fileInput?.files?.length) _recapFile = fileInput.files[0];
  if (!_recapFile) { alert('Please select a scoresheet file'); return; }

  const statusEl = document.getElementById('recap-extract-status');
  const btn = document.getElementById('recap-extract-btn');
  if (statusEl) statusEl.textContent = 'Extracting with Claude… this takes ~10s';
  if (btn) btn.disabled = true;

  try {
    const fd = new FormData();
    fd.append('image', _recapFile);
    const resp = await fetch('/api/competition/import/extract', { method: 'POST', body: fd });
    const data = await resp.json();
    if (data.error) throw new Error(data.error);

    _recapExtracted = data;

    // Run detect-format
    const circuit = data.competition?.circuit_guess;
    const season  = data.competition?.event_date ? parseInt(data.competition.event_date.slice(0, 4)) : null;
    if (circuit && season && Array.isArray(data.raw_captions)) {
      try {
        _recapDetection = await apiFetch('/api/competition/detect-format', {
          method: 'POST',
          body: JSON.stringify({ circuit, season, raw_captions: data.raw_captions })
        });
      } catch (e) {
        _recapDetection = null;
      }
    }

    // Show review step
    document.getElementById('recap-step-upload').style.display = 'none';
    const reviewEl = document.getElementById('recap-step-review');
    reviewEl.style.display = '';
    document.getElementById('recap-review-content').innerHTML = renderRecapReview(_recapExtracted, _recapDetection);

    const warnEl = document.getElementById('recap-format-warning');
    if (_recapDetection && _recapDetection.match !== 'exact') {
      const unmatchedNames = (_recapDetection.unmatched || []).map(u => u.raw_name).join(', ');
      warnEl.style.display = '';
      if (_recapDetection.match === 'partial') {
        warnEl.textContent = `Format partially matched (${_recapDetection.format_name}). Unmatched captions — raw names preserved, analysis features limited: ${unmatchedNames}`;
      } else {
        warnEl.textContent = `Format not matched for circuit "${circuit}". Scores will be imported without caption normalization — you can map them later in the Formats tab.`;
      }
    } else {
      warnEl.style.display = 'none';
    }
  } catch (err) {
    if (statusEl) statusEl.innerHTML =
      `<span style="color:var(--danger)">Extraction failed: ${err.message}</span>
       <button onclick="window.runRecapExtract(${clientId})" class="btn-secondary" style="margin-left:.75rem;padding:.25rem .75rem;font-size:.8rem">Try Again</button>`;
  } finally {
    if (btn) btn.disabled = false;
  }
};

function renderRecapReview(data, detection) {
  const comp = data.competition || {};
  const matched = (detection?.matched || []);
  const matchByRaw = {};
  for (const m of matched) matchByRaw[m.raw_name] = m;

  // Auto-detect client band for display in review
  const orgName = (window._currentClient?.org_name || '').toLowerCase().trim();
  const bandNames = (data.entries || []).map(e => e.band_name);
  const autoMatch = orgName ? (bandNames.find(n => {
    const bn = n.toLowerCase();
    return bn.includes(orgName) || orgName.includes(bn);
  }) || null) : null;

  const seenPlacements = new Set();
  const hasRepeatedPlacements = (data.entries || []).some(e => {
    if (e.placement == null) return false;
    if (seenPlacements.has(e.placement)) return true;
    seenPlacements.add(e.placement); return false;
  });

  const classInputStyle = `width:60px;background:rgba(255,255,255,.07);border:1px solid rgba(255,255,255,.15);border-radius:3px;color:inherit;padding:.15rem .3rem;font-size:.8rem`;
  const placeInputStyle = `width:44px;background:rgba(255,255,255,.07);border:1px solid rgba(255,255,255,.15);border-radius:3px;color:inherit;padding:.15rem .3rem;font-size:.8rem;text-align:center`;

  const entryRows = (data.entries || []).map((e, i) => {
    const isClient = autoMatch && e.band_name === autoMatch;
    return `
    <tr${isClient ? ' style="background:rgba(0,180,216,.06)"' : ''}>
      <td><input type="number" id="recap-band-placement-${i}" value="${e.placement ?? ''}" placeholder="?" min="1" style="${placeInputStyle}"></td>
      <td>${e.band_name}${isClient ? ' <span style="color:#00b4d8;font-size:.72rem">★ linked</span>' : ''}</td>
      <td>${e.total_score != null ? e.total_score : '—'}</td>
      <td><input type="text" id="recap-band-class-${i}" value="${e.classification || ''}" placeholder="e.g. 1-A" style="${classInputStyle}"></td>
      <td>${(e.caption_scores || []).length}</td>
    </tr>`;
  }).join('');

  const captionRows = (data.raw_captions || []).map(c => {
    const m = matchByRaw[c.raw_name];
    const badge = m ? `<span style="color:#4caf50;font-size:.75rem">✓ ${m.caption_group}</span>`
                    : `<span style="color:#888;font-size:.75rem">unmatched</span>`;
    return `<tr><td>${c.raw_name}</td><td>${badge}</td><td>${(c.sub_score_labels || []).join(', ')}</td></tr>`;
  }).join('');

  const formatLine = detection
    ? `<strong>${detection.format_name || 'none'}</strong> (${detection.match})`
    : '<span style="color:#888">not detected</span>';

  const clientNote = autoMatch
    ? `<div style="margin-bottom:.75rem;padding:.5rem .75rem;background:rgba(0,180,216,.07);border:1px solid rgba(0,180,216,.2);border-radius:5px;font-size:.8rem">
         <span style="color:#00b4d8">★</span> <strong>${autoMatch}</strong> will be linked to this client card.
         ${orgName && !bandNames.some(n => n.toLowerCase().includes(orgName) || orgName.includes(n.toLowerCase()))
           ? ' (no exact match — using first band as fallback)' : ''}
       </div>`
    : '';

  const inputStyle = `background:rgba(255,255,255,.07);border:1px solid rgba(255,255,255,.15);border-radius:4px;color:inherit;padding:.2rem .4rem;font-size:.85rem;width:100%;box-sizing:border-box`;
  const selStyle   = `background:#1b2535;border:1px solid rgba(255,255,255,.15);border-radius:4px;color:inherit;padding:.2rem .4rem;font-size:.85rem`;
  const circuits   = ['TOB','CBA','MFA','UIL','BOA'];
  const curCircuit = comp.circuit_guess || '';
  const curRound   = (comp.round || 'regular').toLowerCase();

  return `
    <div style="font-size:.85rem;margin-bottom:.75rem">
      ${clientNote}
      <table style="width:100%;border-collapse:collapse;margin-bottom:.5rem">
        <tr>
          <td style="color:var(--text-muted);width:120px;padding-bottom:.35rem">Event</td>
          <td style="padding-bottom:.35rem"><input id="recap-comp-name" type="text" value="${(comp.name || '').replace(/"/g,'&quot;')}" placeholder="Competition name" style="${inputStyle}"></td>
        </tr>
        <tr>
          <td style="color:var(--text-muted);padding-bottom:.35rem">Date <span style="color:#e05252">*</span></td>
          <td style="padding-bottom:.35rem"><input id="recap-comp-date" type="date" value="${comp.event_date || ''}" style="${inputStyle};width:auto"></td>
        </tr>
        <tr>
          <td style="color:var(--text-muted);padding-bottom:.35rem">Circuit <span style="color:#e05252">*</span></td>
          <td style="padding-bottom:.35rem">
            <select id="recap-comp-circuit" style="${selStyle}">
              <option value="">— select —</option>
              ${circuits.map(c => `<option value="${c}" ${curCircuit===c?'selected':''}>${c}</option>`).join('')}
            </select>
          </td>
        </tr>
        <tr>
          <td style="color:var(--text-muted);padding-bottom:.35rem">Round</td>
          <td style="padding-bottom:.35rem">
            <select id="recap-comp-round" style="${selStyle}">
              ${['regular','prelims','finals','championships'].map(r =>
                `<option value="${r}" ${curRound===r?'selected':''}>${r.charAt(0).toUpperCase()+r.slice(1)}</option>`
              ).join('')}
            </select>
          </td>
        </tr>
        <tr><td style="color:var(--text-muted)">Format</td><td>${formatLine}</td></tr>
      </table>
      <strong>Captions (${(data.raw_captions || []).length})</strong>
      <table class="sheet-table" style="margin:.4rem 0 .75rem;font-size:.8rem">
        <thead><tr><th>Caption</th><th>Match</th><th>Sub-labels</th></tr></thead>
        <tbody>${captionRows}</tbody>
      </table>
      ${hasRepeatedPlacements ? `<div style="margin-bottom:.6rem;padding:.4rem .7rem;background:rgba(255,200,0,.07);border:1px solid rgba(255,200,0,.25);border-radius:5px;font-size:.8rem;color:#e5c900">Duplicate placement numbers — multiple classification groups detected. Correct <strong>Class</strong> and <strong>Place</strong> for each band as needed.</div>` : ''}
      <strong>Bands (${(data.entries || []).length})</strong>
      <table class="sheet-table" style="margin:.4rem 0;font-size:.8rem">
        <thead><tr><th>Place</th><th>Band</th><th>Total</th><th>Class</th><th>Captions</th></tr></thead>
        <tbody>${entryRows}</tbody>
      </table>
    </div>`;
}

window.confirmRecapImport = async function(clientId) {
  if (!_recapExtracted) return;
  const btn = document.getElementById('recap-confirm-btn');
  if (btn) btn.disabled = true;

  const data = _recapExtracted;
  const comp = data.competition || {};

  const compName  = document.getElementById('recap-comp-name')?.value?.trim() || comp.name || 'Unknown Competition';
  const compDate  = document.getElementById('recap-comp-date')?.value?.trim() || comp.event_date || '';
  const circuit   = document.getElementById('recap-comp-circuit')?.value || comp.circuit_guess || '';
  const compRound = document.getElementById('recap-comp-round')?.value || comp.round || 'regular';
  const season    = compDate ? parseInt(compDate.slice(0, 4)) : null;

  if (!compDate) {
    alert('Event date is required — please enter it above');
    if (btn) btn.disabled = false;
    return;
  }
  if (!circuit) {
    alert('Circuit is required — please select one above');
    if (btn) btn.disabled = false;
    return;
  }

  const matched = (_recapDetection?.matched || []);
  const matchByRaw = {};
  for (const m of matched) matchByRaw[m.raw_name] = m;

  // Auto-detect which band on the sheet is the current client.
  // Try to find a band name that contains the org_name or vice-versa (case-insensitive).
  const orgName = (window._currentClient?.org_name || '').toLowerCase().trim();
  const bandNames = (data.entries || []).map(e => e.band_name);
  const primaryBandName = bandNames.find(n => {
    const bn = n.toLowerCase();
    return bn.includes(orgName) || orgName.includes(bn);
  }) || bandNames[0] || null;

  const payload = {
    format_id: _recapDetection?.format_id || null,
    client_id: clientId || null,
    primary_band_name: primaryBandName,
    competition: {
      name:        compName,
      event_date:  compDate,
      host_school: comp.host || null,
      circuit,
      season,
      round:       compRound,
      classification: comp.classification || null,
    },
    entries: (data.entries || []).map((e, i) => {
      const placementRaw = document.getElementById(`recap-band-placement-${i}`)?.value?.trim();
      return {
      band_name: e.band_name,
      placement: placementRaw ? parseInt(placementRaw, 10) : e.placement,
      total_score: e.total_score,
      subtotal:  e.subtotal,
      penalty:   e.penalty,
      classification: document.getElementById(`recap-band-class-${i}`)?.value?.trim() || e.classification || null,
      class_size: e.class_size || null,
      caption_scores: (e.caption_scores || []).flatMap(cs => {
        const captionDef = matchByRaw[cs.raw_name];
        const defId = captionDef?.caption_def_id || null;
        const base = { caption_def_id: defId, caption_raw_name: cs.raw_name, judge_name: cs.judge_name, panel_id: cs.panel_id };
        const rows = [];
        if (cs.sub_score_1 != null)   rows.push({ ...base, sub_score_1: cs.sub_score_1 });
        if (cs.sub_score_2 != null)   rows.push({ ...base, sub_score_2: cs.sub_score_2 });
        if (cs.caption_total != null) rows.push({ ...base, caption_total: cs.caption_total });
        return rows.length ? rows : [];
      })
    }; })
  };

  try {
    await apiFetch('/api/competition/import/confirm', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    document.getElementById('recap-import-modal')?.classList.add('hidden');
    window.resetRecapImport();
    // Reload the results tab
    await loadTabContent('results', window._currentShow, window._currentClient);
  } catch (err) {
    alert('Import failed: ' + err.message);
    if (btn) btn.disabled = false;
  }
};

// ── Director lock handlers ────────────────────────────────────────────────

window.showDirEdit = function(showId) {
  document.getElementById(`fin-dir-form-${showId}`)?.style.setProperty('display', 'block');
};

window.cancelDirEdit = function(showId) {
  document.getElementById(`fin-dir-form-${showId}`)?.style.setProperty('display', 'none');
};

window.saveLockDirector = async function(showId, clientId) {
  const director_name  = document.getElementById(`fin-dir-name-${showId}`)?.value.trim() || '';
  const director_email = document.getElementById(`fin-dir-email-${showId}`)?.value.trim() || '';
  if (!director_name && !director_email) { alert('Enter name or email.'); return; }
  try {
    await apiFetch(`/api/shows/${showId}/director`, {
      method: 'PATCH',
      body: JSON.stringify({ director_name, director_email }),
    });
    if (window.renderClientCard) window.renderClientCard(clientId);
  } catch (err) {
    alert('Failed to save director: ' + err.message);
  }
};

window.unlockDirector = async function(showId, clientId) {
  if (!confirm('Remove the lock?\n\nAutomated processes (like contact updates) will be able to overwrite director info again.')) return;
  try {
    await apiFetch(`/api/shows/${showId}/director/unlock`, { method: 'PATCH' });
    if (window.renderClientCard) window.renderClientCard(clientId);
  } catch (err) {
    alert('Failed to unlock: ' + err.message);
  }
};

